import React, { useState, useCallback, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PublicStudyView from './pages/PublicStudyView';
import AdminPanel from './components/AdminPanel';
import MyStudiesPanel from './components/MyStudiesPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component avec nouveaux noms et ordre d'onglets
const Navigation = ({ activeTab, setActiveTab, isPublicView = false }) => {
  const navigate = useNavigate();
  const { user, logout, hasPermission } = useAuth();
  
  const handleTabClick = (tab) => {
    setActiveTab(tab);
    // En mode public, pas de navigation URL
    if (!isPublicView) {
      navigate(`/${tab === 'dashboard' ? '' : tab}`);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getTabStyle = (isActive) => ({
    padding: '0.5rem 1.25rem',
    borderRadius: '8px',
    fontFamily: 'var(--font-corps)',
    fontWeight: '500',
    fontSize: '0.875rem',
    transition: 'all 200ms ease',
    border: 'none',
    background: isActive ? 'var(--pde-azur)' : 'transparent',
    color: isActive ? 'white' : 'var(--pde-bleu-petrole-fonce)',
    cursor: 'pointer',
    boxShadow: isActive ? '0 2px 10px rgba(57, 125, 255, 0.25)' : 'none',
    letterSpacing: '0.005em'
  });

  return (
    <nav style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative' }}>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
      {/* 1. Ma situation (ex-Dashboard) */}
      <button
        onClick={() => handleTabClick('dashboard')}
        style={getTabStyle(activeTab === 'dashboard')}
        onMouseEnter={(e) => {
          if (activeTab !== 'dashboard') {
            e.target.style.color = 'var(--pde-azur)';
            e.target.style.background = 'rgba(57, 125, 255, 0.05)';
          }
        }}
        onMouseLeave={(e) => {
          if (activeTab !== 'dashboard') {
            Object.assign(e.target.style, getTabStyle(false));
          }
        }}
      >
        Ma situation
      </button>
      
      {/* 2. Mon périmètre (ex-Sites) - Position 2 */}
      <button
        onClick={() => handleTabClick('sites')}
        style={getTabStyle(activeTab === 'sites')}
        onMouseEnter={(e) => {
          if (activeTab !== 'sites') {
            e.target.style.color = 'var(--pde-azur)';
            e.target.style.background = 'rgba(57, 125, 255, 0.05)';
          }
        }}
        onMouseLeave={(e) => {
          if (activeTab !== 'sites') {
            Object.assign(e.target.style, getTabStyle(false));
          }
        }}
      >
        Mon périmètre
      </button>
      
      {/* 3. Mes offres (ex-Comparateur) */}
      <button
        onClick={() => handleTabClick('comparateur')}
        style={getTabStyle(activeTab === 'comparateur')}
        onMouseEnter={(e) => {
          if (activeTab !== 'comparateur') {
            e.target.style.color = 'var(--pde-azur)';
            e.target.style.background = 'rgba(57, 125, 255, 0.05)';
          }
        }}
        onMouseLeave={(e) => {
          if (activeTab !== 'comparateur') {
            Object.assign(e.target.style, getTabStyle(false));
          }
        }}
      >
        Mes offres
      </button>
      
      {/* Onglets suivants masqués en mode public */}
      {!isPublicView && (
        <>
          {/* 4. Import - Masqué pour Commercial - AVANT Mes Études */}
          {hasPermission && hasPermission('view:all') && (
            <button
              onClick={() => handleTabClick('import')}
              style={getTabStyle(activeTab === 'import')}
              onMouseEnter={(e) => {
                if (activeTab !== 'import') {
                  e.target.style.color = 'var(--pde-azur)';
                  e.target.style.background = 'rgba(57, 125, 255, 0.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'import') {
                  Object.assign(e.target.style, getTabStyle(false));
                }
              }}
            >
              Import
            </button>
          )}
          
          {/* 5. Mes Études - Visible pour tous les utilisateurs (ADV, SuperAdmin, Commercial) */}
          <button
            onClick={() => handleTabClick('my-studies')}
            style={getTabStyle(activeTab === 'my-studies')}
            onMouseEnter={(e) => {
              if (activeTab !== 'my-studies') {
                e.target.style.color = 'var(--pde-azur)';
                e.target.style.background = 'rgba(57, 125, 255, 0.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'my-studies') {
                Object.assign(e.target.style, getTabStyle(false));
              }
            }}
          >
            Mes Études
          </button>
          
          {/* 6. Administration - Visible uniquement pour SuperAdmin */}
          {user && user.role === 'superadmin' && (
            <button
              onClick={() => handleTabClick('admin')}
              style={getTabStyle(activeTab === 'admin')}
              onMouseEnter={(e) => {
                if (activeTab !== 'admin') {
                  e.target.style.color = 'var(--pde-azur)';
                  e.target.style.background = 'rgba(57, 125, 255, 0.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'admin') {
                  Object.assign(e.target.style, getTabStyle(false));
                }
              }}
            >
              Administration
            </button>
          )}
        </>
      )}
      </div>

      {/* User info and logout - Masqué en mode public */}
      {!isPublicView && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'absolute', right: 0 }}>
          <span style={{ 
            fontFamily: 'var(--font-corps)', 
            fontSize: '0.8125rem', 
            color: '#687175'
          }}>
            {user?.username || user?.email}
          </span>
          <button
            onClick={handleLogout}
            style={{
              padding: '6px 14px',
              borderRadius: '8px',
              fontFamily: 'var(--font-corps)',
              fontWeight: '500',
              fontSize: '0.8125rem',
              border: '1px solid #E2E6EA',
              background: '#FFFFFF',
              color: '#3C4649',
              cursor: 'pointer',
              transition: 'all 200ms ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#F5F6F9';
              e.target.style.borderColor = '#BFC6CC';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#FFFFFF';
              e.target.style.borderColor = '#E2E6EA';
            }}
          >
            Déconnexion
          </button>
        </div>
      )}
    </nav>
  );
};

// Header Component selon charte PDE - VERROUILLÉ - NE PAS MODIFIER
// Configuration finale validée : Logo PDE + Etude Multisite + Client + Navigation intégrée
const Header = ({ activeTab, setActiveTab, latestAnalysis, isPublicView = false, publicShareInfo = null }) => {
  return (
    <header style={{
      background: '#FFFFFF',
      borderBottom: '1px solid #E2E6EA',
      boxShadow: '0 1px 3px rgba(25, 29, 30, 0.04)',
      padding: '1.25rem 2rem 1rem'
    }}>
      <div className="max-w-7xl mx-auto">
        {/* Ligne 1: Logo - Étude Multisite - Client */}
        <div className="flex items-center justify-between mb-4">
          {/* LOGO Place des Énergies officiel - À gauche */}
          <div className="flex items-center">
            <img 
              src={`${process.env.REACT_APP_BACKEND_URL}/api/static/logos/pde-logo-horizontal.png`}
              alt="Place des Énergies"
              style={{
                height: '2.5rem',
                width: 'auto'
              }}
            />
          </div>

          {/* Étude Multisite - Au centre */}
          <div style={{ textAlign: 'center' }}>
            <h2 style={{
              fontFamily: 'var(--font-titre)',
              fontWeight: '600',
              color: '#191D1E',
              fontSize: '1.5rem',
              margin: '0',
              letterSpacing: '-0.02em'
            }}>Étude Multisite</h2>
          </div>

          {/* Client - À droite */}
          {(latestAnalysis?.client_info?.client_name || isPublicView) && (
            <div style={{ textAlign: 'right' }}>
              <p style={{
                fontFamily: 'var(--font-corps)',
                fontSize: '0.75rem',
                color: '#939BA0',
                margin: '0 0 0.125rem 0',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontWeight: '500'
              }}>Client</p>
              <h3 style={{
                fontFamily: 'var(--font-titre)',
                fontWeight: '600',
                color: '#397DFF',
                fontSize: '1.125rem',
                margin: '0'
              }}>{
                isPublicView && publicShareInfo?.client_name 
                  ? publicShareInfo.client_name 
                  : (latestAnalysis?.client_info?.client_name || latestAnalysis?.name || 'N/A')
              }</h3>
            </div>
          )}
        </div>

        {/* Ligne 2: Navigation intégrée */}
        <div className="flex items-center justify-center">
          <Navigation activeTab={activeTab} setActiveTab={setActiveTab} isPublicView={isPublicView} />
        </div>
      </div>
    </header>
  );
};

// Empty State Component
const EmptyState = ({ message = "Veuillez d'abord importer une étude" }) => {
  return (
    <div className="flex items-center justify-center min-h-96">
      <div style={{ textAlign: 'center', padding: '48px 24px' }}>
        <div style={{
          width: '64px',
          height: '64px',
          background: 'rgba(57, 125, 255, 0.08)',
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px'
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#397DFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
        </div>
        <p style={{
          fontFamily: 'var(--font-corps)',
          fontSize: '0.9375rem',
          color: '#687175'
        }}>{message}</p>
      </div>
    </div>
  );
};

// Dashboard Component with data
const DashboardTab = ({ latestAnalysis, setActiveTab, isPublicView = false }) => {
  const [showFournitureDetails, setShowFournitureDetails] = useState(false);
  const [showTaxesDetails, setShowTaxesDetails] = useState(false);
  const [budgetMode, setBudgetMode] = useState('HTVA'); // 'HTVA' ou 'TTC'

  // Fonction utilitaire pour formater les montants avec 2 décimales
  const formatAmount = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('fr-FR', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    }).format(amount);
  };
  const [analysisDetails, setAnalysisDetails] = useState(null);
  const [ranking, setRanking] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (latestAnalysis) {
      if (isPublicView) {
        // En mode public, utiliser directement les données de latestAnalysis
        setAnalysisDetails(latestAnalysis);
        // Construire le ranking depuis supplier_offers si disponible
        if (latestAnalysis.supplier_offers) {
          const rankingData = buildRankingFromOffers(latestAnalysis.supplier_offers);
          setRanking({ ranking: rankingData, analysis_id: latestAnalysis.analysis_id });
        }
      } else {
        fetchAnalysisData();
      }
    }
  }, [latestAnalysis, isPublicView]);

  // Fonction pour construire le ranking à partir des supplier_offers (pour vue publique)
  const buildRankingFromOffers = (offers) => {
    if (!offers || !Array.isArray(offers)) return [];
    
    const suppliersMap = new Map();
    
    offers
      .filter(offer => offer.a_inserer === 'OUI')
      .sort((a, b) => (a.ranking || 999) - (b.ranking || 999))
      .forEach(offer => {
        const uniqueKey = `${offer.supplier_name}-${offer.duree || ''}`;
        if (!suppliersMap.has(uniqueKey)) {
          suppliersMap.set(uniqueKey, {
            supplier_name: offer.supplier_name,
            total_annual_cost: offer.total_annuel_htva_turpe || 0,
            ranking: offer.ranking || suppliersMap.size + 1,
            is_selected: true,
            duree: offer.duree || '',
            duration: offer.duree || '',
            commission: offer.commission || '',
            offer_type: offer.offre_type || '',
            remarque: offer.remarque || '',
            classement_number: offer.classement_number,
            budget_ligne21: offer.budget_ligne21,
            budget_ligne23: offer.budget_ligne23,
            sheet_reference: offer.sheet_reference
          });
        }
      });
    
    return Array.from(suppliersMap.values());
  };

  const fetchAnalysisData = async () => {
    if (!latestAnalysis) return;

    try {
      setLoading(true);
      
      const [detailsResponse, rankingResponse] = await Promise.all([
        axios.get(`${API}/analysis/${latestAnalysis.analysis_id}`),
        axios.get(`${API}/analysis/${latestAnalysis.analysis_id}/ranking`)
      ]);
      
      setAnalysisDetails(detailsResponse.data);
      setRanking(rankingResponse.data);
      
    } catch (error) {
      console.error('Error fetching analysis data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!latestAnalysis) {
    return <EmptyState />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '3px solid #E2E6EA',
            borderTopColor: '#397DFF',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 20px'
          }} />
          <p style={{ fontFamily: 'var(--font-corps)', fontSize: '0.9375rem', color: '#687175' }}>Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header Summary avec nouveau pictogramme et texte */}
      <div className="mb-8">
        <h1 style={{
          fontFamily: 'var(--font-titre)',
          fontWeight: '600',
          color: '#191D1E',
          fontSize: '1.625rem',
          marginBottom: '6px',
          display: 'flex',
          alignItems: 'center',
          letterSpacing: '-0.02em'
        }}>
          <img 
            src={`${process.env.REACT_APP_BACKEND_URL}/api/static/logos/logo-elec.png`}
            alt="Appel d'offres"
            style={{
              height: '2.25rem',
              width: 'auto',
              marginRight: '12px'
            }}
            onError={(e) => {
              e.target.src = `${process.env.REACT_APP_BACKEND_URL}/api/static/logos/logo-elec.jpg`;
            }}
          />
          Appel d'offres de fourniture d'électricité
        </h1>
        <p style={{
          fontFamily: 'var(--font-corps)',
          color: '#939BA0',
          fontSize: '0.875rem',
          marginBottom: '2rem'
        }}>
          Basé sur votre consommation annuelle de référence (Source: ENEDIS)
        </p>
      </div>

      {/* Nouveaux encarts selon charte PDE - 4 encarts sur une ligne */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        gap: '16px', 
        marginBottom: '2rem' 
      }}>
        {/* Encart Nombre de sites avec détail par segment */}
        <div style={{
          background: '#FFFFFF',
          border: '1px solid #E2E6EA',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(25, 29, 30, 0.05)',
          transition: 'box-shadow 200ms ease, transform 200ms ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(25, 29, 30, 0.08)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = '0 1px 3px rgba(25, 29, 30, 0.05)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
            <img 
              src={`${process.env.REACT_APP_BACKEND_URL}/api/static/logos/logo-perimetre.png`}
              alt="Sites" 
              style={{ 
                height: '2rem', 
                width: 'auto',
                marginRight: '12px'
              }} 
            />
            <div style={{ flex: 1 }}>
              <p style={{
                fontFamily: 'var(--font-corps)',
                fontSize: '0.75rem',
                fontWeight: '500',
                color: '#939BA0',
                margin: '0 0 4px 0',
                textTransform: 'uppercase',
                letterSpacing: '0.04em'
              }}>Nombre de sites</p>
              <p style={{
                fontFamily: 'var(--font-titre)',
                fontWeight: '600',
                fontSize: '1.5rem',
                color: '#191D1E',
                margin: '0',
                letterSpacing: '-0.02em'
              }}>{analysisDetails?.client_info?.total_sites || latestAnalysis?.total_sites || 'N/A'}</p>
            </div>
          </div>
          {/* Détail par segment sur une ligne - segments ≥1 uniquement */}
          {analysisDetails?.client_info?.sites_by_segment && (
            <div style={{ 
              fontSize: '0.7rem', 
              color: '#687175',
              fontFamily: 'var(--font-corps)',
              lineHeight: '1.5',
              paddingTop: '8px',
              borderTop: '1px solid #EEF0F3'
            }}>
              {Object.entries(analysisDetails.client_info.sites_by_segment)
                .filter(([segment, count]) => count >= 1)
                .map(([segment, count]) => `${segment}: ${count}`)
                .join(' · ')}
            </div>
          )}
        </div>

        {/* Encart Consommation globale - valeur directe du fichier Excel */}
        <div style={{
          background: '#FFFFFF',
          border: '1px solid #E2E6EA',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(25, 29, 30, 0.05)',
          transition: 'box-shadow 200ms ease, transform 200ms ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(25, 29, 30, 0.08)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = '0 1px 3px rgba(25, 29, 30, 0.05)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img 
              src={`${process.env.REACT_APP_BACKEND_URL}/api/static/logos/logo-conso.png`}
              alt="Consommation" 
              style={{ 
                height: '2rem', 
                width: 'auto',
                marginRight: '12px'
              }} 
            />
            <div>
              <p style={{
                fontFamily: 'var(--font-corps)',
                fontSize: '0.75rem',
                fontWeight: '500',
                color: '#939BA0',
                margin: '0 0 4px 0',
                textTransform: 'uppercase',
                letterSpacing: '0.04em'
              }}>Consommation globale</p>
              <p style={{
                fontFamily: 'var(--font-titre)',
                fontWeight: '600',
                fontSize: '1.5rem',
                color: '#191D1E',
                margin: '0',
                letterSpacing: '-0.02em'
              }}>
                {analysisDetails?.client_info?.consumption_global_mwh?.toLocaleString('fr-FR') || 'N/A'} 
                <span style={{ fontSize: '0.75rem', fontWeight: '400', color: '#939BA0', marginLeft: '4px' }}>MWh</span>
              </p>
            </div>
          </div>
        </div>

        {/* Encart Budget actuel HTVA avec séparateurs espaces */}
        <div style={{
          background: '#FFFFFF',
          border: '1px solid #E2E6EA',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(25, 29, 30, 0.05)',
          transition: 'box-shadow 200ms ease, transform 200ms ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(25, 29, 30, 0.08)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = '0 1px 3px rgba(25, 29, 30, 0.05)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img 
              src={`${process.env.REACT_APP_BACKEND_URL}/api/static/logos/logo-htva.png`}
              alt="HTVA" 
              style={{ 
                height: '2rem', 
                width: 'auto',
                marginRight: '12px'
              }} 
            />
            <div>
              <p style={{
                fontFamily: 'var(--font-corps)',
                fontSize: '0.75rem',
                fontWeight: '500',
                color: '#939BA0',
                margin: '0 0 4px 0',
                textTransform: 'uppercase',
                letterSpacing: '0.04em'
              }}>Budget actuel HTVA</p>
              <p style={{
                fontFamily: 'var(--font-titre)',
                fontWeight: '600',
                fontSize: '1.5rem',
                color: '#191D1E',
                margin: '0',
                letterSpacing: '-0.02em'
              }}>
                {analysisDetails?.client_info?.budget_actuel_htva 
                  ? Math.round(analysisDetails.client_info.budget_actuel_htva).toLocaleString('fr-FR').replace(/,/g, ' ')
                  : 'N/A'} 
                <span style={{ fontSize: '0.75rem', fontWeight: '400', color: '#939BA0', marginLeft: '4px' }}>€/an</span>
              </p>
            </div>
          </div>
        </div>

        {/* Encart Budget actuel TTC avec séparateurs espaces */}
        <div style={{
          background: '#FFFFFF',
          border: '1px solid #E2E6EA',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(25, 29, 30, 0.05)',
          transition: 'box-shadow 200ms ease, transform 200ms ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(25, 29, 30, 0.08)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = '0 1px 3px rgba(25, 29, 30, 0.05)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img 
              src={`${process.env.REACT_APP_BACKEND_URL}/api/static/logos/logo-ttc.png`}
              alt="TTC" 
              style={{ 
                height: '2rem', 
                width: 'auto',
                marginRight: '12px'
              }} 
            />
            <div>
              <p style={{
                fontFamily: 'var(--font-corps)',
                fontSize: '0.75rem',
                fontWeight: '500',
                color: '#939BA0',
                margin: '0 0 4px 0',
                textTransform: 'uppercase',
                letterSpacing: '0.04em'
              }}>Budget actuel TTC</p>
              <p style={{
                fontFamily: 'var(--font-titre)',
                fontWeight: '600',
                fontSize: '1.5rem',
                color: '#191D1E',
                margin: '0',
                letterSpacing: '-0.02em'
              }}>
                {analysisDetails?.client_info?.budget_actuel_ttc 
                  ? Math.round(analysisDetails.client_info.budget_actuel_ttc).toLocaleString('fr-FR').replace(/,/g, ' ')
                  : 'N/A'} 
                <span style={{ fontSize: '0.75rem', fontWeight: '400', color: '#939BA0', marginLeft: '4px' }}>€/an</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Graphique Budget Actuel - remplace TOP 5 Fournisseurs */}
      {analysisDetails && analysisDetails.client_info && (
        <div style={{ background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E2E6EA', boxShadow: '0 1px 3px rgba(25, 29, 30, 0.05)', marginBottom: '2rem' }}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #EEF0F3' }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <h2 style={{ fontFamily: 'var(--font-titre)', fontWeight: '600', fontSize: '1.125rem', color: '#191D1E', letterSpacing: '-0.01em' }}>Budget actuel</h2>
              </div>
              {/* Bouton toggle HTVA/TTC */}
              <div style={{ display: 'flex', background: '#F5F6F9', borderRadius: '8px', padding: '3px' }}>
                <button
                  onClick={() => setBudgetMode('HTVA')}
                  style={{
                    padding: '5px 14px',
                    fontSize: '0.8125rem',
                    fontFamily: 'var(--font-corps)',
                    fontWeight: '500',
                    borderRadius: '6px',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 200ms ease',
                    background: budgetMode === 'HTVA' ? '#FFFFFF' : 'transparent',
                    color: budgetMode === 'HTVA' ? '#397DFF' : '#687175',
                    boxShadow: budgetMode === 'HTVA' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'
                  }}
                >
                  HTVA
                </button>
                <button
                  onClick={() => setBudgetMode('TTC')}
                  style={{
                    padding: '5px 14px',
                    fontSize: '0.8125rem',
                    fontFamily: 'var(--font-corps)',
                    fontWeight: '500',
                    borderRadius: '6px',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 200ms ease',
                    background: budgetMode === 'TTC' ? '#FFFFFF' : 'transparent',
                    color: budgetMode === 'TTC' ? '#397DFF' : '#687175',
                    boxShadow: budgetMode === 'TTC' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'
                  }}
                >
                  TTC
                </button>
              </div>
            </div>
            <p style={{ fontFamily: 'var(--font-corps)', fontSize: '0.8125rem', color: '#939BA0', marginTop: '4px' }}>
              Répartition détaillée des coûts
            </p>
          </div>
          <div className="p-6">
            {analysisDetails.client_info.fourniture_amount || 
             analysisDetails.client_info.taxes_contribution_amount || 
             analysisDetails.client_info.acheminement_amount ? (
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-center">
                {/* Graphique à barres empilées - élargi */}
                <div className="lg:col-span-3 relative">
                  <div className="h-80 flex flex-col justify-end items-center bg-gray-50 rounded-lg p-6">
                    {/* Calcul du total pour les proportions */}
                    {(() => {
                      const fourniture = analysisDetails.client_info.fourniture_amount || 0;
                      const taxes = analysisDetails.client_info.taxes_contribution_amount || 0;
                      const acheminement = analysisDetails.client_info.acheminement_amount || 0;
                      const tva = analysisDetails.client_info.tva_amount || 0;
                      
                      // Total HTVA pour les calculs de proportions visuelles (garde la même taille de graphique)
                      const totalHTVA = fourniture + taxes + acheminement;
                      
                      // Total affiché selon le mode (pour l'affichage du budget total)
                      let totalAffiche;
                      if (budgetMode === 'TTC' && analysisDetails.client_info.budget_ttc_total) {
                        totalAffiche = analysisDetails.client_info.budget_ttc_total;
                      } else {
                        totalAffiche = totalHTVA;
                      }
                      
                      if (totalHTVA === 0) return <div className="text-gray-500">Données non disponibles</div>;
                      
                      const maxBarHeight = 240; // hauteur max en pixels
                      // Calcul des hauteurs basé sur le total HTVA pour garder la même taille visuelle
                      const fournitureHeight = (fourniture / totalHTVA) * maxBarHeight;
                      const taxesHeight = (taxes / totalHTVA) * maxBarHeight;
                      const acheminementHeight = (acheminement / totalHTVA) * maxBarHeight;
                      
                      return (
                        <div className="flex items-end h-full">
                          <div className="flex flex-col items-center mx-8">
                            <div 
                              className="w-48 bg-gradient-to-b rounded-md flex flex-col justify-start overflow-hidden"
                              style={{ height: `${maxBarHeight}px` }}
                            >
                              {/* Fourniture (haut) - ordre inversé avec texte horizontal */}
                              {fourniture > 0 && (
                                <div 
                                  className="bg-blue-400 flex items-center justify-center text-sm text-white font-medium"
                                  style={{ height: `${fournitureHeight}px` }}
                                  title={`Fourniture: ${formatAmount(fourniture)} €`}
                                >
                                  {fournitureHeight > 40 && <span className="text-sm">Fourniture</span>}
                                </div>
                              )}
                              {/* Taxes et contribution (milieu) */}
                              {taxes > 0 && (
                                <div 
                                  className="bg-blue-600 flex items-center justify-center text-sm text-white font-medium"
                                  style={{ height: `${taxesHeight}px` }}
                                  title={(() => {
                                    const taxesAmount = taxes;
                                    const tvaAmount = tva || 0;
                                    if (budgetMode === 'TTC') {
                                      return `Taxes et contribution + TVA: ${formatAmount(taxesAmount + tvaAmount)} €`;
                                    } else {
                                      return `Taxes et contribution: ${formatAmount(taxesAmount)} €`;
                                    }
                                  })()}
                                >
                                  {taxesHeight > 40 && <span className="text-sm">Taxes & contrib.</span>}
                                </div>
                              )}
                              {/* Acheminement (bas) */}
                              {acheminement > 0 && (
                                <div 
                                  className="bg-blue-800 flex items-center justify-center text-sm text-white font-medium"
                                  style={{ height: `${acheminementHeight}px` }}
                                  title={`Acheminement: ${formatAmount(acheminement)} €`}
                                >
                                  {acheminementHeight > 40 && <span className="text-sm">Acheminement</span>}
                                </div>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 mt-3 font-medium">
                              Budget annuel
                            </div>
                            <div className="text-xl font-bold text-gray-800 mt-1">
                              {formatAmount(totalAffiche)} € <span className="text-sm font-normal text-gray-500">{budgetMode}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
                
                {/* Légende élargie */}
                <div className="lg:col-span-2 space-y-3">
                  {analysisDetails.client_info.fourniture_amount > 0 && (
                    <div className="bg-blue-50 rounded-lg border-l-4 border-blue-400">
                      <div className="flex items-center justify-between p-3">
                        <div className="flex items-center">
                          <div className="w-3 h-3 bg-blue-400 rounded mr-2"></div>
                          <span className="text-sm font-medium text-gray-800">Fourniture</span>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold text-gray-800">
                            {formatAmount(analysisDetails.client_info.fourniture_amount)} €
                          </div>
                          <div className="text-xs text-gray-500">
                            {(() => {
                              const fourniture = analysisDetails.client_info.fourniture_amount || 0;
                              const taxes = analysisDetails.client_info.taxes_contribution_amount || 0;
                              const acheminement = analysisDetails.client_info.acheminement_amount || 0;
                              const total = budgetMode === 'TTC' && analysisDetails.client_info.budget_ttc_total 
                                ? analysisDetails.client_info.budget_ttc_total 
                                : fourniture + taxes + acheminement;
                              return total > 0 ? ((fourniture / total) * 100).toFixed(1) + '%' : '0%';
                            })()}
                          </div>
                        </div>
                      </div>
                      
                      {/* Bouton + de détails */}
                      <div className="px-3 pb-2">
                        <button 
                          onClick={() => setShowFournitureDetails(!showFournitureDetails)}
                          className="text-xs text-gray-500 hover:text-blue-600 transition-colors cursor-pointer underline hover:no-underline"
                        >
                          {showFournitureDetails ? '- moins de détails' : '+ de détails'}
                        </button>
                      </div>
                      
                      {/* Sous-légende dépliable */}
                      {showFournitureDetails && (
                        <div className="px-3 pb-3 border-t border-blue-100 pt-2 mt-2">
                          <div className="space-y-2 text-xs">
                            {(() => {
                              const abonnement = analysisDetails.client_info.abonnement_amount;
                              const energie = analysisDetails.client_info.energie_amount;
                              const energieVerte = analysisDetails.client_info.energie_verte_amount;
                              
                              // Calcul du budget total selon le mode
                              const fournitureTotal = analysisDetails.client_info.fourniture_amount || 0;
                              const taxesTotal = analysisDetails.client_info.taxes_contribution_amount || 0;
                              const acheminementTotal = analysisDetails.client_info.acheminement_amount || 0;
                              const budgetTotal = budgetMode === 'TTC' && analysisDetails.client_info.budget_ttc_total 
                                ? analysisDetails.client_info.budget_ttc_total 
                                : fournitureTotal + taxesTotal + acheminementTotal;
                              
                              return (
                                <>
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-600">• Abonnement</span>
                                    <div className="text-right">
                                      <div className="text-gray-800 font-medium">
                                        {formatAmount(abonnement)} €
                                      </div>
                                      {abonnement && budgetTotal > 0 && (
                                        <div className="text-xs text-gray-500">
                                          {((abonnement / budgetTotal) * 100).toFixed(2)}%
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-600">• Energie</span>
                                    <div className="text-right">
                                      <div className="text-gray-800 font-medium">
                                        {formatAmount(energie)} €
                                      </div>
                                      {energie && budgetTotal > 0 && (
                                        <div className="text-xs text-gray-500">
                                          {((energie / budgetTotal) * 100).toFixed(2)}%
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-600">• Energie verte</span>
                                    <div className="text-right">
                                      <div className="text-gray-800 font-medium">
                                        {formatAmount(energieVerte)} €
                                      </div>
                                      {energieVerte && budgetTotal > 0 && (
                                        <div className="text-xs text-gray-500">
                                          {((energieVerte / budgetTotal) * 100).toFixed(2)}%
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {analysisDetails.client_info.taxes_contribution_amount > 0 && (
                    <div className="bg-blue-50 rounded-lg border-l-4 border-blue-600">
                      <div className="flex items-center justify-between p-3">
                        <div className="flex items-center">
                          <div className="w-3 h-3 bg-blue-600 rounded mr-2"></div>
                          <span className="text-sm font-medium text-gray-800">Taxes & contrib.</span>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold text-gray-800">
                            {(() => {
                              const taxes = analysisDetails.client_info.taxes_contribution_amount || 0;
                              const tva = analysisDetails.client_info.tva_amount || 0;
                              const totalTaxes = budgetMode === 'TTC' ? taxes + tva : taxes;
                              return formatAmount(totalTaxes) + ' €';
                            })()}
                          </div>
                          <div className="text-xs text-gray-500">
                            {(() => {
                              const fourniture = analysisDetails.client_info.fourniture_amount || 0;
                              const taxes = analysisDetails.client_info.taxes_contribution_amount || 0;
                              const acheminement = analysisDetails.client_info.acheminement_amount || 0;
                              const tva = analysisDetails.client_info.tva_amount || 0;
                              const totalTaxes = budgetMode === 'TTC' ? taxes + tva : taxes;
                              const total = budgetMode === 'TTC' && analysisDetails.client_info.budget_ttc_total 
                                ? analysisDetails.client_info.budget_ttc_total 
                                : fourniture + taxes + acheminement;
                              return total > 0 ? ((totalTaxes / total) * 100).toFixed(1) + '%' : '0%';
                            })()}
                          </div>
                        </div>
                      </div>
                      
                      {/* Bouton + de détails pour Taxes */}
                      <div className="px-3 pb-2">
                        <button 
                          onClick={() => setShowTaxesDetails(!showTaxesDetails)}
                          className="text-xs text-gray-500 hover:text-blue-600 transition-colors cursor-pointer underline hover:no-underline"
                        >
                          {showTaxesDetails ? '- moins de détails' : '+ de détails'}
                        </button>
                      </div>
                      
                      {/* Sous-légende dépliable pour Taxes */}
                      {showTaxesDetails && (
                        <div className="px-3 pb-3 border-t border-blue-100 pt-2 mt-2">
                          <div className="space-y-2 text-xs">
                            {(() => {
                              const mecanismeCapacite = analysisDetails.client_info.mecanisme_capacite_amount;
                              const cee = analysisDetails.client_info.cee_amount;
                              const cta = analysisDetails.client_info.cta_amount;
                              const cspe = analysisDetails.client_info.cspe_amount;
                              const tva = analysisDetails.client_info.tva_amount;
                              
                              // Calcul du budget total selon le mode
                              const fournitureTotal = analysisDetails.client_info.fourniture_amount || 0;
                              const taxesTotal = analysisDetails.client_info.taxes_contribution_amount || 0;
                              const acheminementTotal = analysisDetails.client_info.acheminement_amount || 0;
                              const budgetTotal = budgetMode === 'TTC' && analysisDetails.client_info.budget_ttc_total 
                                ? analysisDetails.client_info.budget_ttc_total 
                                : fournitureTotal + taxesTotal + acheminementTotal;
                              
                              return (
                                <>
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-600">• Mécanisme de capacité</span>
                                    <div className="text-right">
                                      <div className="text-gray-800 font-medium">
                                        {formatAmount(mecanismeCapacite)} €
                                      </div>
                                      {mecanismeCapacite && budgetTotal > 0 && (
                                        <div className="text-xs text-gray-500">
                                          {((mecanismeCapacite / budgetTotal) * 100).toFixed(2)}%
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-600">• CEE</span>
                                    <div className="text-right">
                                      <div className="text-gray-800 font-medium">
                                        {formatAmount(cee)} €
                                      </div>
                                      {cee && budgetTotal > 0 && (
                                        <div className="text-xs text-gray-500">
                                          {((cee / budgetTotal) * 100).toFixed(2)}%
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-600">• CTA</span>
                                    <div className="text-right">
                                      <div className="text-gray-800 font-medium">
                                        {formatAmount(cta)} €
                                      </div>
                                      {cta && budgetTotal > 0 && (
                                        <div className="text-xs text-gray-500">
                                          {((cta / budgetTotal) * 100).toFixed(2)}%
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-600">• CSPE</span>
                                    <div className="text-right">
                                      <div className="text-gray-800 font-medium">
                                        {formatAmount(cspe)} €
                                      </div>
                                      {cspe && budgetTotal > 0 && (
                                        <div className="text-xs text-gray-500">
                                          {((cspe / budgetTotal) * 100).toFixed(2)}%
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  {/* TVA - uniquement en mode TTC */}
                                  {budgetMode === 'TTC' && (
                                    <div className="flex justify-between items-center">
                                      <span className="text-gray-600">• TVA</span>
                                      <div className="text-right">
                                        <div className="text-gray-800 font-medium">
                                          {formatAmount(tva)} €
                                        </div>
                                        {tva && budgetTotal > 0 && (
                                          <div className="text-xs text-gray-500">
                                            {((tva / budgetTotal) * 100).toFixed(2)}%
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {analysisDetails.client_info.acheminement_amount > 0 && (
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border-l-4 border-blue-800">
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-blue-800 rounded mr-2"></div>
                        <span className="text-sm font-medium text-gray-800">Acheminement</span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-gray-800">
                          {formatAmount(analysisDetails.client_info.acheminement_amount)} €
                        </div>
                        <div className="text-xs text-gray-500">
                          {(() => {
                            const fourniture = analysisDetails.client_info.fourniture_amount || 0;
                            const taxes = analysisDetails.client_info.taxes_contribution_amount || 0;
                            const acheminement = analysisDetails.client_info.acheminement_amount || 0;
                            const total = budgetMode === 'TTC' && analysisDetails.client_info.budget_ttc_total 
                              ? analysisDetails.client_info.budget_ttc_total 
                              : fourniture + taxes + acheminement;
                            return total > 0 ? ((acheminement / total) * 100).toFixed(1) + '%' : '0%';
                          })()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'rgba(57, 125, 255, 0.08)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 12px'
                }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#397DFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
                </div>
                <p>Données du graphique non disponibles</p>
                <p className="text-sm">Les informations du budget détaillé ne sont pas présentes dans le fichier Excel.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick Sites Overview */}
      {analysisDetails && analysisDetails.sites && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center">
              Mon périmètre ({analysisDetails.sites.length})
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {analysisDetails.sites.slice(0, 3).map((site) => (
                <div key={site.id} className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-2">{site.nom_site}</h4>
                  <p className="text-sm text-gray-600 mb-1">PRM: {site.prm}</p>
                  <p className="text-sm text-gray-600 mb-1">{site.ville}</p>
                  <p className="text-sm text-blue-600 font-medium">
                    Segment: {site.segment || 'N/A'}
                  </p>
                  <div className="text-xs text-gray-500 mt-2">
                    Conso: {((site.conso_hph || 0) + (site.conso_hch || 0)).toFixed(1)} MWh
                  </div>
                </div>
              ))}
            </div>
            {analysisDetails.sites.length > 3 && (
              <div className="text-center mt-4">
                <button 
                  onClick={() => setActiveTab('sites')}
                  className="text-blue-600 hover:text-blue-800 hover:underline transition-colors cursor-pointer font-medium"
                >
                  ... et {analysisDetails.sites.length - 3} autres sites
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Comparateur Component with cards design (exact copy of reference)
const ComparateurTab = ({ 
  latestAnalysis, 
  setSelectedOfferDetail, 
  suppliers,
  recommendedSupplierIndex,
  setRecommendedSupplierIndex,
  recommendationText,
  setRecommendationText,
  user,
  isPublicView = false
}) => {
  const [ranking, setRanking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isTTCMode, setIsTTCMode] = useState(false); // false = HTVA, true = TTC
  
  // Determine if current user is viewing a shared study in read-only mode
  const isSharedReadOnly = user?.role === 'commercial' && latestAnalysis?.is_shared && !latestAnalysis?.is_owner;
  
  // Determine if user can edit recommendations (only ADV and SuperAdmin can, and not in public view)
  const canEditRecommendation = !isPublicView && user && (user.role === 'adv' || user.role === 'superadmin');

  // Fonction pour ouvrir le détail d'une offre dans un nouvel onglet
  const openOfferDetail = (supplierName, duration) => {
    // Créer un identifiant unique combinant nom et durée
    const uniqueId = duration ? `${supplierName}|||${duration}` : supplierName;
    setSelectedOfferDetail(uniqueId);
  };

  // Fonction pour construire le ranking à partir des supplier_offers (pour vue publique)
  const buildRankingFromOffers = (offers) => {
    if (!offers || !Array.isArray(offers)) return [];
    
    const suppliersMap = new Map();
    
    offers
      .filter(offer => offer.a_inserer === 'OUI')
      .sort((a, b) => (a.ranking || 999) - (b.ranking || 999))
      .forEach(offer => {
        const uniqueKey = `${offer.supplier_name}-${offer.duree || ''}`;
        if (!suppliersMap.has(uniqueKey)) {
          suppliersMap.set(uniqueKey, {
            supplier_name: offer.supplier_name,
            total_annual_cost: offer.total_annuel_htva_turpe || 0,
            ranking: offer.ranking || suppliersMap.size + 1,
            is_selected: true,
            duree: offer.duree || '',
            duration: offer.duree || '',
            commission: offer.commission || '',
            offer_type: offer.offre_type || '',
            remarque: offer.remarque || '',
            classement_number: offer.classement_number,
            budget_ligne21: offer.budget_ligne21,
            budget_ligne23: offer.budget_ligne23,
            sheet_reference: offer.sheet_reference
          });
        }
      });
    
    return Array.from(suppliersMap.values());
  };

  useEffect(() => {
    if (latestAnalysis) {
      if (isPublicView) {
        // En mode public, utiliser directement les données de latestAnalysis
        if (latestAnalysis.supplier_offers) {
          const rankingData = buildRankingFromOffers(latestAnalysis.supplier_offers);
          setRanking({ ranking: rankingData, analysis_id: latestAnalysis.analysis_id });
        }
      } else {
        fetchRankingData();
      }
    }
  }, [latestAnalysis, isPublicView]);

  // Auto-save recommendation when it changes (only if user can edit)
  useEffect(() => {
    // Don't save if user can't edit or if no analysis is loaded
    if (!canEditRecommendation || !latestAnalysis?.analysis_id) {
      return;
    }

    // Debounce the save to avoid too many API calls
    const saveTimer = setTimeout(async () => {
      try {
        const token = localStorage.getItem('access_token');
        await axios.post(
          `${API}/analysis/${latestAnalysis.analysis_id}/save-recommendation`,
          {
            supplier_index: recommendedSupplierIndex,
            text: recommendationText
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        console.log('Recommendation saved successfully');
      } catch (error) {
        console.error('Error saving recommendation:', error);
      }
    }, 1000); // Wait 1 second after last change before saving

    return () => clearTimeout(saveTimer);
  }, [recommendedSupplierIndex, recommendationText, latestAnalysis?.analysis_id, canEditRecommendation]);

  const fetchRankingData = async () => {
    if (!latestAnalysis) return;

    try {
      setLoading(true);
      const response = await axios.get(`${API}/analysis/${latestAnalysis.analysis_id}/ranking`);
      setRanking(response.data);
    } catch (error) {
      console.error('Error fetching ranking data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour obtenir le logo d'un fournisseur ou l'emoji par défaut
  const getSupplierLogo = (supplierName) => {
    if (!supplierName) {
      console.log('getSupplierLogo: no supplierName');
      return { type: 'emoji', emoji: '⚡' };
    }
    
    console.log('getSupplierLogo called for:', supplierName);
    
    // Utiliser directement process.env pour accéder à BACKEND_URL
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    console.log('  BACKEND_URL:', BACKEND_URL);
    
    // Normaliser le nom du fournisseur pour le matching avec les fichiers
    // Convertir en minuscules et remplacer les espaces par des tirets
    const normalizedName = supplierName
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')  // Remplacer espaces par tirets
      .replace(/é/g, 'e')    // Remplacer les accents
      .replace(/è/g, 'e')
      .replace(/ê/g, 'e')
      .replace(/à/g, 'a')
      .replace(/ô/g, 'o');
    
    console.log('  Normalized name:', normalizedName);
    
    // Mapping des noms spéciaux
    const specialMapping = {
      'engie': 'engie',
      'engie-pro': 'engie-pro',
      'eni': 'eni',
      'mint': 'mint',
      'mint-energie': 'mint-energie',
      'selia': 'selia',
      'sélia': 'selia',
      'total': 'total-energies',
      'total-energies': 'total-energies',
      'total-energie': 'total-energies',
      'edf': 'edf',
      'proxelia': 'proxelia',
      'la-bellenergie': 'la-bellenergie',
      'lbe': 'la-bellenergie',
      'ekwateur': 'ekwateur',
      'dyneff': 'dyneff',
      'ohm': 'ohm',
      'endesa': 'endesa',
      'alterna': 'alterna',
      'vattenfall': 'vattenfall',
      'ilek': 'ilek',
      'hellio': 'hellio',
      'primeo': 'primeo',
      'primeo-energie': 'primeo'
    };
    
    const logoFileName = specialMapping[normalizedName] || normalizedName;
    console.log('  Logo file name:', logoFileName);
    
    // Essayer d'abord .png puis .jpg
    const logoUrl = `${BACKEND_URL}/api/static/logos/${logoFileName}.png`;
    const logoUrlJpg = `${BACKEND_URL}/api/static/logos/${logoFileName}.jpg`;
    
    console.log('  Logo URL (png):', logoUrl);
    console.log('  Logo URL (jpg):', logoUrlJpg);
    
    // Retourner l'URL du logo avec fallback vers jpg puis emoji
    return {
      type: 'image',
      url: logoUrl,
      urlJpg: logoUrlJpg,
      fallbackEmoji: '⚡',
      alt: supplierName
    };
  };

  const calculateBudgetEvolution = (supplier, budgetActuelHTVA, budgetActuelTTC, isTTCMode) => {
    const budgetActuel = isTTCMode ? budgetActuelTTC : budgetActuelHTVA;
    if (!budgetActuel) return null;
    
    let budgetFournisseur;
    
    if (isTTCMode) {
      // Mode TTC : UNIQUEMENT ligne 23 (B23:K23)
      budgetFournisseur = supplier.budget_ligne23 && supplier.budget_ligne23 > 0 
        ? supplier.budget_ligne23 
        : null;
    } else {
      // Mode HTVA : UNIQUEMENT ligne 21 (B21:K21), sinon fallback
      budgetFournisseur = supplier.budget_ligne21 && supplier.budget_ligne21 > 0 
        ? supplier.budget_ligne21 
        : supplier.total_annual_cost;
    }
    
    // Retourner null si pas de budget fournisseur
    if (budgetFournisseur === null || budgetFournisseur === undefined) {
      return null;
    }
    
    // Evolution = Nouveau budget - Budget actuel
    return budgetFournisseur - budgetActuel;
  };

  // Fonction pour obtenir le budget à afficher selon le mode
  const getBudgetToDisplay = (supplier, isTTCMode) => {
    if (isTTCMode) {
      // Mode TTC : UNIQUEMENT ligne 23 (B23:K23), sinon null
      return supplier.budget_ligne23 && supplier.budget_ligne23 > 0 
        ? supplier.budget_ligne23 
        : null;
    } else {
      // Mode HTVA : UNIQUEMENT ligne 21 (B21:K21), sinon total_annual_cost en fallback
      return supplier.budget_ligne21 && supplier.budget_ligne21 > 0 
        ? supplier.budget_ligne21 
        : supplier.total_annual_cost;
    }
  };

  if (!latestAnalysis) {
    return <EmptyState />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '40px', height: '40px', border: '3px solid #E2E6EA', borderTopColor: '#397DFF', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ fontFamily: 'var(--font-corps)', fontSize: '0.9375rem', color: '#687175' }}>Chargement du comparateur...</p>
        </div>
      </div>
    );
  }

  const bestCost = ranking?.ranking?.[0]?.total_annual_cost || 0;

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0.5rem 0.5rem 2rem 0.5rem' }}>
      {/* Header selon charte PDE */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        justifyContent: 'space-between', 
        marginBottom: '2rem',
        flexWrap: 'wrap',
        gap: '1rem',
        paddingLeft: '1rem',
        paddingRight: '1rem'
      }}>
        <div>
          <h2 style={{
            fontFamily: 'var(--font-titre)',
            fontWeight: '500',
            color: 'var(--pde-anthracite)',
            fontSize: '2rem',
            margin: '0',
            display: 'flex',
            alignItems: 'center'
          }}>
            <img 
              src={`${process.env.REACT_APP_BACKEND_URL}/api/static/logos/picto-budget.jpg`}
              alt="Budget" 
              style={{ 
                height: '2.5rem', 
                width: 'auto',
                marginRight: '1rem'
              }} 
            />
            Mes offres
          </h2>
          <p style={{
            fontFamily: 'var(--font-corps)',
            color: 'var(--pde-gris-acier)',
            fontSize: '1rem',
            margin: '0.5rem 0 0 0'
          }}>
            Classement par budget annuel HT
          </p>
          
          {/* Sélecteur du fournisseur recommandé - Visible UNIQUEMENT pour ADV/SuperAdmin */}
          {canEditRecommendation && (
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ 
                fontFamily: 'var(--font-corps)', 
                fontSize: '0.875rem',
                color: 'var(--pde-anthracite)',
                fontWeight: '500'
              }}>
                Notre favori :
              </span>
              {/* Bouton "Aucun" pour désélectionner */}
              <button
                onClick={() => setRecommendedSupplierIndex(null)}
                style={{
                  padding: '0.35rem 0.75rem',
                  borderRadius: '6px',
                  border: `2px solid ${recommendedSupplierIndex === null ? 'var(--pde-ambre)' : 'var(--pde-border)'}`,
                  background: recommendedSupplierIndex === null ? 'var(--pde-ambre)' : 'white',
                  color: 'var(--pde-anthracite)',
                  fontFamily: 'var(--font-corps)',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Aucun
              </button>
              {ranking?.ranking
                ?.filter(supplier => 
                  supplier.total_annual_cost && 
                  supplier.total_annual_cost > 0 && 
                  supplier.is_selected === true
                )
                ?.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setRecommendedSupplierIndex(idx)}
                  style={{
                    padding: '0.35rem 0.75rem',
                    borderRadius: '6px',
                    border: `2px solid ${idx === recommendedSupplierIndex ? 'var(--pde-ambre)' : 'var(--pde-border)'}`,
                    background: idx === recommendedSupplierIndex ? 'var(--pde-ambre)' : 'white',
                    color: 'var(--pde-anthracite)',
                    fontFamily: 'var(--font-corps)',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Fournisseur #{idx + 1}
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Toggle HTVA/TTC aligné à droite */}
        <div style={{
          display: 'flex',
          background: 'var(--pde-surface)',
          border: '2px solid var(--pde-border)',
          borderRadius: '8px',
          padding: '0.125rem',
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)'
        }}>
          <button
            onClick={() => setIsTTCMode(false)}
            style={{
              padding: '0.5rem 1rem',
              fontFamily: 'var(--font-titre)',
              fontWeight: '600',
              fontSize: '0.75rem',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              background: !isTTCMode ? 'var(--pde-azur)' : 'transparent',
              color: !isTTCMode ? 'white' : 'var(--pde-gris-acier)',
            }}
          >
            HTVA
          </button>
          <button
            onClick={() => setIsTTCMode(true)}
            style={{
              padding: '0.5rem 1rem',
              fontFamily: 'var(--font-titre)',
              fontWeight: '600',
              fontSize: '0.75rem',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              background: isTTCMode ? 'var(--pde-azur)' : 'transparent',
              color: isTTCMode ? 'white' : 'var(--pde-gris-acier)',
            }}
          >
            TTC
          </button>
        </div>
      </div>

      {/* Supplier Cards Grid selon charte PDE - Triés par budget annuel HT */}
      {ranking && ranking.ranking && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '2rem',
          marginBottom: '3rem'
        }}>
          {ranking.ranking
            .filter(supplier => 
              supplier.total_annual_cost && 
              supplier.total_annual_cost > 0 && 
              supplier.is_selected === true // Ne montrer que les fournisseurs marqués "OUI" dans l'onglet classement (via is_selected boolean)
            )
            .sort((a, b) => {
              // Si les deux ont un classement_number valide, trier par classement_number
              if (a.classement_number && a.classement_number !== 999 && 
                  b.classement_number && b.classement_number !== 999) {
                return a.classement_number - b.classement_number;
              }
              // Sinon, trier par budget annuel HT croissant (comportement actuel)
              return (a.total_annual_cost || 0) - (b.total_annual_cost || 0);
            }) // Trier par classement personnalisé (ligne 1) ou budget annuel HT
            .map((supplier, index) => {
            const budgetEvolution = calculateBudgetEvolution(
              supplier, 
              latestAnalysis?.client_info?.budget_actuel_htva,
              latestAnalysis?.client_info?.budget_actuel_ttc,
              isTTCMode
            );
            const isRecommended = index === recommendedSupplierIndex;
            
            const cardStyle = {
              position: 'relative',
              background: 'var(--pde-surface)',
              border: `2px solid ${isRecommended ? 'var(--pde-ambre)' : 'var(--pde-border)'}`,
              borderRadius: '16px',
              padding: '1.5rem',
              boxShadow: isRecommended 
                ? '0 4px 16px rgba(249, 200, 14, 0.2)' 
                : '0 4px 12px rgba(60, 70, 73, 0.08)',
              transition: 'all 0.3s ease'
            };
            
            return (
              <div
                key={`${supplier.supplier_name}-${supplier.duration}-${index}`}
                style={cardStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = isRecommended 
                    ? '0 8px 32px rgba(249, 200, 14, 0.3)'
                    : '0 8px 24px rgba(60, 70, 73, 0.15)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = isRecommended 
                    ? '0 4px 16px rgba(249, 200, 14, 0.2)'
                    : '0 4px 12px rgba(60, 70, 73, 0.08)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {/* Notre favori Badge selon charte PDE */}
                {isRecommended && (
                  <div style={{
                    position: 'absolute',
                    top: '-0.75rem',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'var(--pde-ambre)',
                    color: 'var(--pde-anthracite)',
                    padding: '0.5rem 1rem',
                    borderRadius: '20px',
                    fontFamily: 'var(--font-corps)',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    boxShadow: '0 2px 8px rgba(249, 200, 14, 0.3)'
                  }}>
                    NOTRE FAVORI
                  </div>
                )}

                {/* Ranking Badge selon charte PDE */}
                <div style={{
                  position: 'absolute',
                  top: '-0.75rem',
                  right: '-0.75rem',
                  background: 'var(--pde-azur)',
                  color: 'white',
                  borderRadius: '50%',
                  width: '2rem',
                  height: '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'var(--font-titre)',
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  boxShadow: '0 2px 8px rgba(57, 125, 255, 0.3)'
                }}>
                  {(() => {
                    // Utiliser classement_number si disponible et valide, sinon l'index+1
                    if (supplier.classement_number && supplier.classement_number !== 999) {
                      return `#${supplier.classement_number}`;
                    } else {
                      return `#${index + 1}`;
                    }
                  })()}
                </div>

                <div>
                  {/* Supplier Header selon charte PDE - Logo centré avec nom en dessous */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '1.5rem' }}>
                    {/* Logo Container - 8.4375rem (agrandi x1.25) */}
                    <div style={{ width: '8.4375rem', height: '8.4375rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.1rem' }}>
                      {(() => {
                        const logoData = getSupplierLogo(supplier.supplier_name);
                        if (logoData.type === 'image') {
                          return (
                            <>
                              <img 
                                src={logoData.url} 
                                alt={logoData.alt}
                                style={{ 
                                  maxWidth: '100%', 
                                  maxHeight: '100%', 
                                  objectFit: 'contain',
                                  borderRadius: '4px'
                                }}
                                onError={(e) => {
                                  console.log('Image load error for:', e.target.src);
                                  // Essayer le jpg si le png ne charge pas
                                  if (logoData.urlJpg && e.target.src !== logoData.urlJpg) {
                                    console.log('  Trying JPG fallback:', logoData.urlJpg);
                                    e.target.src = logoData.urlJpg;
                                  } else {
                                    console.log('  Both PNG and JPG failed, showing emoji');
                                    // Fallback vers emoji si jpg ne marche pas non plus
                                    e.target.style.display = 'none';
                                    const emojiSpan = e.target.nextElementSibling;
                                    if (emojiSpan) {
                                      emojiSpan.style.display = 'block';
                                    }
                                  }
                                }}
                                onLoad={(e) => {
                                  console.log('Image loaded successfully:', e.target.src);
                                }}
                              />
                              {/* Emoji de secours affiché si l'image ne charge pas */}
                              <span style={{ fontSize: '3.28125rem', display: 'none' }}>
                                {logoData.fallbackEmoji || '⚡'}
                              </span>
                            </>
                          );
                        } else {
                          return (
                            <span style={{ fontSize: '3.28125rem' }}>
                              {logoData.emoji}
                            </span>
                          );
                        }
                      })()}
                    </div>
                    {/* Nom du fournisseur sous le logo - Taille agrandie x1.5 */}
                    <h3 style={{
                      fontFamily: 'var(--font-titre)',
                      fontWeight: '500',
                      fontSize: '0.9375rem',
                      color: 'var(--pde-anthracite)',
                      margin: '0',
                      textAlign: 'center'
                    }}>
                      {supplier.supplier_name}
                    </h3>
                  </div>

                  {/* Metrics selon charte PDE */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '0.75rem',
                      background: 'rgba(57, 125, 255, 0.05)',
                      border: '1px solid rgba(57, 125, 255, 0.2)',
                      borderRadius: '8px',
                      padding: '0.75rem'
                    }}>
                      <span style={{
                        fontFamily: 'var(--font-corps)',
                        fontSize: '0.875rem',
                        color: 'var(--pde-azur)',
                        fontWeight: '600'
                      }}>
                        {isTTCMode ? 'Budget Annuel TTC' : 'Budget Annuel HTVA'}
                      </span>
                      <span style={{
                        fontFamily: 'var(--font-titre)',
                        fontWeight: '700',
                        fontSize: '1.125rem',
                        color: 'var(--pde-azur)'
                      }}>
                        {(() => {
                          const budget = getBudgetToDisplay(supplier, isTTCMode);
                          if (budget === null || budget === undefined) {
                            return isTTCMode ? 'Données ligne 23 requises' : 'N/A';
                          }
                          return budget.toLocaleString('fr-FR', { 
                            minimumFractionDigits: 2, 
                            maximumFractionDigits: 2 
                          });
                        })()} {getBudgetToDisplay(supplier, isTTCMode) !== null ? '€' : ''}
                      </span>
                    </div>

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '0.75rem'
                    }}>
                      <span style={{
                        fontFamily: 'var(--font-corps)',
                        fontSize: '0.875rem',
                        color: 'var(--pde-gris-acier)'
                      }}>
                        Evolution du budget
                      </span>
                      <span style={{
                        fontFamily: 'var(--font-titre)',
                        fontWeight: '600',
                        color: budgetEvolution === null ? 'var(--pde-gris-acier)' : 
                               (budgetEvolution > 0 ? '#dc2626' : (budgetEvolution < 0 ? '#16a34a' : 'var(--pde-gris-acier)'))
                      }}>
                        {budgetEvolution === null ? 
                          (isTTCMode ? 'Données requises' : 'N/A') :
                          `${budgetEvolution > 0 ? '+' : ''}${budgetEvolution.toLocaleString('fr-FR', { 
                            minimumFractionDigits: 2, 
                            maximumFractionDigits: 2 
                          })} €`
                        }
                      </span>
                    </div>

                    {/* Durée de contrat */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '0.75rem'
                    }}>
                      <span style={{
                        fontFamily: 'var(--font-corps)',
                        fontSize: '0.875rem',
                        color: 'var(--pde-gris-acier)'
                      }}>
                        Durée de contrat
                      </span>
                      <span style={{
                        fontFamily: 'var(--font-corps)',
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        color: 'var(--pde-anthracite)'
                      }}>
                        {supplier.duree ? `${supplier.duree} mois` : 'N/A'}
                      </span>
                    </div>

                    {/* Type offre */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: supplier.remarque ? '0.75rem' : '0'
                    }}>
                      <span style={{
                        fontFamily: 'var(--font-corps)',
                        fontSize: '0.875rem',
                        color: 'var(--pde-gris-acier)'
                      }}>
                        Type offre
                      </span>
                      <span style={{
                        fontFamily: 'var(--font-corps)',
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        color: 'var(--pde-anthracite)'
                      }}>
                        {supplier.offer_type || 'N/A'}
                      </span>
                    </div>

                    {/* Remarque - afficher uniquement si des données existent */}
                    {supplier.remarque && supplier.remarque.trim() !== '' && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <span style={{
                          fontFamily: 'var(--font-corps)',
                          fontSize: '0.875rem',
                          color: 'var(--pde-gris-acier)'
                        }}>
                          Remarque
                        </span>
                        <span style={{
                          fontFamily: 'var(--font-corps)',
                          fontWeight: '600',
                          fontSize: '0.875rem',
                          color: 'var(--pde-anthracite)'
                        }}>
                          {supplier.remarque}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Recommendation selon charte PDE */}
                  {isRecommended && (
                    <div style={{
                      background: 'rgba(57, 125, 255, 0.05)',
                      border: '1px solid rgba(57, 125, 255, 0.2)',
                      borderRadius: '8px',
                      padding: '1rem',
                      marginBottom: '1.5rem'
                    }}>
                      <div>
                        <p style={{
                          fontFamily: 'var(--font-corps)',
                          fontWeight: '600',
                          color: 'var(--pde-azur)',
                          margin: '0',
                          fontSize: '0.875rem',
                          marginBottom: '0.5rem'
                        }}>
                          Notre recommandation :
                        </p>
                        <textarea
                          value={recommendationText}
                          onChange={canEditRecommendation ? (e) => setRecommendationText(e.target.value) : undefined}
                          readOnly={!canEditRecommendation}
                          style={{
                            fontFamily: 'var(--font-corps)',
                            fontSize: '0.875rem',
                            color: 'var(--pde-anthracite-clair)',
                            margin: '0',
                            lineHeight: '1.4',
                            width: '100%',
                            minHeight: '120px',
                            border: '1px solid var(--pde-border)',
                            borderRadius: '8px',
                            padding: '0.5rem',
                            resize: canEditRecommendation ? 'vertical' : 'none',
                            boxSizing: 'border-box',
                            backgroundColor: canEditRecommendation ? 'white' : '#f5f5f5',
                            cursor: canEditRecommendation ? 'text' : 'default'
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Details Button selon charte PDE */}
                  <button 
                    onClick={() => openOfferDetail(supplier.supplier_name, supplier.duree)}
                    style={{
                      width: '100%',
                      background: 'var(--pde-azur)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.75rem 1rem',
                      fontFamily: 'var(--font-corps)',
                      fontWeight: '600',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 8px rgba(57, 125, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#2C5FBF';
                      e.target.style.boxShadow = '0 4px 16px rgba(57, 125, 255, 0.3)';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'var(--pde-azur)';
                      e.target.style.boxShadow = '0 2px 8px rgba(57, 125, 255, 0.2)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/static/logos/picto-detail.png`}
                      alt="Détails" 
                      style={{ 
                        height: '1.2rem', 
                        width: 'auto',
                        filter: 'brightness(0) invert(1)'
                      }} 
                    />
                    Détails de l'offre
                  </button>
                </div>

                {/* Plus besoin du tableau ici car il s'ouvre dans un nouvel onglet */}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Sites Component with data
const SitesTab = ({ latestAnalysis }) => {
  const [analysisDetails, setAnalysisDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConsommationDetails, setShowConsommationDetails] = useState(false);
  
  // États pour les filtres
  const [filters, setFilters] = useState({
    segment: '',
    ville: '',
    echeance: ''
  });
  
  // États pour le tri
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  
  // États pour l'affichage des détails
  const [showBudgetDetails, setShowBudgetDetails] = useState(false);
  const [showTaxesDetails, setShowTaxesDetails] = useState(false);
  
  // État pour le mode HTVA/TTC
  const [tableMode, setTableMode] = useState('HTVA'); // 'HTVA' ou 'TTC'
  
  // Fonction pour vérifier si les colonnes ENERGIE VERTE ont des données
  useEffect(() => {
    if (latestAnalysis) {
      fetchSitesData();
    }
  }, [latestAnalysis]);

  const fetchSitesData = async () => {
    if (!latestAnalysis) return;

    try {
      setLoading(true);
      const response = await axios.get(`${API}/analysis/${latestAnalysis.analysis_id}`);
      setAnalysisDetails(response.data);
    } catch (error) {
      console.error('Error fetching sites data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour réinitialiser les filtres
  const resetFilters = () => {
    setFilters({
      segment: '',
      ville: '',
      echeance: ''
    });
  };

  // Fonction pour gérer le tri
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Obtenir les valeurs uniques pour les filtres
  const getUniqueValues = (key) => {
    if (!analysisDetails?.sites) return [];
    return [...new Set(analysisDetails.sites.map(site => site[key]).filter(Boolean))].sort();
  };

  // Filtrer et trier les sites
  const getFilteredAndSortedSites = () => {
    if (!analysisDetails?.sites) return [];
    
    let filteredSites = analysisDetails.sites.filter(site => {
      return (!filters.segment || site.segment === filters.segment) &&
             (!filters.ville || site.ville === filters.ville) &&
             (!filters.echeance || site.echeance === filters.echeance);
    });

    // Tri
    if (sortConfig.key) {
      filteredSites.sort((a, b) => {
        let aValue, bValue;
        
        if (sortConfig.key === 'segment') {
          aValue = a.segment || '';
          bValue = b.segment || '';
        } else if (sortConfig.key === 'consumption') {
          aValue = (a.conso_hph || 0) + (a.conso_hch || 0) + (a.conso_hpe || 0) + (a.conso_hce || 0) + (a.conso_pointe || 0);
          bValue = (b.conso_hph || 0) + (b.conso_hch || 0) + (b.conso_hpe || 0) + (b.conso_hce || 0) + (b.conso_pointe || 0);
        } else if (sortConfig.key === 'echeance') {
          aValue = new Date(a.echeance || '');
          bValue = new Date(b.echeance || '');
        }
        
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filteredSites;
  };

  const filteredAndSortedSites = getFilteredAndSortedSites();

  const hasEnergyVerteData = () => {
    if (!filteredAndSortedSites.length) return false;
    return filteredAndSortedSites.some(site => 
      (site.prix_energie_verte && site.prix_energie_verte > 0) || 
      (site.budget_option_verte && site.budget_option_verte > 0)
    );
  };

  const hasCeeData = () => {
    if (!filteredAndSortedSites.length) return false;
    return filteredAndSortedSites.some(site => 
      (site.prix_cee && site.prix_cee > 0) || 
      (site.budget_cee && site.budget_cee > 0)
    );
  };

  // Fonctions pour vérifier les colonnes conditionnelles
  const hasPrixElectronPointe = () => {
    if (!filteredAndSortedSites.length) return false;
    return filteredAndSortedSites.some(site => site.prix_pointe && site.prix_pointe > 0);
  };
  
  const hasAbonnementParAn = () => {
    if (!filteredAndSortedSites.length) return false;
    return filteredAndSortedSites.some(site => site.abonnement_par_an && site.abonnement_par_an > 0);
  };

  const hasConsoPointe = () => {
    if (!filteredAndSortedSites.length) return false;
    return filteredAndSortedSites.some(site => site.conso_pointe && site.conso_pointe > 0);
  };

  const hasPrixCapaciteHph = () => {
    if (!filteredAndSortedSites.length) return false;
    return filteredAndSortedSites.some(site => site.capacite_hph && site.capacite_hph > 0);
  };

  const hasPrixCapaciteHch = () => {
    if (!filteredAndSortedSites.length) return false;
    return filteredAndSortedSites.some(site => site.capacite_hch && site.capacite_hch > 0);
  };

  const hasPrixCapaciteHpe = () => {
    if (!filteredAndSortedSites.length) return false;
    return filteredAndSortedSites.some(site => site.capacite_hpe && site.capacite_hpe > 0);
  };

  const hasPrixCapaciteHce = () => {
    if (!filteredAndSortedSites.length) return false;
    return filteredAndSortedSites.some(site => site.capacite_hce && site.capacite_hce > 0);
  };

  const hasPrixCapacitePointe = () => {
    if (!filteredAndSortedSites.length) return false;
    return filteredAndSortedSites.some(site => site.capacite_pointe && site.capacite_pointe > 0);
  };

  const hasBudgetCapacite = () => {
    if (!filteredAndSortedSites.length) return false;
    return filteredAndSortedSites.some(site => site.budget_capacite && site.budget_capacite > 0);
  };
  
  // Calculer les totaux pour la ligne de somme
  const calculateTotals = () => {
    return filteredAndSortedSites.reduce((totals, site) => {
      const totalConso = (site.conso_hph || 0) + (site.conso_hch || 0) + (site.conso_hpe || 0) + (site.conso_hce || 0) + (site.conso_pointe || 0);
      
      // Calculer le total des taxes et contributions
      const budgetCapacite = parseFloat(site.budget_capacite) || 0;
      const budgetCee = parseFloat(site.budget_cee) || 0;
      const cta = parseFloat(site.cta) || 0;
      const cspe = parseFloat(site.cspe) || 0;
      const totalTaxes = budgetCapacite + budgetCee + cta + cspe;
      
      // ACHEMINEMENT
      const acheminement = parseFloat(site.acheminement) || 0;
      
      // BUDGET ANNUEL HTVA
      const budgetAnnuelHtva = parseFloat(site.budget_annuel_htva) || 0;
      
      // TVA et BUDGET ANNUEL TTC
      const tva = parseFloat(site.tva) || 0;
      const budgetAnnuelTTC = parseFloat(site.budget_annuel_ttc) || 0;
      
      return {
        totalConsommations: totals.totalConsommations + totalConso,
        budgetFourniture: totals.budgetFourniture + (parseFloat(site.budget_fourniture) || 0),
        budgetElectron: totals.budgetElectron + (parseFloat(site.budget_electron) || 0),
        abonnementParAn: totals.abonnementParAn + (parseFloat(site.abonnement_par_an) || 0),
        budgetOptionVerte: totals.budgetOptionVerte + (parseFloat(site.budget_option_verte) || 0),
        // Nouveaux totaux TAXES ET CONTRIBUTIONS
        totalTaxesContributions: totals.totalTaxesContributions + totalTaxes,
        budgetCapacite: totals.budgetCapacite + budgetCapacite,
        budgetCee: totals.budgetCee + budgetCee,
        cta: totals.cta + cta,
        cspe: totals.cspe + cspe,
        // Nouveau total ACHEMINEMENT
        acheminement: totals.acheminement + acheminement,
        // Nouveaux totaux BUDGET ANNUEL HTVA et TTC
        budgetAnnuelHtva: totals.budgetAnnuelHtva + budgetAnnuelHtva,
        tva: totals.tva + tva,
        budgetAnnuelTTC: totals.budgetAnnuelTTC + budgetAnnuelTTC
      };
    }, {
      totalConsommations: 0,
      budgetFourniture: 0,
      budgetElectron: 0,
      abonnementParAn: 0,
      budgetOptionVerte: 0,
      // Nouveaux totaux initialisés
      totalTaxesContributions: 0,
      budgetCapacite: 0,
      budgetCee: 0,
      cta: 0,
      cspe: 0,
      // Nouveau total ACHEMINEMENT initialisé
      acheminement: 0,
      // Nouveaux totaux BUDGET ANNUEL HTVA et TTC initialisés
      budgetAnnuelHtva: 0,
      tva: 0,
      budgetAnnuelTTC: 0
    });
  };

  const totals = calculateTotals();

  // Fonction d'export PDF simplifiée et corrigée
  const exportToPDF = async () => {
    try {
      console.log('Début génération PDF...');
      
      if (!filteredAndSortedSites || filteredAndSortedSites.length === 0) {
        alert('Aucune donnée à exporter');
        return;
      }
      
      // Créer un nouveau document PDF en format paysage A4
      const doc = new jsPDF('l', 'mm', 'a4');
      
      // Configuration
      const pageWidth = doc.internal.pageSize.getWidth(); // 297mm
      const pageHeight = doc.internal.pageSize.getHeight(); // 210mm
      const margin = 8;
      let currentY = 15;
      let pageNumber = 1;
      
      // Charger le logo Place des Energies depuis le backend
      let logoData = null;
      try {
        // Essayer d'abord en JPG, puis en PNG
        let logoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/static/logos/place-des-energies.jpg`;
        let response = await fetch(logoUrl);
        
        if (!response.ok) {
          logoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/static/logos/place-des-energies.png`;
          response = await fetch(logoUrl);
        }
        
        if (response.ok) {
          const blob = await response.blob();
          logoData = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
          console.log('Logo chargé avec succès');
        }
      } catch (error) {
        console.log('Erreur lors du chargement du logo:', error);
      }
      
 
      
      // Fonction pour ajouter le logo Place des Energies en haut à droite
      const addLogo = () => {
        if (!logoData) {
          console.log('Pas de logo PDE à ajouter');
          return;
        }
        
        const logoWidth = 40; // Largeur du logo en mm
        const logoHeight = 12; // Hauteur du logo en mm
        const logoX = pageWidth - margin - logoWidth; // Position X (à droite)
        const logoY = 5; // Position Y (en haut)
        
        try {
          doc.addImage(logoData, 'JPEG', logoX, logoY, logoWidth, logoHeight);
          console.log('Logo PDE ajouté à la page');
        } catch (error) {
          console.log('Erreur lors de l\'ajout du logo:', error);
        }
      };
      
      // Fonction pour ajouter le logo du fournisseur au centre (augmenté x1.5)
      const addSupplierLogo = (posY = 5) => {
        if (!supplierLogoData) {
          console.log('Pas de logo fournisseur à ajouter');
          return 0; // Retourne 0 si pas de logo
        }
        
        const logoMaxWidth = 30; // Augmenté x1.5 (20 → 30mm)
        const logoEstimatedHeight = 15; // Hauteur estimée généreuse pour éviter chevauchement
        const logoX = (pageWidth - logoMaxWidth) / 2; // Position X (centré)
        const logoY = posY; // Position Y passée en paramètre
        
        try {
          // Déterminer le format (JPEG ou PNG)
          const format = supplierLogoData.includes('data:image/png') ? 'PNG' : 'JPEG';
          // Utiliser seulement la largeur, jsPDF calculera la hauteur automatiquement pour garder le ratio
          doc.addImage(supplierLogoData, format, logoX, logoY, logoMaxWidth, 0);
          console.log(`Logo fournisseur ajouté à la page (30mm largeur, hauteur auto) à Y=${posY}`);
          return logoEstimatedHeight; // Retourne une estimation généreuse de la hauteur
        } catch (error) {
          console.log('Erreur lors de l\'ajout du logo fournisseur:', error);
          return 0;
        }
      };
      
      // Fonction pour ajouter le logo Place des Energies en filigrane (fond de tableau avec transparence)
      const addWatermark = () => {
        if (!logoData) {
          console.log('Pas de logo pour le filigrane');
          return;
        }
        
        try {
          // Position et taille du filigrane (centré au milieu de la page)
          const watermarkWidth = 140; // Largeur du filigrane en mm (augmentée pour visibilité)
          const watermarkHeight = 42; // Hauteur du filigrane en mm
          const watermarkX = (pageWidth - watermarkWidth) / 2;
          const watermarkY = (pageHeight - watermarkHeight) / 2;
          
          // Ajouter le logo en filigrane avec opacité réduite
          doc.saveGraphicsState();
          doc.setGState(new doc.GState({ opacity: 0.1 })); // 10% d'opacité (90% de transparence)
          doc.addImage(logoData, 'JPEG', watermarkX, watermarkY, watermarkWidth, watermarkHeight);
          doc.restoreGraphicsState();
          
          console.log('Filigrane ajouté à la page (140x42mm, 10% opacité)');
        } catch (error) {
          console.log('Erreur GState, tentative sans transparence:', error);
          // Tentative alternative sans GState
          try {
            const watermarkWidth = 140;
            const watermarkHeight = 42;
            const watermarkX = (pageWidth - watermarkWidth) / 2;
            const watermarkY = (pageHeight - watermarkHeight) / 2;
            doc.addImage(logoData, 'JPEG', watermarkX, watermarkY, watermarkWidth, watermarkHeight);
            console.log('Filigrane ajouté (sans transparence)');
          } catch (err2) {
            console.log('Impossible d\'ajouter le filigrane:', err2);
          }
        }
      };
      
      // Fonction utilitaire pour formater les montants avec centimes et séparateur espace
      const formatAmountNumeric = (amount) => {
        if (!amount) return '0,00 €';
        return new Intl.NumberFormat('fr-FR', { 
          minimumFractionDigits: 2, 
          maximumFractionDigits: 2 
        }).format(amount).replace(/\s/g, ' ') + ' €';
      };
      
      // Fonction utilitaire pour formater les montants avec décimales (pour affichage normal)
      const formatAmount = (amount) => {
        if (!amount) return '0,00';
        return new Intl.NumberFormat('fr-FR', { 
          minimumFractionDigits: 2, 
          maximumFractionDigits: 2 
        }).format(amount);
      };
      
      // Fonction pour ajouter une nouvelle page
      const addNewPage = () => {
        // PAS de numéro de page ici (ajouté à la fin pour toutes les pages)
        pageNumber++;
        
        doc.addPage();
        currentY = 22; // Réduit de 25 à 22 pour cohérence avec la première page
        
        // Ajouter le logo sur la nouvelle page
        addLogo();
        // Le filigrane sera ajouté à la fin sur toutes les pages
        
        // Répéter les en-têtes
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Mon Périmètre - Suite', margin, currentY);
        currentY += 10;
        
        // En-têtes du tableau
        doc.setFillColor(57, 125, 255); // AZUR
        doc.rect(margin, currentY, pageWidth - (margin * 2), 12, 'F'); // Augmenté de 8 à 12
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        
        // Headers sur 2 lignes
        const headers = [
          ['SITE', ''],
          ['PRM', ''],
          ['ADRESSE', ''],
          ['SEGMENT', ''],
          ['ECHEANCE', 'ACTUELLE'],
          ['TOTAL', 'CONSO'],
          ['BUDGET', 'FOURNITURE'],
          ['ACHEMI-', 'NEMENT'],
          ['TAXES ET', 'CONTRIB.'],
          ['BUDGET', 'HTVA']
        ];
        
        if (tableMode === 'TTC') {
          headers.push(['TVA', '']);
          headers.push(['BUDGET', 'ANNUEL TTC']);
        }
        
        let x = margin;
        
        for (let i = 0; i < headers.length; i++) {
          const [line1, line2] = headers[i];
          const width = colWidths[i];
          const textWidth1 = doc.getTextWidth(line1);
          const textWidth2 = doc.getTextWidth(line2);
          
          // Première ligne
          doc.text(line1, x + (width - textWidth1) / 2, currentY + 4);
          
          // Deuxième ligne (si elle existe)
          if (line2) {
            doc.text(line2, x + (width - textWidth2) / 2, currentY + 8);
          }
          
          x += width;
        }
        
        currentY += 14; // Augmenté pour accommoder les 2 lignes
        
        // Réinitialiser le style pour les données
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6);
      };
      
      // Ajouter le logo sur la première page
      addLogo();
      // Le filigrane sera ajouté à la fin, PAR DESSUS les données
      
      // Titre
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(25, 29, 30); // ANTHRACITE
      doc.text('Mon Périmètre - Analyse des Sites', margin, currentY);
      currentY += 6;
      
      // Informations
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      const currentDate = new Date().toLocaleDateString('fr-FR');
      doc.text(`Exporté le: ${currentDate} | Sites: ${filteredAndSortedSites.length}`, margin, currentY);
      currentY += 8;
      
      // En-têtes du tableau
      doc.setFillColor(57, 125, 255); // AZUR
      doc.rect(margin, currentY, pageWidth - (margin * 2), 12, 'F'); // Augmenté de 8 à 12 pour 2 lignes
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      
      // Headers sur 2 lignes pour meilleure lecture
      const headers = [
        ['SITE', ''],
        ['PRM', ''],
        ['ADRESSE', ''],
        ['SEGMENT', ''],
        ['ECHEANCE', 'ACTUELLE'],
        ['TOTAL', 'CONSO'],
        ['BUDGET', 'FOURNITURE'],
        ['ACHEMI-', 'NEMENT'],
        ['TAXES ET', 'CONTRIB.'],
        ['BUDGET', 'HTVA']
      ];
      
      if (tableMode === 'TTC') {
        headers.push(['TVA', '']);
        headers.push(['BUDGET', 'ANNUEL TTC']);
      }
      
      // Largeurs personnalisées des colonnes (en mm) - optimisées pour utiliser toute la page
      const colWidths = [
        35,  // SITE (élargie)
        35,  // PRM (élargie)
        45,  // ADRESSE (élargie pour adresses complètes)
        23,  // SEGMENT
        23,  // ÉCHÉANCE
        23,  // TOTAL CONSO
        23,  // BUDGET FOURNITURE
        23,  // ACHEMINEMENT
        23,  // TAXES
        23   // BUDGET HTVA
      ];
      
      if (tableMode === 'TTC') {
        colWidths[0] = 30;  // SITE (réduite en TTC)
        colWidths[1] = 30;  // PRM (réduite en TTC)
        colWidths[2] = 40;  // ADRESSE (réduite en TTC)
        colWidths[3] = 19;  // SEGMENT (réduite en TTC)
        colWidths[4] = 19;  // ÉCHÉANCE (réduite en TTC)
        colWidths[5] = 19;  // TOTAL CONSO (réduite en TTC)
        colWidths[6] = 19;  // BUDGET FOURNITURE (réduite en TTC)
        colWidths[7] = 19;  // ACHEMINEMENT (réduite en TTC)
        colWidths[8] = 19;  // TAXES (réduite en TTC)
        colWidths[9] = 19;  // BUDGET HTVA (réduite en TTC)
        colWidths.push(19); // TVA
        colWidths.push(19); // BUDGET ANNUEL TTC
      }
      
      let x = margin;
      
      for (let i = 0; i < headers.length; i++) {
        const [line1, line2] = headers[i];
        const width = colWidths[i];
        const textWidth1 = doc.getTextWidth(line1);
        const textWidth2 = doc.getTextWidth(line2);
        
        // Première ligne
        doc.text(line1, x + (width - textWidth1) / 2, currentY + 4);
        
        // Deuxième ligne (si elle existe)
        if (line2) {
          doc.text(line2, x + (width - textWidth2) / 2, currentY + 8);
        }
        
        x += width;
      }
      
      currentY += 14; // Augmenté pour accommoder les 2 lignes
      
      // Données
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      
      for (let i = 0; i < filteredAndSortedSites.length; i++) {
        const site = filteredAndSortedSites[i];
        
        // Nouvelle page si nécessaire (hauteur augmentée pour adresses sur 2 lignes)
        if (currentY > pageHeight - 20) {
          addNewPage();
        }
        
        // Fond BLANC LUNAIRE pour toute la ligne (hauteur augmentée pour 2 lignes d'adresse)
        doc.setFillColor(245, 246, 249);
        doc.rect(margin, currentY, pageWidth - (margin * 2), 8, 'F'); // Augmenté de 5 à 8
        
        // Données
        const totalConso = ((site.conso_hph || 0) + (site.conso_hch || 0) + (site.conso_hpe || 0) + (site.conso_hce || 0) + (site.conso_pointe || 0)).toFixed(1);
        const taxes = (site.cta || 0) + (site.cspe || 0) + (site.budget_capacite || 0) + (site.budget_cee || 0);
        
        // Formatage de la date d'échéance
        const echeanceFormatted = site.echeance ? 
          new Date(site.echeance).toLocaleDateString('fr-FR', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
          }) : '';
        
        // Construction de l'adresse en 2 parties pour affichage sur 2 lignes
        let adresseLigne1 = (site.address || '').substring(0, 40);
        let adresseLigne2 = '';
        if (site.cp && site.ville) {
          adresseLigne2 = site.cp + ' ' + site.ville;
        } else if (site.cp) {
          adresseLigne2 = site.cp;
        } else if (site.ville) {
          adresseLigne2 = site.ville;
        }
        
        const rowData = [
          (site.nom_site || '').substring(0, 25), // Plus de caractères pour SITE
          (site.prm || '').substring(0, 14),
          { ligne1: adresseLigne1, ligne2: adresseLigne2 }, // Adresse sur 2 lignes
          site.segment || '',
          echeanceFormatted,
          totalConso + ' MWh', // Ajout de "MWh"
          formatAmountNumeric(site.budget_fourniture || 0), // Avec € et centimes
          formatAmountNumeric(site.acheminement || 0), // Avec € et centimes
          formatAmountNumeric(taxes), // Avec € et centimes
          formatAmountNumeric(site.budget_annuel_htva || 0) // Avec € et centimes
        ];
        
        if (tableMode === 'TTC') {
          rowData.push(formatAmountNumeric(site.tva || 0)); // TVA
          rowData.push(formatAmountNumeric(site.budget_annuel_ttc || 0)); // BUDGET ANNUEL TTC
        }
        
        x = margin;
        for (let j = 0; j < rowData.length; j++) {
          const data = rowData[j];
          const width = colWidths[j];
          
          // Traitement spécial pour l'adresse (colonne 2)
          if (j === 2 && typeof data === 'object') {
            // Adresse sur 2 lignes
            const textWidth1 = doc.getTextWidth(data.ligne1);
            const textWidth2 = doc.getTextWidth(data.ligne2);
            doc.text(data.ligne1, x + (width - textWidth1) / 2, currentY + 3);
            doc.text(data.ligne2, x + (width - textWidth2) / 2, currentY + 6);
          } else {
            // Autres colonnes normales
            const textWidth = doc.getTextWidth(data);
            doc.text(data, x + (width - textWidth) / 2, currentY + 4); // Centré verticalement
          }
          
          x += width;
        }
        
        currentY += 8; // Augmenté de 5 à 8 pour accommoder 2 lignes
      }
      
      // Calculer les totaux
      const totals = calculateTotals();
      
      // Ligne TOTAL
      if (currentY > pageHeight - 15) {
        addNewPage();
      }
      
      doc.setFillColor(191, 198, 204); // ARGENT
      doc.rect(margin, currentY, pageWidth - (margin * 2), 6, 'F');
      
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      
      const totalRowData = [
        'TOTAL',
        '',
        '',
        '', // SEGMENT vide
        '', // ÉCHÉANCE vide
        totals.totalConsommations.toFixed(1) + ' MWh', // Ajout de "MWh"
        formatAmountNumeric(totals.budgetFourniture), // Avec € et centimes
        formatAmountNumeric(totals.acheminement), // Avec € et centimes
        formatAmountNumeric(totals.cta + totals.cspe + totals.budgetCapacite + totals.budgetCee), // Avec € et centimes
        formatAmountNumeric(totals.budgetAnnuelHtva) // Avec € et centimes
      ];
      
      if (tableMode === 'TTC') {
        totalRowData.push(formatAmountNumeric(totals.tva)); // TVA
        totalRowData.push(formatAmountNumeric(totals.budgetAnnuelTTC)); // BUDGET ANNUEL TTC
      }
      
      x = margin;
      for (let k = 0; k < totalRowData.length; k++) {
        const data = totalRowData[k];
        const width = colWidths[k];
        const textWidth = doc.getTextWidth(data);
        doc.text(data, x + (width - textWidth) / 2, currentY + 4);
        x += width;
      }
      
      // Ajouter le filigrane sur toutes les pages (PAR DESSUS les données)
      console.log('Ajout du filigrane sur toutes les pages...');
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        addWatermark(); // Ajouter le filigrane sur chaque page
      }
      
      // Numéro de page finale (remplacer l'ancien système)
      console.log('Ajout des numéros de page...');
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text(`Page ${i} sur ${totalPages}`, pageWidth - margin - 15, pageHeight - 10);
      }
      
      // Télécharger
      const fileName = `mon-perimetre-${tableMode}-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      console.log('PDF généré avec succès');
      
    } catch (error) {
      console.error('Erreur PDF:', error);
      alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  if (!latestAnalysis) {
    return <EmptyState />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '40px', height: '40px', border: '3px solid #E2E6EA', borderTopColor: '#397DFF', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ fontFamily: 'var(--font-corps)', fontSize: '0.9375rem', color: '#687175' }}>Chargement des sites...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-2 py-6">
      <div style={{ marginBottom: '32px', padding: '0 16px' }}>
        <h1 style={{
          fontFamily: 'var(--font-titre)',
          fontWeight: '600',
          color: '#191D1E',
          fontSize: '1.625rem',
          marginBottom: '6px',
          display: 'flex',
          alignItems: 'center',
          letterSpacing: '-0.02em'
        }}>
          <img 
            src={`${process.env.REACT_APP_BACKEND_URL}/api/static/logos/picto-perimetre.jpg`} 
            alt="Périmètre" 
            style={{ 
              height: '2.25rem', 
              width: 'auto',
              marginRight: '12px'
            }} 
          />
          Mon périmètre
        </h1>
        <p style={{ fontFamily: 'var(--font-corps)', fontSize: '0.875rem', color: '#939BA0' }}>
          {analysisDetails?.sites?.length || 0} sites au total · Consommation: {analysisDetails?.client_info?.total_consumption?.toFixed(1) || 'N/A'} MWh
        </p>
      </div>

      {/* Barre de filtres */}
      {analysisDetails && analysisDetails.sites && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Filtres :</span>
            </div>
            
            {/* Filtre Segment */}
            <div className="flex flex-col">
              <label className="text-xs text-gray-500 mb-1">Segment</label>
              <select
                value={filters.segment}
                onChange={(e) => setFilters({...filters, segment: e.target.value})}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Tous</option>
                {getUniqueValues('segment').map(segment => (
                  <option key={segment} value={segment}>{segment}</option>
                ))}
              </select>
            </div>

            {/* Filtre Ville */}
            <div className="flex flex-col">
              <label className="text-xs text-gray-500 mb-1">Ville</label>
              <select
                value={filters.ville}
                onChange={(e) => setFilters({...filters, ville: e.target.value})}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Toutes</option>
                {getUniqueValues('ville').map(ville => (
                  <option key={ville} value={ville}>{ville}</option>
                ))}
              </select>
            </div>

            {/* Filtre Échéance */}
            <div className="flex flex-col">
              <label className="text-xs text-gray-500 mb-1">Échéance</label>
              <select
                value={filters.echeance}
                onChange={(e) => setFilters({...filters, echeance: e.target.value})}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Toutes</option>
                {getUniqueValues('echeance').map(echeance => (
                  <option key={echeance} value={echeance}>{echeance}</option>
                ))}
              </select>
            </div>

            {/* Bouton Réinitialiser */}
            <button
              onClick={resetFilters}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-md transition-colors duration-200 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Réinitialiser
            </button>

            {/* Boutons HTVA/TTC et Export */}
            <div className="ml-auto flex items-center space-x-3">
              {/* Bouton HTVA/TTC */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setTableMode('HTVA')}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 ${
                    tableMode === 'HTVA' 
                      ? 'bg-white text-blue-600 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  HTVA
                </button>
                <button
                  onClick={() => setTableMode('TTC')}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 ${
                    tableMode === 'TTC' 
                      ? 'bg-white text-blue-600 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  TTC
                </button>
              </div>

              {/* Bouton d'export */}
              <button
                onClick={exportToPDF}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 2a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V4a2 2 0 00-2-2H4zm3 2h6v4H7V4zm8 6v6h-6v-6h6zM5 10v6H4v-6h1z" clipRule="evenodd" />
                </svg>
                <span>Éditer en PDF</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sites Table */}
      {analysisDetails && analysisDetails.sites && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Container avec tableau et boutons de navigation - AVEC SCROLL VERTICAL */}
          <div className="flex">
            {/* Tableau élargi avec scroll vertical pour 10 lignes visibles */}
            <div 
              className="flex-1 overflow-x-auto overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
              id="sites-table-container"
              style={{ 
                maxWidth: 'calc(100vw - 40px)', // Marges réduites pour plus de largeur
                maxHeight: '750px' // Hauteur pour afficher environ 1 header + 10 lignes de sites + 1 ligne TOTAL sticky
              }}
            >
              <table className="w-full table-fixed" style={{ 
                minWidth: `${1100 + 
                  (showConsommationDetails ? 500 : 0) + 
                  (showBudgetDetails ? 800 : 0) + 
                  (showTaxesDetails ? 780 : 0)}px` 
              }}>
                <thead style={{ backgroundColor: '#F5F6F9', position: 'sticky', top: 0, zIndex: 20 }}>
                  <tr>
                    {/* Colonne Site - 120px */}
                    <th className="sticky left-0 px-3 py-3 text-center text-xs font-bold uppercase tracking-wider border-r-2 shadow-lg" style={{ width: '120px', minWidth: '120px', maxWidth: '120px', backgroundColor: '#F5F6F9', top: 0, zIndex: 30, color: '#191D1E', borderBottom: '2px solid #E2E6EA' }}>
                      Site
                    </th>
                    {/* Colonne PRM - 115px */}
                    <th className="sticky px-3 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '115px', minWidth: '115px', maxWidth: '115px', backgroundColor: '#F5F6F9', top: 0, zIndex: 20 }}>
                      PRM
                    </th>
                    {/* Colonne ADRESSE - 130px */}
                    <th className="sticky px-3 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '130px', minWidth: '130px', maxWidth: '130px', backgroundColor: '#F5F6F9', top: 0, zIndex: 20 }}>
                      Adresse
                    </th>
                    {/* Colonne SEGMENT - 75px */}
                    <th className="sticky px-3 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '75px', minWidth: '75px', maxWidth: '75px', backgroundColor: '#F5F6F9', top: 0, zIndex: 20 }}>
                      Segment
                    </th>
                    <th className="sticky px-6 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '115px', minWidth: '115px', maxWidth: '115px', backgroundColor: '#F5F6F9', top: 0, zIndex: 20 }}>
                      <div className="flex flex-col items-center">
                        <span>TOTAL CONSOMMATIONS</span>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowConsommationDetails(!showConsommationDetails);
                          }}
                          className="text-xs text-white hover:text-gray-200 mt-1 transition-colors cursor-pointer underline hover:no-underline whitespace-nowrap"
                        >
                          {showConsommationDetails ? '- de détails' : '+ de détails'}
                        </button>
                      </div>
                    </th>
                    {/* Colonnes de consommation conditionnelles après TOTAL CONSOMMATIONS */}
                    {showConsommationDetails && (
                      <>
                        {hasConsoPointe() && (
                          <th className="px-2 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>
                            Conso Pointe
                          </th>
                        )}
                        <th className="px-2 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>
                          Conso HPH
                        </th>
                        <th className="px-2 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>
                          Conso HCH
                        </th>
                        <th className="px-2 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>
                          Conso HPE
                        </th>
                        <th className="px-2 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>
                          Conso HCE
                        </th>
                      </>
                    )}
                    <th className="sticky px-6 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '95px', minWidth: '95px', maxWidth: '95px', backgroundColor: '#F5F6F9', top: 0, zIndex: 20 }}>
                      ECHEANCE ACTUELLE
                    </th>
                    <th className="sticky px-6 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '120px', minWidth: '120px', maxWidth: '120px', backgroundColor: '#F5F6F9', top: 0, zIndex: 20 }}>
                      <div className="flex flex-col items-center">
                        <span className="font-bold">Budget Fourniture</span>
                        <button 
                          onClick={() => setShowBudgetDetails(!showBudgetDetails)}
                          className="text-xs text-white hover:text-gray-200 mt-1 transition-colors cursor-pointer underline hover:no-underline"
                        >
                          {showBudgetDetails ? '- de détails' : '+ de détails'}
                        </button>
                      </div>
                    </th>
                    {/* Colonnes de détail Budget Fourniture conditionnelles */}
                    {showBudgetDetails && (
                      <>
                        {/* Colonnes 1-5: PRIX ELECTRON avec sous-titres - LARGEUR FIXE 105px */}
                        <th className="px-2 py-2 text-center text-xs font-bold uppercase tracking-wider border-l-2 border-blue-200" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#F5F6F9' }}>
                          <div className="flex flex-col items-center">
                            <div className="text-xs font-bold text-white mb-1">PRIX ELECTRON</div>
                            <div className="text-xs">HPH</div>
                          </div>
                        </th>
                        <th className="px-2 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#F5F6F9' }}>
                          <div className="flex flex-col items-center">
                            <div className="text-xs font-bold text-white mb-1">PRIX ELECTRON</div>
                            <div className="text-xs">HCH</div>
                          </div>
                        </th>
                        <th className="px-2 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#F5F6F9' }}>
                          <div className="flex flex-col items-center">
                            <div className="text-xs font-bold text-white mb-1">PRIX ELECTRON</div>
                            <div className="text-xs">HPE</div>
                          </div>
                        </th>
                        <th className="px-2 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#F5F6F9' }}>
                          <div className="flex flex-col items-center">
                            <div className="text-xs font-bold text-white mb-1">PRIX ELECTRON</div>
                            <div className="text-xs">HCE</div>
                          </div>
                        </th>
                        {hasPrixElectronPointe() && (
                          <th className="px-2 py-2 text-center text-xs font-bold uppercase tracking-wider border-r-2 border-blue-200" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#F5F6F9' }}>
                            <div className="flex flex-col items-center">
                              <div className="text-xs font-bold text-white mb-1">PRIX ELECTRON</div>
                              <div className="text-xs">POINTE</div>
                            </div>
                          </th>
                        )}
                        
                        {/* Colonne 6: BUDGET ELECTRON (teinte bleu) - 110px */}
                        <th className="px-2 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '110px', minWidth: '110px', maxWidth: '110px', backgroundColor: '#F5F6F9' }}>
                          <div className="text-xs">BUDGET ELECTRON</div>
                        </th>
                        
                        {/* Colonne 7: ABONNEMENT PAR AN (teinte bleu) - 110px - conditionnelle */}
                        {hasAbonnementParAn() && (
                          <th className="px-2 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '110px', minWidth: '110px', maxWidth: '110px', backgroundColor: '#F5F6F9' }}>
                            <div className="text-xs">ABONNEMENT PAR AN</div>
                          </th>
                        )}
                        
                        {/* Colonnes 8-9: PRIX OPTION VERTE et BUDGET OPTION VERTE - conditionnelles */}
                        {hasEnergyVerteData() && (
                          <>
                            <th className="px-2 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '110px', minWidth: '110px', maxWidth: '110px', backgroundColor: '#F5F6F9' }}>
                              <div className="text-xs">PRIX OPTION VERTE</div>
                            </th>
                            <th className="px-2 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '110px', minWidth: '110px', maxWidth: '110px', backgroundColor: '#F5F6F9' }}>
                              <div className="text-xs">BUDGET OPTION VERTE</div>
                            </th>
                          </>
                        )}
                      </>
                    )}
                    {/* Nouvelle colonne ACHEMINEMENT - 110px */}
                    <th className="sticky px-6 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '110px', minWidth: '110px', maxWidth: '110px', backgroundColor: '#F5F6F9', top: 0, zIndex: 20 }}>
                      <div className="flex flex-col items-center">
                        <span>ACHEMINEMENT</span>
                      </div>
                    </th>
                    {/* Nouvelle colonne TAXES ET CONTRIBUTION - 115px */}
                    <th className="sticky px-4 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '115px', minWidth: '115px', maxWidth: '115px', backgroundColor: '#F5F6F9', top: 0, zIndex: 20 }}>
                      <div className="flex flex-col items-center">
                        <span className="font-bold">TAXES ET CONTRIBUTION</span>
                        <button
                          onClick={() => setShowTaxesDetails(!showTaxesDetails)}
                          className="mt-1 text-xs text-white hover:text-gray-200 transition-colors duration-200 underline hover:no-underline"
                        >
                          {showTaxesDetails ? '- masquer' : '+ de détails'}
                        </button>
                      </div>
                    </th>
                    {/* Colonnes de détail TAXES ET CONTRIBUTION conditionnelles - DÉPLACÉES ICI */}
                    {showTaxesDetails && (
                      <>
                        {/* Colonnes 1-5: PRIX CAPACITE avec sous-titres - 100px chacun */}
                        {hasPrixCapaciteHph() && (
                          <th className="px-1 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>
                            <div className="flex flex-col items-center">
                              <div className="text-xs font-bold text-white mb-1">PRIX CAPACITE</div>
                              <div className="text-xs">HPH</div>
                            </div>
                          </th>
                        )}
                        {hasPrixCapaciteHch() && (
                          <th className="px-1 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>
                            <div className="flex flex-col items-center">
                              <div className="text-xs font-bold text-white mb-1">PRIX CAPACITE</div>
                              <div className="text-xs">HCH</div>
                            </div>
                          </th>
                        )}
                        {hasPrixCapaciteHpe() && (
                          <th className="px-1 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>
                            <div className="flex flex-col items-center">
                              <div className="text-xs font-bold text-white mb-1">PRIX CAPACITE</div>
                              <div className="text-xs">HPE</div>
                            </div>
                          </th>
                        )}
                        {hasPrixCapaciteHce() && (
                          <th className="px-1 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>
                            <div className="flex flex-col items-center">
                              <div className="text-xs font-bold text-white mb-1">PRIX CAPACITE</div>
                              <div className="text-xs">HCE</div>
                            </div>
                          </th>
                        )}
                        {hasPrixCapacitePointe() && (
                          <th className="px-1 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>
                            <div className="flex flex-col items-center">
                              <div className="text-xs font-bold text-white mb-1">PRIX CAPACITE</div>
                              <div className="text-xs">POINTE</div>
                            </div>
                          </th>
                        )}
                        
                        {/* Colonne 6: BUDGET CAPACITE (teinte bleu) - 100px */}
                        {hasBudgetCapacite() && (
                          <th className="px-1 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>
                            <div className="text-xs">BUDGET CAPACITE</div>
                          </th>
                        )}
                        
                        {/* Colonnes 7-8: CEE avec sous-titres - conditionnelles si données CEE existent - 85px chacun */}
                        {hasCeeData() && (
                          <>
                            <th className="px-1 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '85px', minWidth: '85px', maxWidth: '85px', backgroundColor: '#F5F6F9' }}>
                              <div className="flex flex-col items-center">
                                <div className="text-xs">PRIX CEE</div>
                              </div>
                            </th>
                            <th className="px-1 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '85px', minWidth: '85px', maxWidth: '85px', backgroundColor: '#F5F6F9' }}>
                              <div className="flex flex-col items-center">
                                <div className="text-xs">BUDGET CEE</div>
                              </div>
                            </th>
                          </>
                        )}
                        
                        {/* Colonnes 9-10: TAXES avec sous-titres - 100px chacun */}
                        <th className="px-1 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>
                          <div className="flex flex-col items-center">
                            <div className="text-xs">CTA</div>
                          </div>
                        </th>
                        <th className="px-1 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>
                          <div className="flex flex-col items-center">
                            <div className="text-xs">CSPE</div>
                          </div>
                        </th>
                      </>
                    )}
                    {/* Nouvelle colonne BUDGET ANNUEL HTVA - 105px */}
                    <th className="sticky px-4 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#F5F6F9', top: 0, zIndex: 20 }}>
                      <div className="flex flex-col items-center">
                        <span className="font-bold">BUDGET ANNUEL HTVA</span>
                      </div>
                    </th>
                    {/* Colonnes TTC conditionnelles */}
                    {tableMode === 'TTC' && (
                      <>
                        {/* Colonne TVA */}
                        <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>
                          <div className="flex flex-col items-center">
                            <span>TVA</span>
                          </div>
                        </th>
                        {/* Colonne BUDGET ANNUEL TTC */}
                        <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '120px', minWidth: '120px', maxWidth: '120px', backgroundColor: '#F5F6F9' }}>
                          <div className="flex flex-col items-center">
                            <span>BUDGET ANNUEL TTC</span>
                          </div>
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody style={{ backgroundColor: '#FFFFFF' }}>
                  {filteredAndSortedSites.map((site, index) => {
                    const totalConso = (site.conso_hph || 0) + (site.conso_hch || 0) + (site.conso_hpe || 0) + (site.conso_hce || 0) + (site.conso_pointe || 0);
                     
                    return (
                      <tr key={site.id} style={{ backgroundColor: '#FFFFFF', color: 'black' }}>
                        {/* Colonne Site - 120px - identique au header */}
                        <td className="sticky left-0 z-10 px-3 py-3 border-r-2 shadow-lg" style={{ width: '120px', minWidth: '120px', maxWidth: '120px', backgroundColor: '#FFFFFF', color: 'black' }}>
                          <div>
                            <div className="text-xs font-medium text-slate-800 truncate">{site.nom_site}</div>
                          </div>
                        </td>
                        {/* PRM - 115px - identique au header */}
                        <td className="px-3 py-3 whitespace-nowrap" style={{ width: '115px', minWidth: '115px', maxWidth: '115px', backgroundColor: '#FFFFFF', color: 'black' }}>
                          <div className="text-sm text-slate-800 font-medium">{site.prm}</div>
                        </td>
                        {/* ADRESSE - 130px - identique au header */}
                        <td className="px-3 py-3" style={{ width: '130px', minWidth: '130px', maxWidth: '130px', backgroundColor: '#FFFFFF', color: 'black' }}>
                          <div className="text-sm text-slate-800 font-medium truncate">{site.address}</div>
                          <div className="text-xs text-slate-600 truncate">{site.cp} {site.ville}</div>
                        </td>
                        {/* SEGMENT - 75px - identique au header */}
                        <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '75px', minWidth: '75px', maxWidth: '75px', backgroundColor: '#FFFFFF', color: 'black' }}>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            site.segment === 'C5' ? 'bg-blue-600 text-white' :
                            site.segment === 'C4' ? 'bg-amber-500 text-white' :
                            (site.segment === 'C1' || site.segment === 'C2') ? 'bg-emerald-600 text-white' :
                            'bg-slate-500 text-white'
                          }`}>
                            {site.segment}
                          </span>
                        </td>
                        {/* TOTAL CONSOMMATIONS - 115px - identique au header */}
                        <td className="px-8 py-3 whitespace-nowrap text-center" style={{ width: '115px', minWidth: '115px', maxWidth: '115px', backgroundColor: '#FFFFFF', color: 'black' }}>
                          <div className="text-sm font-medium text-black">
                            {totalConso > 0 ? `${totalConso.toFixed(3)} MWh` : '-'}
                          </div>
                        </td>
                        {/* Colonnes de consommation conditionnelles dans le corps du tableau - après TOTAL CONSOMMATIONS */}
                        {showConsommationDetails && (
                          <>
                            {hasConsoPointe() && (
                              <td className="px-2 py-4 whitespace-nowrap text-right" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                                <div className="text-sm font-medium text-black">
                                  {site.conso_pointe ? site.conso_pointe.toFixed(3) : '-'} {site.conso_pointe ? 'MWh' : ''}
                                </div>
                              </td>
                            )}
                            <td className="px-2 py-4 whitespace-nowrap text-right" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                              <div className="text-sm font-medium text-black">
                                {site.conso_hph ? site.conso_hph.toFixed(3) : '-'} {site.conso_hph ? 'MWh' : ''}
                              </div>
                            </td>
                            <td className="px-2 py-4 whitespace-nowrap text-right" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                              <div className="text-sm font-medium text-black">
                                {site.conso_hch ? site.conso_hch.toFixed(3) : '-'} {site.conso_hch ? 'MWh' : ''}
                              </div>
                            </td>
                            <td className="px-2 py-4 whitespace-nowrap text-right" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                              <div className="text-sm font-medium text-black">
                                {site.conso_hpe ? site.conso_hpe.toFixed(3) : '-'} {site.conso_hpe ? 'MWh' : ''}
                              </div>
                            </td>
                            <td className="px-2 py-4 whitespace-nowrap text-right" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                              <div className="text-sm font-medium text-black">
                                {site.conso_hce ? site.conso_hce.toFixed(3) : '-'} {site.conso_hce ? 'MWh' : ''}
                              </div>
                            </td>
                          </>
                        )}
                        {/* ECHEANCE ACTUELLE - 95px - identique au header */}
                        <td className="px-6 py-3 whitespace-nowrap text-center" style={{ width: '95px', minWidth: '95px', maxWidth: '95px', backgroundColor: '#FFFFFF', color: 'black' }}>
                          <div className="text-sm font-medium text-black">
                            {site.echeance ? (() => {
                              const date = new Date(site.echeance);
                              const day = date.getDate().toString().padStart(2, '0');
                              const month = (date.getMonth() + 1).toString().padStart(2, '0');
                              const year = date.getFullYear();
                              return `${day}/${month}/${year}`;
                            })() : 'N/A'}
                          </div>
                        </td>
                        {/* BUDGET FOURNITURE - 120px - identique au header */}
                        <td className="px-6 py-3 whitespace-nowrap text-center" style={{ width: '120px', minWidth: '120px', maxWidth: '120px', backgroundColor: '#FFFFFF', color: 'black' }}>
                          <div className="text-sm font-medium text-black">
                            {site.budget_fourniture ? (() => {
                              const amount = parseFloat(site.budget_fourniture);
                              return new Intl.NumberFormat('fr-FR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                                useGrouping: true
                              }).format(amount).replace(/\s/g, ' ') + ' €';
                            })() : 'N/A'}
                          </div>
                        </td>
                        {/* Colonnes de détail Budget Fourniture conditionnelles dans le corps du tableau */}
                        {showBudgetDetails && (
                          <>
                            {/* Colonnes 1-5: PRIX ELECTRON - LARGEUR FIXE 105px */}
                            <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#FFFFFF', color: 'black' }}>
                              <div className="text-sm font-medium text-black">
                                {site.prix_hph ? `${site.prix_hph.toFixed(2)} €/MWh` : '-'}
                              </div>
                            </td>
                            <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#FFFFFF', color: 'black' }}>
                              <div className="text-sm font-medium text-black">
                                {site.prix_hch ? `${site.prix_hch.toFixed(2)} €/MWh` : '-'}
                              </div>
                            </td>
                            <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#FFFFFF', color: 'black' }}>
                              <div className="text-sm font-medium text-black">
                                {site.prix_hpe ? `${site.prix_hpe.toFixed(2)} €/MWh` : '-'}
                              </div>
                            </td>
                            <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#FFFFFF', color: 'black' }}>
                              <div className="text-sm font-medium text-black">
                                {site.prix_hce ? `${site.prix_hce.toFixed(2)} €/MWh` : '-'}
                              </div>
                            </td>
                            {hasPrixElectronPointe() && (
                              <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#FFFFFF', color: 'black' }}>
                                <div className="text-sm font-medium text-black">
                                  {site.prix_pointe ? `${site.prix_pointe.toFixed(2)} €/MWh` : '-'}
                                </div>
                              </td>
                            )}
                            
                            {/* Colonne 6: BUDGET ELECTRON (teinte bleu) - 110px */}
                            <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '110px', minWidth: '110px', maxWidth: '110px', backgroundColor: '#FFFFFF', color: 'black' }}>
                              <div className="text-sm font-medium text-black">
                                {site.budget_electron ? (() => {
                                  const amount = parseFloat(site.budget_electron);
                                  return new Intl.NumberFormat('fr-FR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                    useGrouping: true
                                  }).format(amount).replace(/\s/g, ' ') + ' €';
                                })() : '-'}
                              </div>
                            </td>
                            
                            {/* Colonne 7: ABONNEMENT PAR AN (teinte bleu) - 110px - conditionnelle */}
                            {hasAbonnementParAn() && (
                              <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '110px', minWidth: '110px', maxWidth: '110px', backgroundColor: '#FFFFFF', color: 'black' }}>
                                <div className="text-sm font-medium text-black">
                                  {site.abonnement_par_an ? (() => {
                                    const amount = parseFloat(site.abonnement_par_an);
                                    return new Intl.NumberFormat('fr-FR', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                      useGrouping: true
                                    }).format(amount).replace(/\s/g, ' ') + ' €';
                                  })() : '-'}
                                </div>
                              </td>
                            )}
                            
                            {/* Colonnes 8-9: PRIX OPTION VERTE et BUDGET OPTION VERTE - conditionnelles */}
                            {hasEnergyVerteData() && (
                              <>
                                <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '110px', minWidth: '110px', maxWidth: '110px', backgroundColor: '#FFFFFF', color: 'black' }}>
                                  <div className="text-sm font-medium text-black">
                                    {site.prix_energie_verte ? `${site.prix_energie_verte.toFixed(2)} €/MWh` : '-'}
                                  </div>
                                </td>
                                <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '110px', minWidth: '110px', maxWidth: '110px', backgroundColor: '#FFFFFF', color: 'black' }}>
                                  <div className="text-sm font-medium text-black">
                                    {site.budget_option_verte ? (() => {
                                      const amount = parseFloat(site.budget_option_verte);
                                      return new Intl.NumberFormat('fr-FR', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                        useGrouping: true
                                      }).format(amount).replace(/\s/g, ' ') + ' €';
                                    })() : '-'}
                                  </div>
                                </td>
                              </>
                            )}
                          </>
                        )}
                        {/* Nouvelle colonne ACHEMINEMENT - 110px - identique au header */}
                        <td className="px-6 py-4 whitespace-nowrap text-center" style={{ width: '110px', minWidth: '110px', maxWidth: '110px', backgroundColor: '#FFFFFF', color: 'black' }}>
                          <div className="text-sm font-medium text-black">
                            {site.acheminement ? (() => {
                              const amount = parseFloat(site.acheminement);
                              return new Intl.NumberFormat('fr-FR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                                useGrouping: true
                              }).format(amount).replace(/\s/g, ' ') + ' €';
                            })() : '-'}
                          </div>
                        </td>
                        {/* Nouvelle colonne TAXES ET CONTRIBUTION - 115px - identique au header */}
                        <td className="px-4 py-4 whitespace-nowrap text-center" style={{ width: '115px', minWidth: '115px', maxWidth: '115px', backgroundColor: '#FFFFFF', color: 'black' }}>
                          <div className="text-sm font-medium text-black">
                            {(() => {
                              const budgetCapacite = parseFloat(site.budget_capacite) || 0;
                              const budgetCee = parseFloat(site.budget_cee) || 0;
                              const cta = parseFloat(site.cta) || 0;
                              const cspe = parseFloat(site.cspe) || 0;
                              const totalTaxes = budgetCapacite + budgetCee + cta + cspe;
                              return totalTaxes > 0 ? 
                                new Intl.NumberFormat('fr-FR', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                  useGrouping: true
                                }).format(totalTaxes).replace(/\s/g, ' ') + ' €' 
                                : '-';
                            })()}
                          </div>
                        </td>
                        {/* Colonnes de détail TAXES ET CONTRIBUTION conditionnelles - DÉPLACÉES ICI */}
                        {showTaxesDetails && (
                          <>
                            {/* Colonnes 1-5: PRIX CAPACITE - 100px chacun */}
                            {hasPrixCapaciteHph() && (
                              <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                                <div className="text-sm font-medium text-black">
                                  {site.capacite_hph ? `${site.capacite_hph.toFixed(2)} €/MWh` : '-'}
                                </div>
                              </td>
                            )}
                            {hasPrixCapaciteHch() && (
                              <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                                <div className="text-sm font-medium text-black">
                                  {site.capacite_hch ? `${site.capacite_hch.toFixed(2)} €/MWh` : '-'}
                                </div>
                              </td>
                            )}
                            {hasPrixCapaciteHpe() && (
                              <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                                <div className="text-sm font-medium text-black">
                                  {site.capacite_hpe ? `${site.capacite_hpe.toFixed(2)} €/MWh` : '-'}
                                </div>
                              </td>
                            )}
                            {hasPrixCapaciteHce() && (
                              <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                                <div className="text-sm font-medium text-black">
                                  {site.capacite_hce ? `${site.capacite_hce.toFixed(2)} €/MWh` : '-'}
                                </div>
                              </td>
                            )}
                            {hasPrixCapacitePointe() && (
                              <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                                <div className="text-sm font-medium text-black">
                                  {site.capacite_pointe ? `${site.capacite_pointe.toFixed(2)} €/MWh` : '-'}
                                </div>
                              </td>
                            )}
                            
                            {/* Colonne 6: BUDGET CAPACITE - 100px */}
                            {hasBudgetCapacite() && (
                              <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                                <div className="text-sm font-medium text-black">
                                  {site.budget_capacite ? (() => {
                                    const amount = parseFloat(site.budget_capacite);
                                    return new Intl.NumberFormat('fr-FR', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                      useGrouping: true
                                    }).format(amount).replace(/\s/g, ' ') + ' €';
                                  })() : '-'}
                                </div>
                              </td>
                            )}
                            
                            {/* Colonnes 7-8: CEE conditionnelles - 85px chacun */}
                            {hasCeeData() && (
                              <>
                                <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '85px', minWidth: '85px', maxWidth: '85px', backgroundColor: '#FFFFFF', color: 'black' }}>
                                  <div className="text-sm font-medium text-black">
                                    {site.prix_cee ? `${site.prix_cee.toFixed(2)} €/MWh` : '-'}
                                  </div>
                                </td>
                                <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '85px', minWidth: '85px', maxWidth: '85px', backgroundColor: '#FFFFFF', color: 'black' }}>
                                  <div className="text-sm font-medium text-black">
                                    {site.budget_cee ? (() => {
                                      const amount = parseFloat(site.budget_cee);
                                      return new Intl.NumberFormat('fr-FR', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                        useGrouping: true
                                      }).format(amount).replace(/\s/g, ' ') + ' €';
                                    })() : '-'}
                                  </div>
                                </td>
                              </>
                            )}
                            
                            {/* Colonnes 9-10: TAXES CTA et CSPE - 100px chacun */}
                            <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                              <div className="text-sm font-medium text-black">
                                {site.cta ? (() => {
                                  const amount = parseFloat(site.cta);
                                  return new Intl.NumberFormat('fr-FR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                    useGrouping: true
                                  }).format(amount).replace(/\s/g, ' ') + ' €';
                                })() : '-'}
                              </div>
                            </td>
                            <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                              <div className="text-sm font-medium text-black">
                                {site.cspe ? (() => {
                                  const amount = parseFloat(site.cspe);
                                  return new Intl.NumberFormat('fr-FR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                    useGrouping: true
                                  }).format(amount).replace(/\s/g, ' ') + ' €';
                                })() : '-'}
                              </div>
                            </td>
                          </>
                        )}
                        {/* Nouvelle colonne BUDGET ANNUEL HTVA - 105px - identique au header */}
                        <td className="px-6 py-3 whitespace-nowrap text-center" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#FFFFFF', color: 'black' }}>
                          <div className="text-sm font-medium text-black">
                            {site.budget_annuel_htva ? (() => {
                              const amount = parseFloat(site.budget_annuel_htva);
                              return new Intl.NumberFormat('fr-FR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                                useGrouping: true
                              }).format(amount).replace(/\s/g, ' ') + ' €';
                            })() : '-'}
                          </div>
                        </td>
                        {/* Colonnes TTC conditionnelles */}
                        {tableMode === 'TTC' && (
                          <>
                            {/* Colonne TVA */}
                            <td className="px-4 py-4 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                              <div className="text-sm font-medium text-black">
                                {site.tva ? (() => {
                                  const amount = parseFloat(site.tva);
                                  return new Intl.NumberFormat('fr-FR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                    useGrouping: true
                                  }).format(amount).replace(/\s/g, ' ') + ' €';
                                })() : '-'}
                              </div>
                            </td>
                            {/* Colonne BUDGET ANNUEL TTC */}
                            <td className="px-4 py-4 whitespace-nowrap text-center" style={{ width: '120px', minWidth: '120px', maxWidth: '120px', backgroundColor: '#FFFFFF', color: 'black' }}>
                              <div className="text-sm font-medium text-black">
                                {site.budget_annuel_ttc ? (() => {
                                  const amount = parseFloat(site.budget_annuel_ttc);
                                  return new Intl.NumberFormat('fr-FR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                    useGrouping: true
                                  }).format(amount).replace(/\s/g, ' ') + ' €';
                                })() : '-'}
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                  
                  {/* Ligne TOTAL - Sticky bottom */}
                  <tr style={{ backgroundColor: '#BFC6CC', position: 'sticky', bottom: 0, zIndex: 15 }}>
                    {/* Colonne Site - 120px - identique au header */}
                    <td className="sticky left-0 px-3 py-3 border-r-2 shadow-lg" style={{ width: '120px', minWidth: '120px', maxWidth: '120px', backgroundColor: '#BFC6CC', color: 'black', fontWeight: 'bold', textAlign: 'center', zIndex: 25 }}>
                      <div className="text-sm font-bold text-gray-900">TOTAL</div>
                    </td>
                    {/* Colonnes vides pour PRM, ADRESSE, SEGMENT - Identiques au header */}
                    <td className="px-3 py-3 whitespace-nowrap" style={{ width: '115px', minWidth: '115px', maxWidth: '115px', backgroundColor: '#BFC6CC', color: 'black', textAlign: 'center' }}></td>
                    <td className="px-3 py-3 whitespace-nowrap" style={{ width: '130px', minWidth: '130px', maxWidth: '130px', backgroundColor: '#BFC6CC', color: 'black', textAlign: 'center' }}></td>
                    <td className="px-3 py-3 whitespace-nowrap" style={{ width: '75px', minWidth: '75px', maxWidth: '75px', backgroundColor: '#BFC6CC', color: 'black', textAlign: 'center' }}></td>
                    <td className="px-8 py-3 whitespace-nowrap text-center" style={{ width: '115px', minWidth: '115px', maxWidth: '115px', backgroundColor: '#BFC6CC', color: 'black' }}>
                      <div className="text-sm font-bold text-gray-900">
                        {totals.totalConsommations.toFixed(3)} MWh
                      </div>
                    </td>
                    {/* Colonnes de consommation conditionnelles - vides pour les totaux */}
                    {showConsommationDetails && (
                      <>
                        {hasConsoPointe() && <td className="px-6 py-4"></td>}
                        <td className="px-6 py-4"></td>
                        <td className="px-6 py-4"></td>
                        <td className="px-6 py-4"></td>
                        <td className="px-6 py-4"></td>
                      </>
                    )}
                    {/* ECHEANCE ACTUELLE vide - 95px - identique au header */}
                    <td className="px-6 py-3 whitespace-nowrap text-center" style={{ width: '95px', minWidth: '95px', maxWidth: '95px', backgroundColor: '#BFC6CC', color: 'black' }}></td>
                    {/* BUDGET FOURNITURE - Total - 120px - identique au header */}
                    <td className="px-6 py-3 whitespace-nowrap text-center" style={{ width: '120px', minWidth: '120px', maxWidth: '120px', backgroundColor: '#BFC6CC', color: 'black' }}>
                      <div className="text-sm font-bold text-gray-900">
                        {new Intl.NumberFormat('fr-FR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                          useGrouping: true
                        }).format(totals.budgetFourniture).replace(/\s/g, ' ')} €
                      </div>
                    </td>
                    {/* Colonnes de détail Budget Fourniture - totaux */}
                    {showBudgetDetails && (
                      <>
                        {/* Colonnes 1-5: PRIX ELECTRON - vides pour les totaux */}
                        <td className="px-4 py-4"></td>
                        <td className="px-4 py-4"></td>
                        <td className="px-4 py-4"></td>
                        <td className="px-4 py-4"></td>
                        {hasPrixElectronPointe() && <td className="px-4 py-4"></td>}
                        
                        {/* Colonne 6: BUDGET ELECTRON */}
                        <td className="px-4 py-4 whitespace-nowrap text-center" style={{ backgroundColor: '#BFC6CC', color: 'black' }}>
                          <div className="text-sm font-bold text-gray-900">
                            {new Intl.NumberFormat('fr-FR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                              useGrouping: true
                            }).format(totals.budgetElectron).replace(/\s/g, ' ')} €
                          </div>
                        </td>
                        
                        {/* Colonne 7: ABONNEMENT PAR AN - conditionnelle */}
                        {hasAbonnementParAn() && (
                          <td className="px-4 py-4 whitespace-nowrap text-center" style={{ backgroundColor: '#BFC6CC', color: 'black' }}>
                            <div className="text-sm font-bold text-gray-900">
                              {new Intl.NumberFormat('fr-FR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                                useGrouping: true
                              }).format(totals.abonnementParAn).replace(/\s/g, ' ')} €
                            </div>
                          </td>
                        )}
                        
                        {/* Colonnes 8-9: PRIX OPTION VERTE et BUDGET OPTION VERTE - conditionnelles */}
                        {hasEnergyVerteData() && (
                          <>
                            <td className="px-2 py-3" style={{ width: '110px', minWidth: '110px', maxWidth: '110px' }}></td>
                            <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '110px', minWidth: '110px', maxWidth: '110px', backgroundColor: '#BFC6CC', color: 'black' }}>
                              <div className="text-sm font-bold text-gray-900">
                                {new Intl.NumberFormat('fr-FR', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                  useGrouping: true
                                }).format(totals.budgetOptionVerte).replace(/\s/g, ' ')} €
                              </div>
                            </td>
                          </>
                        )}
                      </>
                    )}
                    {/* Nouvelle colonne ACHEMINEMENT dans la ligne TOTAL - 110px - identique au header */}
                    <td className="px-6 py-4 whitespace-nowrap text-center" style={{ width: '110px', minWidth: '110px', maxWidth: '110px', backgroundColor: '#BFC6CC', color: 'black' }}>
                      <div className="text-sm font-bold text-gray-900">
                        {totals.acheminement > 0 ? 
                          new Intl.NumberFormat('fr-FR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                            useGrouping: true
                          }).format(totals.acheminement).replace(/\s/g, ' ') + ' €' 
                          : '-'
                        }
                      </div>
                    </td>
                    {/* Nouvelle colonne TAXES ET CONTRIBUTION dans la ligne TOTAL - 115px - identique au header */}
                    <td className="px-4 py-4 whitespace-nowrap text-center" style={{ width: '115px', minWidth: '115px', maxWidth: '115px', backgroundColor: '#BFC6CC', color: 'black' }}>
                      <div className="text-sm font-bold text-gray-800">
                        {(() => {
                          const totalTaxesContributions = 
                            (totals.budgetCapacite || 0) + 
                            (totals.budgetCee || 0) + 
                            (totals.cta || 0) + 
                            (totals.cspe || 0);
                          return totalTaxesContributions > 0 ? 
                            new Intl.NumberFormat('fr-FR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                              useGrouping: true
                            }).format(totalTaxesContributions).replace(/\s/g, ' ') + ' €' 
                            : '-';
                        })()}
                      </div>
                    </td>
                    {/* Colonnes de détail TAXES ET CONTRIBUTION - totaux - DÉPLACÉES ICI */}
                    {showTaxesDetails && (
                      <>
                        {/* Colonnes 1-5: PRIX CAPACITE totaux */}
                        {hasPrixCapaciteHph() && (
                          <td className="px-3 py-3 whitespace-nowrap text-center" style={{ backgroundColor: '#BFC6CC', color: 'black' }}>
                            <div className="text-sm font-bold text-slate-800">-</div>
                          </td>
                        )}
                        {hasPrixCapaciteHch() && (
                          <td className="px-3 py-3 whitespace-nowrap text-center" style={{ backgroundColor: '#BFC6CC', color: 'black' }}>
                            <div className="text-sm font-bold text-slate-800">-</div>
                          </td>
                        )}
                        {hasPrixCapaciteHpe() && (
                          <td className="px-3 py-3 whitespace-nowrap text-center" style={{ backgroundColor: '#BFC6CC', color: 'black' }}>
                            <div className="text-sm font-bold text-slate-800">-</div>
                          </td>
                        )}
                        {hasPrixCapaciteHce() && (
                          <td className="px-3 py-3 whitespace-nowrap text-center" style={{ backgroundColor: '#BFC6CC', color: 'black' }}>
                            <div className="text-sm font-bold text-slate-800">-</div>
                          </td>
                        )}
                        {hasPrixCapacitePointe() && (
                          <td className="px-3 py-3 whitespace-nowrap text-center" style={{ backgroundColor: '#BFC6CC', color: 'black' }}>
                            <div className="text-sm font-bold text-slate-800">-</div>
                          </td>
                        )}
                        
                        {/* Colonne 6: BUDGET CAPACITE total */}
                        {hasBudgetCapacite() && (
                          <td className="px-3 py-3 whitespace-nowrap text-center" style={{ backgroundColor: '#BFC6CC', color: 'black' }}>
                            <div className="text-sm font-bold text-slate-800">
                              {totals.budgetCapacite > 0 ? 
                                new Intl.NumberFormat('fr-FR', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                  useGrouping: true
                                }).format(totals.budgetCapacite).replace(/\s/g, ' ') + ' €' 
                                : '-'
                              }
                            </div>
                          </td>
                        )}
                        
                        {/* Colonnes 7-8: CEE totaux conditionnels */}
                        {hasCeeData() && (
                          <>
                            <td className="px-3 py-3 whitespace-nowrap text-center" style={{ backgroundColor: '#BFC6CC', color: 'black' }}>
                              <div className="text-sm font-bold text-slate-800">-</div>
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap text-center" style={{ backgroundColor: '#BFC6CC', color: 'black' }}>
                              <div className="text-sm font-bold text-slate-800">
                                {totals.budgetCee > 0 ? 
                                  new Intl.NumberFormat('fr-FR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                    useGrouping: true
                                  }).format(totals.budgetCee).replace(/\s/g, ' ') + ' €' 
                                  : '-'
                                }
                              </div>
                            </td>
                          </>
                        )}
                        
                        {/* Colonnes 9-10: TAXES CTA et CSPE totaux */}
                        <td className="px-3 py-3 whitespace-nowrap text-center" style={{ backgroundColor: '#BFC6CC', color: 'black' }}>
                          <div className="text-sm font-bold text-slate-800">
                            {totals.cta > 0 ? 
                              new Intl.NumberFormat('fr-FR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                                useGrouping: true
                              }).format(totals.cta).replace(/\s/g, ' ') + ' €' 
                              : '-'
                            }
                          </div>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-center" style={{ backgroundColor: '#BFC6CC', color: 'black' }}>
                          <div className="text-sm font-bold text-slate-800">
                            {totals.cspe > 0 ? 
                              new Intl.NumberFormat('fr-FR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                                useGrouping: true
                              }).format(totals.cspe).replace(/\s/g, ' ') + ' €' 
                              : '-'
                            }
                          </div>
                        </td>
                      </>
                    )}
                    {/* Nouvelle colonne BUDGET ANNUEL HTVA dans la ligne TOTAL - 105px - identique au header */}
                    <td className="px-6 py-3 whitespace-nowrap text-center" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#BFC6CC', color: 'black' }}>
                      <div className="text-sm font-bold text-gray-800">
                        {totals.budgetAnnuelHtva > 0 ? 
                          new Intl.NumberFormat('fr-FR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                            useGrouping: true
                          }).format(totals.budgetAnnuelHtva).replace(/\s/g, ' ') + ' €' 
                          : '-'
                        }
                      </div>
                    </td>
                    {/* Colonnes TTC conditionnelles dans la ligne TOTAL */}
                    {tableMode === 'TTC' && (
                      <>
                        {/* Colonne TVA TOTAL */}
                        <td className="px-4 py-4 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#BFC6CC', color: 'black' }}>
                          <div className="text-sm font-bold">
                            {totals.tva > 0 ? 
                              new Intl.NumberFormat('fr-FR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                                useGrouping: true
                              }).format(totals.tva).replace(/\s/g, ' ') + ' €' 
                              : '-'
                            }
                          </div>
                        </td>
                        {/* Colonne BUDGET ANNUEL TTC TOTAL */}
                        <td className="px-4 py-4 whitespace-nowrap text-center" style={{ width: '120px', minWidth: '120px', maxWidth: '120px', backgroundColor: '#BFC6CC', color: 'black' }}>
                          <div className="text-sm font-bold">
                            {totals.budgetAnnuelTTC > 0 ? 
                              new Intl.NumberFormat('fr-FR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                                useGrouping: true
                              }).format(totals.budgetAnnuelTTC).replace(/\s/g, ' ') + ' €' 
                              : '-'
                            }
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
            
          </div>
        </div>
      )}
    </div>
  );
};

// Import Component (Upload Interface)
const ImportTab = ({ onUploadSuccess, onDeleteAnalysis, analyses }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ percentage: 0, message: '' });
  const [tempAnalysisId, setTempAnalysisId] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [useLocalProgress, setUseLocalProgress] = useState(false); // Skip server polling when using chunked upload

  // Polling pour récupérer la progression (only for non-chunked uploads)
  useEffect(() => {
    // Skip polling if using local progress (chunked upload manages its own progress)
    if (!tempAnalysisId || !uploading || useLocalProgress) return;

    const interval = setInterval(async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${API}/upload-progress/${tempAnalysisId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.data) {
          setUploadProgress({
            percentage: response.data.percentage || 0,
            message: response.data.message || ''
          });

          // Arrêter le polling si terminé
          if (response.data.percentage >= 100) {
            clearInterval(interval);
          }
        }
      } catch (error) {
        console.error('Error fetching progress:', error);
      }
    }, 500); // Interroger toutes les 500ms

    return () => clearInterval(interval);
  }, [tempAnalysisId, uploading, useLocalProgress]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.endsWith('.xlsx') || droppedFile.name.endsWith('.xlsm')) {
        setFile(droppedFile);
      } else {
        alert('Veuillez sélectionner un fichier Excel (.xlsx ou .xlsm)');
      }
    }
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.xlsm')) {
        setFile(selectedFile);
      } else {
        alert('Veuillez sélectionner un fichier Excel (.xlsx ou .xlsm)');
      }
    }
  };

  // Chunked upload function to handle large files
  const uploadFileInChunks = async (file, token) => {
    // Use 256KB chunks - balances between fewer requests and avoiding size limits
    const CHUNK_SIZE = 256 * 1024; // 256 KB chunks
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const MAX_RETRIES = 5;
    const BATCH_SIZE = 15; // Smaller batches
    const BATCH_PAUSE_MS = 2000; // 2 second pause between batches
    
    console.log(`Starting chunked upload: ${file.name}, ${(file.size / 1024 / 1024).toFixed(2)} MB, ${totalChunks} chunks`);
    
    // Step 1: Initialize upload
    setUploadProgress({ percentage: 2, message: 'Initialisation...' });
    
    let initResponse;
    for (let initRetry = 0; initRetry < 3; initRetry++) {
      try {
        initResponse = await axios.post(`${API}/upload/init`, {
          filename: file.name,
          total_size: file.size,
          total_chunks: totalChunks
        }, {
          headers: { 'Authorization': `Bearer ${token}` },
          timeout: 30000
        });
        break;
      } catch (initError) {
        console.error(`Init error (attempt ${initRetry + 1}):`, initError);
        if (initRetry === 2) throw new Error(`Erreur d'initialisation: ${initError.message}`);
        await new Promise(r => setTimeout(r, 2000));
      }
    }
    
    const uploadId = initResponse.data.upload_id;
    console.log(`Upload initialized: ${uploadId}`);
    
    // Step 2: Upload chunks with batch pauses
    let lastDisplayedProgress = 2;
    let consecutiveFailures = 0;
    
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      
      let success = false;
      let lastError = null;
      
      for (let retry = 0; retry < MAX_RETRIES && !success; retry++) {
        try {
          const chunkFormData = new FormData();
          chunkFormData.append('file', chunk);
          
          await axios.post(`${API}/upload/chunk/${uploadId}/${i}`, chunkFormData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${token}`
            },
            timeout: 60000
          });
          
          success = true;
          consecutiveFailures = 0;
        } catch (chunkError) {
          lastError = chunkError;
          consecutiveFailures++;
          console.warn(`Chunk ${i} attempt ${retry + 1} failed:`, chunkError.message);
          
          if (consecutiveFailures > 10) {
            throw new Error('Connexion instable. Veuillez réessayer plus tard.');
          }
          
          if (retry < MAX_RETRIES - 1) {
            // Wait longer on retries: 3s, 6s, 9s, 12s
            const waitTime = 3000 * (retry + 1);
            console.log(`Waiting ${waitTime}ms before retry...`);
            await new Promise(r => setTimeout(r, waitTime));
          }
        }
      }
      
      if (!success) {
        try {
          await axios.delete(`${API}/upload/cancel/${uploadId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
        } catch (e) { /* ignore */ }
        throw new Error(`Échec de l'envoi du bloc ${i + 1}/${totalChunks}: ${lastError?.message || 'Erreur réseau'}`);
      }
      
      // Batch pause every BATCH_SIZE chunks to reset connection
      if ((i + 1) % BATCH_SIZE === 0 && i < totalChunks - 1) {
        const batchNum = Math.floor((i + 1) / BATCH_SIZE);
        console.log(`Batch ${batchNum} complete, pausing ${BATCH_PAUSE_MS}ms...`);
        setUploadProgress({ 
          percentage: Math.round(5 + ((i + 1) / totalChunks * 55)), 
          message: `Pause... ${Math.round(((i + 1) / totalChunks) * 100)}%`
        });
        await new Promise(r => setTimeout(r, BATCH_PAUSE_MS));
      }
      
      // Update progress
      const newProgress = Math.round(5 + ((i + 1) / totalChunks * 55));
      if (newProgress > lastDisplayedProgress) {
        lastDisplayedProgress = newProgress;
        setUploadProgress({ 
          percentage: newProgress, 
          message: `Envoi en cours... ${Math.round(((i + 1) / totalChunks) * 100)}%`
        });
      }
    }
    
    // Step 3: Complete upload and process with progress polling
    setUploadProgress({ percentage: 60, message: 'Traitement du fichier Excel...' });
    console.log('All chunks uploaded, completing...');
    
    // Start polling for processing progress
    let processingComplete = false;
    const pollInterval = setInterval(async () => {
      if (processingComplete) return;
      try {
        const progressResponse = await axios.get(`${API}/upload-progress/${uploadId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (progressResponse.data) {
          const serverProgress = progressResponse.data.percentage || 0;
          const serverMessage = progressResponse.data.message || 'Traitement...';
          // Map server progress (0-100) to our range (60-95)
          const mappedProgress = 60 + (serverProgress * 0.35);
          setUploadProgress({
            percentage: Math.round(mappedProgress),
            message: serverMessage
          });
        }
      } catch (e) {
        // Ignore polling errors
      }
    }, 500);
    
    try {
      const completeResponse = await axios.post(`${API}/upload/complete/${uploadId}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` },
        timeout: 600000 // 10 minutes for processing
      });
      
      processingComplete = true;
      clearInterval(pollInterval);
      setUploadProgress({ percentage: 95, message: 'Finalisation...' });
      return completeResponse.data;
    } catch (completeError) {
      processingComplete = true;
      clearInterval(pollInterval);
      console.error('Complete error:', completeError);
      throw new Error(`Erreur lors du traitement: ${completeError.response?.data?.detail || completeError.message}`);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    // Générer un ID temporaire côté frontend
    const newTempAnalysisId = 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    setTempAnalysisId(newTempAnalysisId);
    setUploading(true);
    setUploadProgress({ percentage: 0, message: 'Initialisation...' });

    try {
      const token = localStorage.getItem('access_token');
      
      // Use chunked upload for files larger than 500KB, or always use it in production
      const useChunkedUpload = file.size > 500 * 1024 || window.location.hostname !== 'localhost';
      
      // Disable server polling for chunked uploads (progress is managed locally)
      setUseLocalProgress(useChunkedUpload);
      
      let responseData;
      
      if (useChunkedUpload) {
        // Use chunked upload (recommended for production)
        responseData = await uploadFileInChunks(file, token);
      } else {
        // Use simple upload for small files in development
        const formData = new FormData();
        formData.append('file', file);
        formData.append('temp_analysis_id', newTempAnalysisId);
        
        const response = await axios.post(`${API}/upload-analysis`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`,
          },
        });
        responseData = response.data;
      }

      if (responseData) {
        onUploadSuccess(responseData);
        setFile(null);
        setUploadProgress({ percentage: 100, message: 'Terminé!' });
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Erreur lors de l\'upload du fichier: ' + (error.response?.data?.detail || error.message));
      setUploadProgress({ percentage: 0, message: '' });
    } finally {
      setUploading(false);
      setUseLocalProgress(false); // Reset for next upload
      setTimeout(() => setTempAnalysisId(null), 1000); // Nettoyer après 1 seconde
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-8">
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{
          fontFamily: 'var(--font-titre)',
          fontWeight: '600',
          color: '#191D1E',
          fontSize: '1.5rem',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          letterSpacing: '-0.02em'
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#397DFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '12px' }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 12 15 15"/></svg>
          Import d'Étude Multisite
        </h2>
        <p style={{ fontFamily: 'var(--font-corps)', color: '#687175', fontSize: '0.875rem' }}>
          Importez votre fichier Excel d'étude multisite pour commencer l'analyse
        </p>
      </div>

      {/* Upload Zone */}
      <div
        style={{
          position: 'relative',
          border: dragActive ? '2px dashed #397DFF' : file ? '2px dashed #10D183' : '2px dashed #BFC6CC',
          borderRadius: '14px',
          padding: '64px 32px',
          textAlign: 'center',
          transition: 'all 200ms ease',
          background: dragActive ? 'rgba(57,125,255,0.04)' : file ? '#ECFDF5' : '#F5F6F9',
          cursor: 'pointer'
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="space-y-6">
          <div style={{
            width: '72px',
            height: '72px',
            background: file ? 'rgba(16, 209, 131, 0.1)' : 'rgba(57, 125, 255, 0.08)',
            borderRadius: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto'
          }}>
            {file ? (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10D183" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            ) : (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#397DFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            )}
          </div>
          
          {file ? (
            <div className="space-y-2">
              <p style={{ fontFamily: 'var(--font-titre)', fontWeight: '600', fontSize: '1.125rem', color: '#191D1E' }}>
                {file.name}
              </p>
              <p style={{ fontFamily: 'var(--font-corps)', fontSize: '0.8125rem', color: '#687175' }}>
                Taille: {(file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p style={{ fontFamily: 'var(--font-titre)', fontWeight: '500', fontSize: '1.125rem', color: '#191D1E' }}>
                Glissez-déposez votre fichier Excel ici
              </p>
              <p style={{ fontFamily: 'var(--font-corps)', fontSize: '0.875rem', color: '#939BA0' }}>
                ou cliquez pour sélectionner un fichier (.xlsx, .xlsm)
              </p>
            </div>
          )}

          <input
            type="file"
            onChange={handleFileSelect}
            accept=".xlsx,.xlsm"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>
      </div>

      {/* Upload Button */}
      {file && (
        <div style={{ textAlign: 'center', marginTop: '32px' }}>
          <button
            onClick={handleUpload}
            disabled={uploading}
            style={{
              padding: '13px 32px',
              borderRadius: '10px',
              fontFamily: 'var(--font-corps)',
              fontWeight: '500',
              fontSize: '0.9375rem',
              color: '#FFFFFF',
              border: 'none',
              cursor: uploading ? 'not-allowed' : 'pointer',
              transition: 'all 200ms ease',
              background: uploading ? '#BFC6CC' : '#397DFF',
              boxShadow: uploading ? 'none' : '0 2px 12px rgba(57,125,255,0.25)'
            }}
            onMouseEnter={(e) => { if (!uploading) { e.target.style.background = '#2B6AE6'; e.target.style.boxShadow = '0 4px 20px rgba(57,125,255,0.35)'; }}}
            onMouseLeave={(e) => { if (!uploading) { e.target.style.background = '#397DFF'; e.target.style.boxShadow = '0 2px 12px rgba(57,125,255,0.25)'; }}}
          >
            {uploading ? (
              <span>
                Chargement... {uploadProgress.percentage}%
                {uploadProgress.message && (
                  <span style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '400', marginTop: '4px' }}>{uploadProgress.message}</span>
                )}
              </span>
            ) : (
              "Lancer l'analyse"
            )}
          </button>
          
          {/* Barre de progression */}
          {uploading && (
            <div style={{ marginTop: '16px', maxWidth: '320px', marginInline: 'auto' }}>
              <div style={{ width: '100%', background: '#E2E6EA', borderRadius: '8px', height: '6px', overflow: 'hidden' }}>
                <div
                  style={{ 
                    width: `${uploadProgress.percentage}%`, 
                    height: '6px', 
                    borderRadius: '8px',
                    background: '#397DFF',
                    transition: 'width 300ms ease-out' 
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Analyses existantes */}
      {analyses && analyses.length > 0 && (
        <div style={{ marginTop: '48px', background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E2E6EA', padding: '32px', boxShadow: '0 1px 3px rgba(25, 29, 30, 0.05)' }}>
          <h3 style={{
            fontFamily: 'var(--font-titre)',
            fontWeight: '600',
            fontSize: '1.125rem',
            color: '#191D1E',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            letterSpacing: '-0.01em'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#397DFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '10px' }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
            Analyses existantes ({analyses.length})
          </h3>
          
          <div className="space-y-3">
            {analyses.map((analysis) => (
              <div 
                key={analysis.analysis_id} 
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px 20px',
                  border: '1px solid #E2E6EA',
                  borderRadius: '10px',
                  transition: 'background 200ms ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#F5F6F9'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#FFFFFF'}
              >
                <div style={{ flex: 1 }}>
                  <h4 style={{ fontFamily: 'var(--font-titre)', fontWeight: '500', color: '#191D1E', fontSize: '0.9375rem' }}>
                    {analysis.client_info?.client_name || 'Sans nom'}
                  </h4>
                  <p style={{ fontFamily: 'var(--font-corps)', fontSize: '0.8125rem', color: '#687175', marginTop: '4px' }}>
                    {analysis.total_sites} sites · {analysis.suppliers_count} fournisseurs
                  </p>
                  <p style={{ fontFamily: 'var(--font-corps)', fontSize: '0.6875rem', color: '#939BA0', marginTop: '2px' }}>
                    ID: {analysis.analysis_id}
                  </p>
                </div>
                
                <button
                  onClick={() => onDeleteAnalysis(analysis.analysis_id)}
                  style={{
                    padding: '8px 16px',
                    background: '#FFFFFF',
                    border: '1px solid #EF4444',
                    borderRadius: '8px',
                    color: '#EF4444',
                    fontFamily: 'var(--font-corps)',
                    fontWeight: '500',
                    fontSize: '0.8125rem',
                    cursor: 'pointer',
                    transition: 'all 200ms ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#FEF2F2'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#FFFFFF'; }}
                  title="Supprimer cette analyse"
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Supprimer
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* File Format Info */}
      <div style={{ marginTop: '48px', background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E2E6EA', padding: '32px', boxShadow: '0 1px 3px rgba(25, 29, 30, 0.05)' }}>
        <h3 style={{
          fontFamily: 'var(--font-titre)',
          fontWeight: '600',
          fontSize: '1.125rem',
          color: '#191D1E',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          letterSpacing: '-0.01em'
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#397DFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '10px' }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          Format de fichier attendu
        </h3>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="space-y-4">
            <h4 style={{ fontFamily: 'var(--font-titre)', fontWeight: '500', fontSize: '0.9375rem', color: '#191D1E' }}>
              Onglets requis
            </h4>
            <ul style={{ fontFamily: 'var(--font-corps)', fontSize: '0.8125rem', color: '#687175', paddingLeft: '16px' }} className="space-y-2">
              <li><strong style={{ color: '#3C4649' }}>2.PERIMETRE FOUR ELEC</strong> - Détails des sites (PRM, adresses, consommations)</li>
              <li><strong style={{ color: '#3C4649' }}>CLASSEMENT</strong> - Classement des fournisseurs</li>
              <li><strong style={{ color: '#3C4649' }}>Onglets fournisseurs</strong> - MINT, HELLIO, TOTAL, ENGIE, etc.</li>
            </ul>
          </div>

          <div className="space-y-4">
            <h4 style={{ fontFamily: 'var(--font-titre)', fontWeight: '500', fontSize: '0.9375rem', color: '#191D1E' }}>
              Données extraites
            </h4>
            <ul style={{ fontFamily: 'var(--font-corps)', fontSize: '0.8125rem', color: '#687175', paddingLeft: '16px' }} className="space-y-2">
              <li>Informations client et commercial</li>
              <li>Liste complète des sites avec consommations</li>
              <li>Offres et tarifs de tous les fournisseurs</li>
              <li>Classement et recommandations</li>
              <li>Calculs d'économies potentielles</li>
            </ul>
          </div>

          <div className="space-y-4">
            <h4 style={{ fontFamily: 'var(--font-titre)', fontWeight: '500', fontSize: '0.9375rem', color: '#191D1E' }}>
              Sécurité
            </h4>
            <ul style={{ fontFamily: 'var(--font-corps)', fontSize: '0.8125rem', color: '#687175', paddingLeft: '16px' }} className="space-y-2">
              <li>Vos données sont traitées de manière sécurisée</li>
              <li>Stockage temporaire pour la durée de l'analyse</li>
              <li>Aucun partage avec des tiers</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant pour afficher les détails d'une offre
const OfferDetailTab = ({ latestAnalysis, supplierName, onBack }) => {
  // Extraire le nom du fournisseur et la durée du supplierName (format: "nom|||durée")
  const [actualSupplierName, duration] = supplierName?.includes('|||') 
    ? supplierName.split('|||') 
    : [supplierName, null];
  
  // États pour l'affichage des détails (identiques à Mon périmètre)
  const [showConsommationDetails, setShowConsommationDetails] = useState(false);
  const [showBudgetDetails, setShowBudgetDetails] = useState(false);
  const [showTaxesDetails, setShowTaxesDetails] = useState(false);
  const [tableMode, setTableMode] = useState('HTVA');
  const [analysisDetails, setAnalysisDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // États pour les données spécifiques du fournisseur
  const [supplierDetails, setSupplierDetails] = useState(null);
  const [supplierLogoUrl, setSupplierLogoUrl] = useState(null);
  
  // États pour les filtres (identiques à Mon périmètre)
  const [filters, setFilters] = useState({
    segment: '',
    ville: '',
    echeance: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });

  // Fonctions utilitaires identiques à Mon périmètre
  const getUniqueValues = (field) => {
    if (!analysisDetails?.sites) return [];
    return [...new Set(analysisDetails.sites.map(site => site[field]).filter(Boolean))].sort();
  };

  const resetFilters = () => {
    setFilters({ segment: '', ville: '', echeance: '' });
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filtrage et tri des sites (identique à Mon périmètre)
  const filteredAndSortedSites = analysisDetails?.sites?.filter(site => {
    return (
      (!filters.segment || site.segment === filters.segment) &&
      (!filters.ville || site.ville === filters.ville) &&
      (!filters.echeance || site.echeance === filters.echeance)
    );
  }).sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    
    if (sortConfig.direction === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  }) || [];

  // Fonction pour obtenir les données spécifiques d'un site pour ce fournisseur
  const getSupplierDataForSite = (site) => {
    if (!supplierDetails?.details || !site?.id) {
      return null;
    }
    return supplierDetails.details[site.id];
  };

  // Fonction pour vérifier si les données d'abonnement/option verte existent
  const hasAbonnementOrOptionVerteData = () => {
    if (!supplierDetails?.details) return false;
    
    return Object.values(supplierDetails.details).some(siteData => {
      const bf = siteData?.budget_fourniture;
      return (bf?.abonnement_par_an && bf.abonnement_par_an !== 0) ||
             (bf?.prix_option_verte && bf.prix_option_verte !== 0) ||
             (bf?.budget_option_verte && bf.budget_option_verte !== 0);
    });
  };

  // Fonction pour vérifier si une colonne de prix a des données
  const hasColumnData = (columnKey) => {
    if (!supplierDetails?.details) return false;
    
    return Object.values(supplierDetails.details).some(siteData => {
      const value = siteData?.[columnKey];
      return value && value > 0;
    });
  };

  // Fonctions pour vérifier chaque colonne spécifique
  const hasPrixElectronPointe = () => {
    if (!supplierDetails?.details) return false;
    return Object.values(supplierDetails.details).some(siteData => {
      const prix = siteData?.prix_electron_pointe;
      return prix && prix > 0;
    });
  };
  
  const hasPrixOptionVerte = () => {
    if (!supplierDetails?.details) return false;
    return Object.values(supplierDetails.details).some(siteData => {
      const bf = siteData?.budget_fourniture;
      return (bf?.prix_option_verte && bf.prix_option_verte !== 0) || (bf?.budget_option_verte && bf.budget_option_verte !== 0);
    });
  };
  
  const hasAbonnementParAn = () => {
    if (!supplierDetails?.details) return false;
    return Object.values(supplierDetails.details).some(siteData => {
      const bf = siteData?.budget_fourniture;
      return bf?.abonnement_par_an && bf.abonnement_par_an !== 0;
    });
  };
  
  const hasPrixCapaciteHph = () => {
    if (!supplierDetails?.details) return false;
    return Object.values(supplierDetails.details).some(siteData => {
      const prix = siteData?.taxes_contribution?.prix_capacite_hph;
      return prix && prix > 0;
    });
  };
  
  const hasPrixCapaciteHch = () => {
    if (!supplierDetails?.details) return false;
    return Object.values(supplierDetails.details).some(siteData => {
      const prix = siteData?.taxes_contribution?.prix_capacite_hch;
      return prix && prix > 0;
    });
  };
  
  const hasPrixCapaciteHpe = () => {
    if (!supplierDetails?.details) return false;
    return Object.values(supplierDetails.details).some(siteData => {
      const prix = siteData?.taxes_contribution?.prix_capacite_hpe;
      return prix && prix > 0;
    });
  };
  
  const hasPrixCapaciteHce = () => {
    if (!supplierDetails?.details) return false;
    return Object.values(supplierDetails.details).some(siteData => {
      const prix = siteData?.taxes_contribution?.prix_capacite_hce;
      return prix && prix > 0;
    });
  };
  
  const hasPrixCapacitePointe = () => {
    if (!supplierDetails?.details) return false;
    return Object.values(supplierDetails.details).some(siteData => {
      const prix = siteData?.taxes_contribution?.prix_capacite_pointe;
      return prix && prix > 0;
    });
  };
  
  const hasPrixCee = () => {
    if (!supplierDetails?.details) return false;
    return Object.values(supplierDetails.details).some(siteData => {
      const tc = siteData?.taxes_contribution;
      return (tc?.prix_cee && tc.prix_cee > 0) || (tc?.budget_cee && tc.budget_cee > 0);
    });
  };

  const hasConsoPointe = () => {
    if (!supplierDetails?.details) return false;
    return Object.values(supplierDetails.details).some(siteData => {
      const conso = siteData?.conso_pointe;
      return conso && conso > 0;
    });
  };

  // Fonction d'export PDF pour les détails d'offre
  const exportToPDF = async () => {
    try {
      console.log('Début génération PDF détail offre...');
      
      if (!filteredAndSortedSites || filteredAndSortedSites.length === 0) {
        alert('Aucune donnée à exporter');
        return;
      }
      
      // Créer un nouveau document PDF en format paysage A4
      const doc = new jsPDF('l', 'mm', 'a4');
      
      // Configuration (avec espaces optimisés)
      const pageWidth = doc.internal.pageSize.getWidth(); // 297mm
      const pageHeight = doc.internal.pageSize.getHeight(); // 210mm
      const margin = 8;
      let currentY = 22; // Réduit de 25 à 22 pour remonter le titre
      let pageNumber = 1;
      
      // Charger le logo Place des Energies depuis le backend
      let logoData = null;
      try {
        // Essayer d'abord en JPG, puis en PNG
        let logoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/static/logos/place-des-energies.jpg`;
        let response = await fetch(logoUrl);
        
        if (!response.ok) {
          logoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/static/logos/place-des-energies.png`;
          response = await fetch(logoUrl);
        }
        
        if (response.ok) {
          const blob = await response.blob();
          logoData = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
          console.log('Logo chargé avec succès');
        }
      } catch (error) {
        console.log('Erreur lors du chargement du logo:', error);
      }
      
      // Charger le logo du fournisseur depuis le backend pour OfferDetailTab
      let supplierLogoData = null;
      try {
        // Utiliser actualSupplierName (sans la durée) pour le nom du fichier logo
        const supplierLogoName = actualSupplierName.toLowerCase().replace(/\s+/g, '-');
        let logoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/static/logos/${supplierLogoName}.jpg`;
        console.log('Tentative de chargement du logo fournisseur:', logoUrl);
        let response = await fetch(logoUrl);
        
        if (!response.ok) {
          logoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/static/logos/${supplierLogoName}.png`;
          console.log('Tentative PNG:', logoUrl);
          response = await fetch(logoUrl);
        }
        
        if (response.ok) {
          const blob = await response.blob();
          supplierLogoData = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
          console.log('✅ Logo fournisseur chargé avec succès pour le PDF');
        } else {
          console.log('⚠️ Logo fournisseur non trouvé:', supplierLogoName);
        }
      } catch (error) {
        console.log('❌ Erreur lors du chargement du logo fournisseur:', error);
      }
      
 
      
      // Fonction pour ajouter le logo Place des Energies en haut à droite
      const addLogo = () => {
        if (!logoData) {
          console.log('Pas de logo PDE à ajouter');
          return;
        }
        
        const logoWidth = 40; // Largeur du logo en mm
        const logoHeight = 12; // Hauteur du logo en mm
        const logoX = pageWidth - margin - logoWidth; // Position X (à droite)
        const logoY = 5; // Position Y (en haut)
        
        try {
          doc.addImage(logoData, 'JPEG', logoX, logoY, logoWidth, logoHeight);
          console.log('Logo PDE ajouté à la page');
        } catch (error) {
          console.log('Erreur lors de l\'ajout du logo:', error);
        }
      };
      
      // Fonction pour ajouter le logo du fournisseur au centre (augmenté x1.5)
      const addSupplierLogo = (posY = 5) => {
        if (!supplierLogoData) {
          console.log('Pas de logo fournisseur à ajouter');
          return 0; // Retourne 0 si pas de logo
        }
        
        const logoMaxWidth = 30; // Augmenté x1.5 (20 → 30mm)
        const logoEstimatedHeight = 15; // Hauteur estimée généreuse pour éviter chevauchement
        const logoX = (pageWidth - logoMaxWidth) / 2; // Position X (centré)
        const logoY = posY; // Position Y passée en paramètre
        
        try {
          // Déterminer le format (JPEG ou PNG)
          const format = supplierLogoData.includes('data:image/png') ? 'PNG' : 'JPEG';
          // Utiliser seulement la largeur, jsPDF calculera la hauteur automatiquement pour garder le ratio
          doc.addImage(supplierLogoData, format, logoX, logoY, logoMaxWidth, 0);
          console.log(`Logo fournisseur ajouté à la page (30mm largeur, hauteur auto) à Y=${posY}`);
          return logoEstimatedHeight; // Retourne une estimation généreuse de la hauteur
        } catch (error) {
          console.log('Erreur lors de l\'ajout du logo fournisseur:', error);
          return 0;
        }
      };
      
      // Fonction pour ajouter le logo Place des Energies en filigrane (fond de tableau avec transparence)
      const addWatermark = () => {
        if (!logoData) {
          console.log('Pas de logo pour le filigrane');
          return;
        }
        
        try {
          // Position et taille du filigrane (centré au milieu de la page)
          const watermarkWidth = 140; // Largeur du filigrane en mm (augmentée pour visibilité)
          const watermarkHeight = 42; // Hauteur du filigrane en mm
          const watermarkX = (pageWidth - watermarkWidth) / 2;
          const watermarkY = (pageHeight - watermarkHeight) / 2;
          
          // Ajouter le logo en filigrane avec opacité réduite
          doc.saveGraphicsState();
          doc.setGState(new doc.GState({ opacity: 0.1 })); // 10% d'opacité (90% de transparence)
          doc.addImage(logoData, 'JPEG', watermarkX, watermarkY, watermarkWidth, watermarkHeight);
          doc.restoreGraphicsState();
          
          console.log('Filigrane ajouté à la page (140x42mm, 10% opacité)');
        } catch (error) {
          console.log('Erreur GState, tentative sans transparence:', error);
          // Tentative alternative sans GState
          try {
            const watermarkWidth = 140;
            const watermarkHeight = 42;
            const watermarkX = (pageWidth - watermarkWidth) / 2;
            const watermarkY = (pageHeight - watermarkHeight) / 2;
            doc.addImage(logoData, 'JPEG', watermarkX, watermarkY, watermarkWidth, watermarkHeight);
            console.log('Filigrane ajouté (sans transparence)');
          } catch (err2) {
            console.log('Impossible d\'ajouter le filigrane:', err2);
          }
        }
      };
      
      // Fonction utilitaire pour formater les montants avec centimes et séparateur espace
      const formatAmountNumeric = (amount) => {
        if (!amount) return '0,00 €';
        return new Intl.NumberFormat('fr-FR', { 
          minimumFractionDigits: 2, 
          maximumFractionDigits: 2 
        }).format(amount).replace(/\s/g, ' ') + ' €';
      };
      
      // Fonction utilitaire pour formater les montants avec décimales (pour affichage normal)
      const formatAmount = (amount) => {
        if (!amount) return '0,00';
        return new Intl.NumberFormat('fr-FR', { 
          minimumFractionDigits: 2, 
          maximumFractionDigits: 2 
        }).format(amount);
      };
      
      // Largeurs des colonnes (définies AVANT addNewPage pour être accessibles partout)
      const colWidths = [
        35,  // SITE (élargie en HTVA)
        35,  // PRM (élargie en HTVA)
        45,  // ADRESSE (élargie en HTVA)
        23,  // SEGMENT
        23,  // ÉCHÉANCE
        23,  // TOTAL CONSO
        23,  // BUDGET FOURNITURE
        23,  // ACHEMINEMENT
        23,  // TAXES
        23   // BUDGET HTVA
      ];
      
      if (tableMode === 'TTC') {
        colWidths[0] = 30;
        colWidths[1] = 30;
        colWidths[2] = 40;
        colWidths[3] = 19;
        colWidths[4] = 19;
        colWidths[5] = 19;
        colWidths[6] = 19;
        colWidths[7] = 19;
        colWidths[8] = 19;
        colWidths[9] = 19;
        colWidths.push(19); // TVA
        colWidths.push(19); // BUDGET ANNUEL TTC
      }
      // En mode HTVA : 10 colonnes seulement, pas de TVA ni BUDGET ANNUEL TTC
      
      // Fonction pour ajouter une nouvelle page
      const addNewPage = () => {
        // PAS de numéro de page ici (ajouté à la fin pour toutes les pages)
        pageNumber++;
        
        doc.addPage();
        currentY = 22; // Réduit de 25 à 22 pour cohérence avec la première page
        
        // Ajouter les logos sur la nouvelle page
        addLogo();
        // Le filigrane sera ajouté à la fin sur toutes les pages
        
        // Zone réservée pour le titre et le logo fournisseur
        const titleY = currentY; // Position du titre (22mm)
        const logoY = titleY - 1; // Logo légèrement au-dessus du titre
        
        // Ajouter le logo du fournisseur centré
        const logoHeight = addSupplierLogo(logoY);
        
        // Répéter les en-têtes avec logo fournisseur
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(`Détail de l'offre ${actualSupplierName} - Suite`, margin, titleY);
        
        // Avancer currentY en tenant compte de la hauteur du logo
        currentY = titleY + Math.max(10, logoHeight + 2);
        
        // En-têtes du tableau (format identique à Mon Périmètre)
        doc.setFillColor(57, 125, 255); // AZUR
        doc.rect(margin, currentY, pageWidth - (margin * 2), 12, 'F'); // Augmenté de 8 à 12
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        
        // Headers sur 2 lignes
        const headers = [
          ['SITE', ''],
          ['PRM', ''],
          ['ADRESSE', ''],
          ['SEGMENT', ''],
          ['NOUVELLE', 'ÉCHÉANCE'],
          ['TOTAL', 'CONSO'],
          ['BUDGET', 'FOURNITURE'],
          ['ACHEMI-', 'NEMENT'],
          ['TAXES ET', 'CONTRIB.'],
          ['BUDGET', 'HTVA']
        ];
        
        if (tableMode === 'TTC') {
          headers.push(['TVA', '']);
          headers.push(['BUDGET', 'ANNUEL TTC']);
        }
        // PAS de BUDGET ANNUEL TTC en mode HTVA
        
        let x = margin;
        
        for (let i = 0; i < headers.length; i++) {
          const [line1, line2] = headers[i];
          const width = colWidths[i];
          const textWidth1 = doc.getTextWidth(line1);
          const textWidth2 = doc.getTextWidth(line2);
          
          // Première ligne
          doc.text(line1, x + (width - textWidth1) / 2, currentY + 4);
          
          // Deuxième ligne (si elle existe)
          if (line2) {
            doc.text(line2, x + (width - textWidth2) / 2, currentY + 8);
          }
          
          x += width;
        }
        
        currentY += 14; // Augmenté pour accommoder les 2 lignes
        
        // Réinitialiser le style pour les données
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6);
      };

      // Ajouter le logo sur la première page
      addLogo();
      // Le filigrane sera ajouté à la fin, PAR DESSUS les données
      
      // Zone réservée pour le titre et le logo fournisseur
      const titleY = currentY; // Position du titre (22mm)
      const logoY = titleY - 1; // Logo légèrement au-dessus du titre
      
      // Ajouter le logo du fournisseur centré
      const logoHeight = addSupplierLogo(logoY);
      
      // Titre (format identique à Mon Périmètre)
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(25, 29, 30); // ANTHRACITE
      doc.text(`Détail de l'offre ${actualSupplierName}`, margin, titleY);
      
      // Avancer currentY en tenant compte de la hauteur du logo (qui peut dépasser le titre)
      // Le logo fait environ 10mm de haut, donc on réserve au moins 12mm depuis titleY
      currentY = titleY + Math.max(6, logoHeight + 2);
      
      // Informations (format identique à Mon Périmètre)
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      const currentDate = new Date().toLocaleDateString('fr-FR');
      doc.text(`Exporté le: ${currentDate} | Sites: ${filteredAndSortedSites.length}`, margin, currentY);
      currentY += 8;
      
      // En-têtes du tableau (format identique à Mon Périmètre)
      doc.setFillColor(57, 125, 255); // AZUR
      doc.rect(margin, currentY, pageWidth - (margin * 2), 12, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      
      // Headers sur 2 lignes
      const headers = [
        ['SITE', ''],
        ['PRM', ''],
        ['ADRESSE', ''],
        ['SEGMENT', ''],
        ['NOUVELLE', 'ÉCHÉANCE'],
        ['TOTAL', 'CONSO'],
        ['BUDGET', 'FOURNITURE'],
        ['ACHEMI-', 'NEMENT'],
        ['TAXES ET', 'CONTRIB.'],
        ['BUDGET', 'HTVA']
      ];
      
      if (tableMode === 'TTC') {
        headers.push(['TVA', '']);
        headers.push(['BUDGET', 'ANNUEL TTC']);
      }
      // PAS de BUDGET ANNUEL TTC en mode HTVA
      
      let x = margin;
      
      for (let i = 0; i < headers.length; i++) {
        const [line1, line2] = headers[i];
        const width = colWidths[i];
        const textWidth1 = doc.getTextWidth(line1);
        const textWidth2 = doc.getTextWidth(line2);
        
        // Première ligne
        doc.text(line1, x + (width - textWidth1) / 2, currentY + 4);
        
        // Deuxième ligne (si elle existe)
        if (line2) {
          doc.text(line2, x + (width - textWidth2) / 2, currentY + 8);
        }
        
        x += width;
      }
      
      currentY += 14;
      
      // Réinitialiser le style pour les données
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);

      // Données des sites avec données spécifiques du fournisseur
      for (let i = 0; i < filteredAndSortedSites.length; i++) {
        const site = filteredAndSortedSites[i];
        const supplierData = getSupplierDataForSite(site); // Données spécifiques au fournisseur
        
        // Nouvelle page si nécessaire (marge réduite)
        if (currentY > pageHeight - 20) { // Réduit de 40 à 20 pour réduire l'espace en pied de page
          addNewPage();
        }
        
        // Fond BLANC LUNAIRE pour toute la ligne (hauteur augmentée pour 2 lignes d'adresse)
        doc.setFillColor(245, 246, 249);
        doc.rect(margin, currentY, pageWidth - (margin * 2), 8, 'F'); // Augmenté de 5 à 8

        // Calculs des données AVEC les données du fournisseur
        const totalConso = supplierData?.total_consommations || 0;
        
        // Calcul correct du budget fourniture
        const budgetFourniture = (() => {
          const bf = supplierData?.budget_fourniture;
          if (bf) {
            return (bf.budget_electron || 0) + 
                   (bf.abonnement_par_an || 0) + 
                   (bf.budget_option_verte || 0);
          }
          return 0;
        })();
        
        const acheminement = supplierData?.acheminement || 0;
        
        // Calcul correct des taxes et contributions
        const taxes = (() => {
          const tc = supplierData?.taxes_contribution;
          if (tc) {
            return (tc.budget_capacite || 0) + 
                   (tc.budget_cee || 0) + 
                   (tc.cta || 0) + 
                   (tc.cspe || 0);
          }
          return 0;
        })();
        
        const budgetAnnuelHtva = supplierData?.budget_annuel_htva || 0;
        const tva = supplierData?.tva || 0;
        const budgetAnnuelTTC = supplierData?.budget_annuel_ttc || 0;
        
        // Formatage de la nouvelle échéance
        // supplierData.nouvelle_echeance est déjà au format "dd/mm/yyyy", on l'utilise directement
        // site.echeance est au format ISO, on le formate
        let nouvelleEcheanceFormatted = '';
        if (supplierData?.nouvelle_echeance) {
          // Déjà formaté en dd/mm/yyyy
          nouvelleEcheanceFormatted = supplierData.nouvelle_echeance;
        } else if (site.echeance) {
          // Format ISO, on le convertit
          const date = new Date(site.echeance);
          if (!isNaN(date.getTime())) {
            nouvelleEcheanceFormatted = date.toLocaleDateString('fr-FR', { 
              day: '2-digit', 
              month: '2-digit', 
              year: 'numeric' 
            });
          }
        }
        
        // Construction de l'adresse en 2 parties pour affichage sur 2 lignes
        let adresseLigne1 = (site.address || '').substring(0, 40);
        let adresseLigne2 = '';
        if (site.cp && site.ville) {
          adresseLigne2 = site.cp + ' ' + site.ville;
        } else if (site.cp) {
          adresseLigne2 = site.cp;
        } else if (site.ville) {
          adresseLigne2 = site.ville;
        }

        const rowData = [
          (site.nom_site || '').substring(0, 25),
          (site.prm || '').substring(0, 14),
          { ligne1: adresseLigne1, ligne2: adresseLigne2 }, // Adresse sur 2 lignes
          site.segment || '',
          nouvelleEcheanceFormatted,
          totalConso > 0 ? totalConso.toFixed(1) + ' MWh' : '- MWh',
          formatAmountNumeric(budgetFourniture),
          formatAmountNumeric(acheminement),
          formatAmountNumeric(taxes),
          formatAmountNumeric(budgetAnnuelHtva)
        ];

        if (tableMode === 'TTC') {
          rowData.push(formatAmountNumeric(tva));
          rowData.push(formatAmountNumeric(budgetAnnuelTTC));
        }
        // PAS de BUDGET ANNUEL TTC en mode HTVA

        let x = margin;
        for (let j = 0; j < rowData.length; j++) {
          const data = rowData[j];
          const width = colWidths[j];
          
          // Traitement spécial pour l'adresse (colonne 2)
          if (j === 2 && typeof data === 'object') {
            // Adresse sur 2 lignes
            const textWidth1 = doc.getTextWidth(data.ligne1);
            const textWidth2 = doc.getTextWidth(data.ligne2);
            doc.text(data.ligne1, x + (width - textWidth1) / 2, currentY + 3);
            doc.text(data.ligne2, x + (width - textWidth2) / 2, currentY + 6);
          } else {
            // Autres colonnes normales
            const textWidth = doc.getTextWidth(data);
            doc.text(data, x + (width - textWidth) / 2, currentY + 4);
          }
          
          x += width;
        }

        currentY += 8; // Augmenté de 6 à 8 pour accommoder 2 lignes
      }

      // Calculer les totaux AVEC les données du fournisseur
      const totals = {
        totalConsommations: 0,
        budgetFourniture: 0,
        acheminement: 0,
        taxes: 0,
        budgetAnnuelHtva: 0,
        tva: 0,
        budgetAnnuelTTC: 0
      };
      
      filteredAndSortedSites.forEach(site => {
        const supplierData = getSupplierDataForSite(site);
        if (supplierData) {
          totals.totalConsommations += (supplierData.total_consommations || 0);
          
          // Calcul correct du budget fourniture
          const bf = supplierData.budget_fourniture;
          if (bf) {
            totals.budgetFourniture += (bf.budget_electron || 0) + 
                                       (bf.abonnement_par_an || 0) + 
                                       (bf.budget_option_verte || 0);
          }
          
          totals.acheminement += (supplierData.acheminement || 0);
          
          // Calcul correct des taxes et contributions
          const tc = supplierData.taxes_contribution;
          if (tc) {
            totals.taxes += (tc.budget_capacite || 0) + 
                           (tc.budget_cee || 0) + 
                           (tc.cta || 0) + 
                           (tc.cspe || 0);
          }
          
          totals.budgetAnnuelHtva += (supplierData.budget_annuel_htva || 0);
          totals.tva += (supplierData.tva || 0);
          totals.budgetAnnuelTTC += (supplierData.budget_annuel_ttc || 0);
        }
      });

      // Ligne TOTAL (pour la cohérence avec Mon périmètre)
      if (currentY > pageHeight - 20) { // Réduit de 40 à 20 pour réduire l'espace en pied de page
        addNewPage();
      }

      // Fond ARGENT pour la ligne TOTAL
      doc.setFillColor(191, 198, 204);
      doc.rect(margin, currentY, pageWidth - (margin * 2), 6, 'F');

      // Données de la ligne TOTAL
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);

      const totalRowData = [
        'TOTAL',
        '',
        '',
        '', // SEGMENT vide
        '', // ECHEANCE ACTUELLE vide
        totals.totalConsommations.toFixed(1) + ' MWh',
        formatAmountNumeric(totals.budgetFourniture),
        formatAmountNumeric(totals.acheminement),
        formatAmountNumeric(totals.taxes),
        formatAmountNumeric(totals.budgetAnnuelHtva)
      ];

      if (tableMode === 'TTC') {
        totalRowData.push(formatAmountNumeric(totals.tva));
        totalRowData.push(formatAmountNumeric(totals.budgetAnnuelTTC));
      }
      // PAS de BUDGET ANNUEL TTC en mode HTVA

      let xTotal = margin;
      for (let k = 0; k < totalRowData.length; k++) {
        const data = totalRowData[k];
        const width = colWidths[k];
        const textWidth = doc.getTextWidth(data);
        doc.text(data, xTotal + (width - textWidth) / 2, currentY + 4);
        xTotal += width;
      }

      // Ajouter le filigrane sur toutes les pages (PAR DESSUS les données)
      console.log('Ajout du filigrane sur toutes les pages...');
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        addWatermark(); // Ajouter le filigrane sur chaque page
      }
      
      // Numéro de page finale (après le filigrane)
      console.log('Ajout des numéros de page...');
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(`Page ${i} sur ${totalPages}`, pageWidth - 30, pageHeight - 10);
      }

      // Sauvegarder le PDF
      console.log('Sauvegarde du PDF...');
      const fileName = `Detail_Offre_${supplierName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      console.log('Nom de fichier:', fileName);
      doc.save(fileName);
      console.log('✅ PDF généré avec succès');

    } catch (error) {
      console.error('❌ Erreur lors de la génération du PDF:', error);
      console.error('Stack trace:', error.stack);
      alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  // Récupérer les données des sites et du fournisseur spécifique
  useEffect(() => {
    const fetchData = async () => {
      if (!latestAnalysis || !supplierName) return;

      try {
        setLoading(true);
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        const API = `${BACKEND_URL}/api`;
        
        // Récupérer les données générales de l'analyse
        const analysisResponse = await axios.get(`${API}/analysis/${latestAnalysis.analysis_id}`);
        setAnalysisDetails(analysisResponse.data);
        
        // Récupérer les données spécifiques du fournisseur
        // Si une durée est spécifiée, l'ajouter comme paramètre de query
        const supplierDetailUrl = duration 
          ? `${API}/analysis/${latestAnalysis.analysis_id}/supplier-detail/${actualSupplierName}?duration=${encodeURIComponent(duration)}`
          : `${API}/analysis/${latestAnalysis.analysis_id}/supplier-detail/${actualSupplierName}`;
        const supplierResponse = await axios.get(supplierDetailUrl);
        setSupplierDetails(supplierResponse.data);
        
        // Charger le logo du fournisseur - construire l'URL directement
        // Gérer les espaces : les remplacer par des tirets pour les noms de fichiers
        const baseLogoName = actualSupplierName.toLowerCase().replace(/\s+/g, '-');
        const logoUrl = `${BACKEND_URL}/api/static/logos/${baseLogoName}.png`;
        console.log('Logo URL construite:', logoUrl);
        setSupplierLogoUrl(logoUrl);
        
      } catch (error) {
        console.error('Error fetching data for offer detail:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [latestAnalysis, supplierName]);
  
  return (
    <div className="max-w-7xl mx-auto px-2 py-6">
      {/* Header avec bouton retour */}
      <div style={{ 
        position: 'relative',
        marginBottom: '2rem',
        paddingLeft: '1rem',
        paddingRight: '1rem'
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'var(--pde-gris-acier)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '0.5rem 1rem',
            fontFamily: 'var(--font-corps)',
            fontSize: '0.875rem',
            cursor: 'pointer',
            marginBottom: '1rem',
            transition: 'all 0.2s ease',
            display: 'block'
          }}
        >
          ← Retour aux offres
        </button>
        
        <div style={{ position: 'relative' }}>
          <h1 style={{
            fontFamily: 'var(--font-titre)',
            fontWeight: '500',
            color: 'var(--pde-anthracite)',
            fontSize: '1.5rem',
            margin: '0'
          }}>
            Détail de l'offre {actualSupplierName}
          </h1>
          
          {/* Logo du fournisseur centré par rapport à la page sur la même ligne que le titre */}
          {supplierLogoUrl && (
            <div style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10
            }}>
              <img 
                src={supplierLogoUrl} 
                alt={`Logo ${actualSupplierName}`} 
                style={{ 
                  height: '60px',
                  width: 'auto',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  // Si le .png échoue, essayer le .jpg
                  const currentSrc = e.target.src;
                  if (currentSrc.endsWith('.png')) {
                    e.target.src = currentSrc.replace('.png', '.jpg');
                  } else {
                    // Si tout échoue, masquer l'image
                    e.target.style.display = 'none';
                  }
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Section Filtres et boutons (identique à Mon périmètre) */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Filtres :</span>
          </div>
          
          {/* Filtre Segment */}
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">Segment</label>
            <select
              value={filters.segment}
              onChange={(e) => setFilters({...filters, segment: e.target.value})}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Tous</option>
              {getUniqueValues('segment').map(segment => (
                <option key={segment} value={segment}>{segment}</option>
              ))}
            </select>
          </div>

          {/* Filtre Ville */}
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">Ville</label>
            <select
              value={filters.ville}
              onChange={(e) => setFilters({...filters, ville: e.target.value})}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Toutes</option>
              {getUniqueValues('ville').map(ville => (
                <option key={ville} value={ville}>{ville}</option>
              ))}
            </select>
          </div>

          {/* Filtre Échéance */}
          <div className="flex flex-col">
            <label className="text-xs text-gray-500 mb-1">Échéance</label>
            <select
              value={filters.echeance}
              onChange={(e) => setFilters({...filters, echeance: e.target.value})}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Toutes</option>
              {getUniqueValues('echeance').map(echeance => (
                <option key={echeance} value={echeance}>{echeance}</option>
              ))}
            </select>
          </div>

          {/* Bouton Réinitialiser */}
          <button
            onClick={resetFilters}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-md transition-colors duration-200 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Réinitialiser
          </button>

          {/* Boutons HTVA/TTC et Export */}
          <div className="ml-auto flex items-center space-x-3">
            {/* Bouton HTVA/TTC */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setTableMode('HTVA')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 ${
                  tableMode === 'HTVA' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                HTVA
              </button>
              <button
                onClick={() => setTableMode('TTC')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 ${
                  tableMode === 'TTC' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                TTC
              </button>
            </div>

            {/* Bouton d'export */}
            <button
              onClick={() => {
                exportToPDF().catch(err => {
                  console.error('Erreur export PDF:', err);
                  alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
                });
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 2a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V4a2 2 0 00-2-2H4zm3 2h6v4H7V4zm8 6v6h-6v-6h6zM5 10v6H4v-6h1z" clipRule="evenodd" />
              </svg>
              <span>Éditer en PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sites Table IDENTIQUE à Mon périmètre */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Container avec tableau et boutons de navigation - LARGEUR AUGMENTÉE pour toutes les colonnes */}
        <div className="flex">
          {/* Tableau élargi avec largeur augmentée + SCROLL VERTICAL pour 10 lignes visibles + ligne TOTAL sticky */}
          <div 
            className="flex-1 overflow-x-auto overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
            id="offer-detail-table-container"
            style={{ 
              maxWidth: 'calc(100vw - 40px)', // Marges réduites pour plus de largeur
              maxHeight: '750px' // Hauteur pour afficher environ 1 header + 10 lignes de sites + 1 ligne TOTAL sticky
            }}
          >
            <table className="w-auto table-fixed">
              <thead style={{ backgroundColor: '#F5F6F9', position: 'sticky', top: 0, zIndex: 20 }}>
                <tr>
                  {/* Colonne Site figée - sticky left ET top avec z-index le plus élevé */}
                  <th className="sticky left-0 px-3 py-3 text-center text-xs font-bold uppercase tracking-wider border-r-2 shadow-lg" style={{ width: '135px', minWidth: '135px', maxWidth: '135px', backgroundColor: '#F5F6F9', top: 0, zIndex: 30 }}>
                    Site
                  </th>
                  {/* Colonne PRM - sticky top - 134px - identique à Détail de l'offre */}
                  <th className="sticky px-3 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '134px', minWidth: '134px', maxWidth: '134px', backgroundColor: '#F5F6F9', top: 0, zIndex: 20 }}>
                    PRM
                  </th>
                  {/* Colonne ADRESSE - sticky top - 144px - identique à Détail de l'offre */}
                  <th className="sticky px-3 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '144px', minWidth: '144px', maxWidth: '144px', backgroundColor: '#F5F6F9', top: 0, zIndex: 20 }}>
                    Adresse
                  </th>
                  {/* Colonne SEGMENT - sticky top - 88px - identique à Détail de l'offre */}
                  <th className="sticky px-3 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '88px', minWidth: '88px', maxWidth: '88px', backgroundColor: '#F5F6F9', top: 0, zIndex: 20 }}>
                    Segment
                  </th>
                    {/* Colonne TOTAL CONSOMMATIONS - sticky top - 129px - identique à Détail de l'offre */}
                    <th className="sticky px-6 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '129px', minWidth: '129px', maxWidth: '129px', backgroundColor: '#F5F6F9', top: 0, zIndex: 20 }}>
                      <div className="flex flex-col items-center">
                        <span>TOTAL CONSOMMATIONS</span>
                        <button 
                          onClick={() => setShowConsommationDetails(!showConsommationDetails)}
                          className="text-xs text-white hover:text-gray-200 mt-1 transition-colors cursor-pointer underline hover:no-underline whitespace-nowrap"
                        >
                          {showConsommationDetails ? '- de détails' : '+ de détails'}
                        </button>
                      </div>
                    </th>
                    
                    {/* Colonnes de détail TOTAL CONSOMMATIONS (conditionnelles) - sticky top */}
                    {showConsommationDetails && (
                      <>
                        {hasConsoPointe() && (
                          <th className="sticky px-3 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9', top: 0, zIndex: 20 }}>CONSO POINTE</th>
                        )}
                        <th className="sticky px-3 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9', top: 0, zIndex: 20 }}>CONSO HPH</th>
                        <th className="sticky px-3 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9', top: 0, zIndex: 20 }}>CONSO HCH</th>
                        <th className="sticky px-3 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9', top: 0, zIndex: 20 }}>CONSO HPE</th>
                        <th className="sticky px-3 py-3 text-center text-xs font-bold uppercase tracking-wider border-r-2 border-green-200" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9', top: 0, zIndex: 20 }}>CONSO HCE</th>
                      </>
                    )}
                    {/* Colonne NOUVELLE ÉCHÉANCE - sticky top - 103px - identique à Détail de l'offre */}
                    <th className="sticky px-6 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '103px', minWidth: '103px', maxWidth: '103px', backgroundColor: '#F5F6F9', top: 0, zIndex: 20 }}>
                      NOUVELLE ÉCHÉANCE
                    </th>
                    {/* Colonne BUDGET FOURNITURE - sticky top - 139px - identique à Détail de l'offre */}
                    <th className="sticky px-6 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '139px', minWidth: '139px', maxWidth: '139px', backgroundColor: '#F5F6F9', top: 0, zIndex: 20 }}>
                      <div className="flex flex-col items-center">
                        <span className="font-bold">Budget Fourniture</span>
                        <button 
                          onClick={() => setShowBudgetDetails(!showBudgetDetails)}
                          className="text-xs text-white hover:text-gray-200 mt-1 transition-colors cursor-pointer underline hover:no-underline"
                        >
                          {showBudgetDetails ? '- de détails' : '+ de détails'}
                        </button>
                      </div>
                    </th>
                    
                    {/* Colonnes de détail Budget Fourniture - conditionnelles */}
                    {showBudgetDetails && (
                      <>
                        <th className="px-2 py-2 text-center text-xs font-bold uppercase tracking-wider border-l-2 border-blue-200" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#F5F6F9' }}>
                          <div className="flex flex-col items-center">
                            <div className="text-xs font-bold text-white mb-1">PRIX ELECTRON</div>
                            <div className="text-xs">HPH</div>
                          </div>
                        </th>
                        <th className="px-2 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#F5F6F9' }}>
                          <div className="flex flex-col items-center">
                            <div className="text-xs font-bold text-white mb-1">PRIX ELECTRON</div>
                            <div className="text-xs">HCH</div>
                          </div>
                        </th>
                        <th className="px-2 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#F5F6F9' }}>
                          <div className="flex flex-col items-center">
                            <div className="text-xs font-bold text-white mb-1">PRIX ELECTRON</div>
                            <div className="text-xs">HPE</div>
                          </div>
                        </th>
                        <th className="px-2 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#F5F6F9' }}>
                          <div className="flex flex-col items-center">
                            <div className="text-xs font-bold text-white mb-1">PRIX ELECTRON</div>
                            <div className="text-xs">HCE</div>
                          </div>
                        </th>
                        {hasPrixElectronPointe() && (
                          <th className="px-2 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#F5F6F9' }}>
                            <div className="flex flex-col items-center">
                              <div className="text-xs font-bold text-white mb-1">PRIX ELECTRON</div>
                              <div className="text-xs">POINTE</div>
                            </div>
                          </th>
                        )}
                        {hasAbonnementParAn() && (
                          <th className="px-2 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '110px', minWidth: '110px', maxWidth: '110px', backgroundColor: '#F5F6F9' }}>
                            <div className="text-xs">ABONNEMENT PAR AN</div>
                          </th>
                        )}
                        {hasPrixOptionVerte() && (
                          <>
                            <th className="px-2 py-2 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>
                              <div className="text-xs">PRIX OPTION VERTE</div>
                            </th>
                            <th className="px-2 py-2 text-center text-xs font-bold uppercase tracking-wider border-r-2 border-green-200" style={{ width: '110px', minWidth: '110px', maxWidth: '110px', backgroundColor: '#F5F6F9' }}>
                              <div className="text-xs">BUDGET OPTION VERTE</div>
                            </th>
                          </>
                        )}
                      </>
                    )}
                    
                    {/* Colonne ACHEMINEMENT - 129px */}
                    <th className="px-6 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '129px', minWidth: '129px', maxWidth: '129px', backgroundColor: '#F5F6F9' }}>
                      ACHEMINEMENT
                    </th>
                    
                    {/* Colonne TAXES ET CONTRIBUTION - 134px - identique à Détail de l'offre */}
                    <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '134px', minWidth: '134px', maxWidth: '134px', backgroundColor: '#F5F6F9' }}>
                      <div className="flex flex-col items-center">
                        <span className="font-bold">TAXES ET CONTRIBUTION</span>
                        <button
                          onClick={() => setShowTaxesDetails(!showTaxesDetails)}
                          className="mt-1 text-xs text-white hover:text-gray-200 transition-colors duration-200 underline hover:no-underline"
                        >
                          {showTaxesDetails ? '- masquer' : '+ de détails'}
                        </button>
                      </div>
                    </th>
                  {/* Colonnes de détail Taxes (conditionnelles) */}
                  {showTaxesDetails && (
                    <>
                      {hasPrixCapaciteHph() && <th className="px-3 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>Prix Capacite HPH</th>}
                      {hasPrixCapaciteHch() && <th className="px-3 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>Prix Capacite HCH</th>}
                      {hasPrixCapaciteHpe() && <th className="px-3 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>Prix Capacite HPE</th>}
                      {hasPrixCapaciteHce() && <th className="px-3 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>Prix Capacite HCE</th>}
                      {hasPrixCapacitePointe() && <th className="px-3 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>Prix Capacite Pointe</th>}
                      <th className="px-3 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>Budget Capacite</th>
                      {hasPrixCee() && (
                        <>
                          <th className="px-3 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '80px', minWidth: '80px', maxWidth: '80px', backgroundColor: '#F5F6F9' }}>Prix CEE</th>
                          <th className="px-3 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '90px', minWidth: '90px', maxWidth: '90px', backgroundColor: '#F5F6F9' }}>Budget CEE</th>
                        </>
                      )}
                      <th className="px-3 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>CTA</th>
                      <th className="px-3 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>CSPE</th>
                    </>
                  )}
                  {/* Colonne BUDGET ANNUEL HTVA - toujours visible - 119px - identique à Détail de l'offre */}
                  <th className="px-6 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '119px', minWidth: '119px', maxWidth: '119px', backgroundColor: '#F5F6F9' }}>
                    Budget Annuel HTVA
                  </th>
                  {/* Colonnes TVA et BUDGET ANNUEL TTC - visibles uniquement en mode TTC */}
                  {tableMode === 'TTC' && (
                    <>
                      <th className="px-6 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#F5F6F9' }}>
                        TVA
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-bold uppercase tracking-wider" style={{ width: '140px', minWidth: '140px', maxWidth: '140px', backgroundColor: '#F5F6F9' }}>
                        Budget Annuel TTC
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {/* Afficher les données des sites */}
                {loading ? (
                  <tr>
                    <td colSpan="20" className="px-6 py-8 text-center text-gray-500">
                      <div style={{ width: '24px', height: '24px', border: '2px solid #E2E6EA', borderTopColor: '#397DFF', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 8px' }}></div>
                      <p>Chargement...</p>
                    </td>
                  </tr>
                ) : filteredAndSortedSites && filteredAndSortedSites.length > 0 ? (
                  filteredAndSortedSites.map((site, index) => (
                    <tr key={index}>
                      {/* SITE - 135px - identique à Détail de l'offre */}
                      <td className="sticky left-0 z-10 px-3 py-3 border-r-2 shadow-lg" style={{ width: '135px', minWidth: '135px', maxWidth: '135px', backgroundColor: '#FFFFFF', color: 'black' }}>
                        <div>
                          <div className="text-xs font-medium text-slate-800 truncate">{site.nom_site}</div>
                        </div>
                      </td>
                      
                      {/* PRM - 134px - identique à Détail de l'offre */}
                      <td className="px-3 py-3 whitespace-nowrap" style={{ width: '134px', minWidth: '134px', maxWidth: '134px', backgroundColor: '#FFFFFF', color: 'black' }}>
                        <div className="text-sm text-slate-800 font-medium">{site.prm}</div>
                      </td>
                      
                      {/* ADRESSE - 144px - identique à Détail de l'offre */}
                      <td className="px-3 py-3" style={{ width: '144px', minWidth: '144px', maxWidth: '144px', backgroundColor: '#FFFFFF', color: 'black' }}>
                        <div className="text-sm text-slate-800 font-medium">{site.address}</div>
                        <div className="text-xs text-slate-600">{site.cp} {site.ville}</div>
                      </td>
                      
                      {/* SEGMENT - 88px - identique à Détail de l'offre */}
                      <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '88px', minWidth: '88px', maxWidth: '88px', backgroundColor: '#FFFFFF', color: 'black' }}>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          site.segment === 'C5' ? 'bg-blue-600 text-white' :
                          site.segment === 'C4' ? 'bg-amber-500 text-white' :
                          (site.segment === 'C1' || site.segment === 'C2') ? 'bg-emerald-600 text-white' :
                          'bg-slate-500 text-white'
                        }`}>
                          {site.segment || 'N/A'}
                        </span>
                      </td>
                      
                      {/* TOTAL CONSOMMATIONS - 129px */}
                      <td className="px-8 py-3 whitespace-nowrap text-center" style={{ width: '129px', minWidth: '129px', maxWidth: '129px', backgroundColor: '#FFFFFF', color: 'black' }}>
                        <div className="text-sm font-medium text-black">
                          {(() => {
                            const supplierData = getSupplierDataForSite(site);
                            const totalConso = supplierData?.total_consommations;
                            return totalConso && totalConso > 0 
                              ? `${totalConso.toFixed(3)} MWh` 
                              : '-';
                          })()}
                        </div>
                      </td>
                      
                      {/* Colonnes de détail TOTAL CONSOMMATIONS (conditionnelles) */}
                      {showConsommationDetails && (
                        <>
                          {/* CONSO POINTE */}
                          {hasConsoPointe() && (
                            <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                              <div className="text-sm font-medium text-black">
                                {(() => {
                                  const supplierData = getSupplierDataForSite(site);
                                  const consoPointe = supplierData?.conso_pointe;
                                  return consoPointe && consoPointe > 0 
                                    ? `${consoPointe.toFixed(3)} MWh`
                                    : '-';
                                })()}
                              </div>
                            </td>
                          )}
                          {/* CONSO HPH */}
                          <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                            <div className="text-sm font-medium text-black">
                              {(() => {
                                const supplierData = getSupplierDataForSite(site);
                                const consoHph = supplierData?.conso_hph;
                                return consoHph && consoHph > 0 
                                  ? `${consoHph.toFixed(3)} MWh`
                                  : '-';
                              })()}
                            </div>
                          </td>
                          {/* CONSO HCH */}
                          <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                            <div className="text-sm font-medium text-black">
                              {(() => {
                                const supplierData = getSupplierDataForSite(site);
                                const consoHch = supplierData?.conso_hch;
                                return consoHch && consoHch > 0 
                                  ? `${consoHch.toFixed(3)} MWh`
                                  : '-';
                              })()}
                            </div>
                          </td>
                          {/* CONSO HPE */}
                          <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                            <div className="text-sm font-medium text-black">
                              {(() => {
                                const supplierData = getSupplierDataForSite(site);
                                const consoHpe = supplierData?.conso_hpe;
                                return consoHpe && consoHpe > 0 
                                  ? `${consoHpe.toFixed(3)} MWh`
                                  : '-';
                              })()}
                            </div>
                          </td>
                          {/* CONSO HCE */}
                          <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                            <div className="text-sm font-medium text-black">
                              {(() => {
                                const supplierData = getSupplierDataForSite(site);
                                const consoHce = supplierData?.conso_hce;
                                return consoHce && consoHce > 0 
                                  ? `${consoHce.toFixed(3)} MWh`
                                  : '-';
                              })()}
                            </div>
                          </td>
                        </>
                      )}
                      
                      {/* NOUVELLE ÉCHÉANCE - 103px - identique à Détail de l'offre */}
                      <td className="px-6 py-3 whitespace-nowrap text-center" style={{ width: '103px', minWidth: '103px', maxWidth: '103px', backgroundColor: '#FFFFFF', color: 'black' }}>
                        <div className="text-sm font-medium text-black">
                          {(() => {
                            const supplierData = getSupplierDataForSite(site);
                            return supplierData?.nouvelle_echeance || '-';
                          })()}
                        </div>
                      </td>
                      
                      {/* BUDGET FOURNITURE - 139px - identique au header et Détail de l'offre */}
                      <td className="px-6 py-3 whitespace-nowrap text-center" style={{ width: '139px', minWidth: '139px', maxWidth: '139px', backgroundColor: '#FFFFFF', color: 'black' }}>
                        <div className="text-sm font-medium text-black">
                          {(() => {
                            const supplierData = getSupplierDataForSite(site);
                            const budgetFourniture = supplierData?.budget_fourniture;
                            if (budgetFourniture) {
                              const total = (budgetFourniture.budget_electron || 0) + 
                                          (budgetFourniture.abonnement_par_an || 0) + 
                                          (budgetFourniture.budget_option_verte || 0);
                              return total > 0 
                                ? new Intl.NumberFormat('fr-FR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  }).format(total) + ' €'
                                : '-';
                            }
                            return '-';
                          })()}
                        </div>
                      </td>
                      
                      {/* Colonnes de détail Budget Fourniture - Prix Électron (conditionnelles) */}
                      {showBudgetDetails && (
                        <>
                          {/* PRIX ELECTRON HPH */}
                          <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#FFFFFF', color: 'black' }}>
                            <div className="text-sm font-medium text-black">
                              {(() => {
                                const supplierData = getSupplierDataForSite(site);
                                const prixElectronHph = supplierData?.prix_electron_hph;
                                return prixElectronHph && prixElectronHph > 0 
                                  ? `${prixElectronHph.toFixed(2)} €/MWh`
                                  : '-';
                              })()}
                            </div>
                          </td>
                          {/* PRIX ELECTRON HCH */}
                          <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#FFFFFF', color: 'black' }}>
                            <div className="text-sm font-medium text-black">
                              {(() => {
                                const supplierData = getSupplierDataForSite(site);
                                const prixElectronHch = supplierData?.prix_electron_hch;
                                return prixElectronHch && prixElectronHch > 0 
                                  ? `${prixElectronHch.toFixed(2)} €/MWh`
                                  : '-';
                              })()}
                            </div>
                          </td>
                          {/* PRIX ELECTRON HPE */}
                          <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#FFFFFF', color: 'black' }}>
                            <div className="text-sm font-medium text-black">
                              {(() => {
                                const supplierData = getSupplierDataForSite(site);
                                const prixElectronHpe = supplierData?.prix_electron_hpe;
                                return prixElectronHpe && prixElectronHpe > 0 
                                  ? `${prixElectronHpe.toFixed(2)} €/MWh`
                                  : '-';
                              })()}
                            </div>
                          </td>
                          {/* PRIX ELECTRON HCE */}
                          <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#FFFFFF', color: 'black' }}>
                            <div className="text-sm font-medium text-black">
                              {(() => {
                                const supplierData = getSupplierDataForSite(site);
                                const prixElectronHce = supplierData?.prix_electron_hce;
                                return prixElectronHce && prixElectronHce > 0 
                                  ? `${prixElectronHce.toFixed(2)} €/MWh`
                                  : '-';
                              })()}
                            </div>
                          </td>
                          {/* PRIX ELECTRON POINTE */}
                          {hasPrixElectronPointe() && (
                            <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#FFFFFF', color: 'black' }}>
                              <div className="text-sm font-medium text-black">
                                {(() => {
                                  const supplierData = getSupplierDataForSite(site);
                                  const prixElectronPointe = supplierData?.prix_electron_pointe;
                                  return prixElectronPointe && prixElectronPointe > 0 
                                    ? `${prixElectronPointe.toFixed(2)} €/MWh`
                                    : '-';
                                })()}
                              </div>
                            </td>
                          )}
                          {/* ABONNEMENT PAR AN */}
                          {hasAbonnementParAn() && (
                            <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '110px', minWidth: '110px', maxWidth: '110px', backgroundColor: '#FFFFFF', color: 'black' }}>
                              <div className="text-sm font-medium text-black">
                                {(() => {
                                  const supplierData = getSupplierDataForSite(site);
                                  const abonnement = supplierData?.budget_fourniture?.abonnement_par_an;
                                  return abonnement && abonnement !== 0 
                                    ? new Intl.NumberFormat('fr-FR', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                      }).format(abonnement) + ' €'
                                    : '-';
                                })()}
                              </div>
                            </td>
                          )}
                          {/* PRIX OPTION VERTE et BUDGET OPTION VERTE */}
                          {hasPrixOptionVerte() && (
                            <>
                              <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                                <div className="text-sm font-medium text-black">
                                  {(() => {
                                    const supplierData = getSupplierDataForSite(site);
                                    const prixOptionVerte = supplierData?.budget_fourniture?.prix_option_verte;
                                    return prixOptionVerte && prixOptionVerte !== 0 
                                      ? new Intl.NumberFormat('fr-FR', {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2
                                        }).format(prixOptionVerte) + ' €'
                                      : '-';
                                  })()}
                                </div>
                              </td>
                              <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '110px', minWidth: '110px', maxWidth: '110px', backgroundColor: '#FFFFFF', color: 'black' }}>
                                <div className="text-sm font-medium text-black">
                                  {(() => {
                                    const supplierData = getSupplierDataForSite(site);
                                    const budgetOptionVerte = supplierData?.budget_fourniture?.budget_option_verte;
                                    return budgetOptionVerte && budgetOptionVerte !== 0 
                                      ? new Intl.NumberFormat('fr-FR', {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2
                                        }).format(budgetOptionVerte) + ' €'
                                      : '-';
                                  })()}
                                </div>
                              </td>
                            </>
                          )}
                        </>
                      )}
                      
                      {/* ACHEMINEMENT - 129px - identique au header et Détail de l'offre */}
                      <td className="px-6 py-4 whitespace-nowrap text-center" style={{ width: '129px', minWidth: '129px', maxWidth: '129px', backgroundColor: '#FFFFFF', color: 'black' }}>
                        <div className="text-sm font-medium text-black">
                          {(() => {
                            const supplierData = getSupplierDataForSite(site);
                            const acheminement = supplierData?.acheminement;
                            return acheminement && acheminement > 0 
                              ? new Intl.NumberFormat('fr-FR', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                }).format(acheminement) + ' €'
                              : '-';
                          })()}
                        </div>
                      </td>
                      
                      {/* TAXES ET CONTRIBUTION - 134px - identique à Détail de l'offre */}
                      <td className="px-4 py-4 whitespace-nowrap text-center" style={{ width: '134px', minWidth: '134px', maxWidth: '134px', backgroundColor: '#FFFFFF', color: 'black' }}>
                        <div className="text-sm text-gray-800 font-medium">
                          {(() => {
                            const supplierData = getSupplierDataForSite(site);
                            const taxesContrib = supplierData?.taxes_contribution;
                            if (taxesContrib) {
                              const total = (taxesContrib.budget_capacite || 0) + 
                                          (taxesContrib.budget_cee || 0) + 
                                          (taxesContrib.cta || 0) + 
                                          (taxesContrib.cspe || 0);
                              return total > 0 
                                ? new Intl.NumberFormat('fr-FR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  }).format(total) + ' €'
                                : '-';
                            }
                            return '-';
                          })()}
                        </div>
                      </td>
                      
                      {/* Colonnes de détail Taxes (conditionnelles) */}
                      {showTaxesDetails && (
                        <>
                          {/* Prix Capacité HPH */}
                          {hasPrixCapaciteHph() && (
                            <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                              <div className="text-sm font-medium text-black">
                                {(() => {
                                  const supplierData = getSupplierDataForSite(site);
                                  const capaciteHph = supplierData?.taxes_contribution?.prix_capacite_hph;
                                  return capaciteHph && capaciteHph > 0 
                                    ? `${capaciteHph.toFixed(2)} €/MWh`
                                    : '-';
                                })()}
                              </div>
                            </td>
                          )}
                          {/* Prix Capacite HCH */}
                          {hasPrixCapaciteHch() && (
                            <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                              <div className="text-sm font-medium text-black">
                                {(() => {
                                  const supplierData = getSupplierDataForSite(site);
                                  const capaciteHch = supplierData?.taxes_contribution?.prix_capacite_hch;
                                  return capaciteHch && capaciteHch > 0 
                                    ? `${capaciteHch.toFixed(2)} €/MWh`
                                    : '-';
                                })()}
                              </div>
                            </td>
                          )}
                          {/* Prix Capacite HPE */}
                          {hasPrixCapaciteHpe() && (
                            <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                              <div className="text-sm font-medium text-black">
                                {(() => {
                                  const supplierData = getSupplierDataForSite(site);
                                  const capaciteHpe = supplierData?.taxes_contribution?.prix_capacite_hpe;
                                  return capaciteHpe && capaciteHpe > 0 
                                    ? `${capaciteHpe.toFixed(2)} €/MWh`
                                    : '-';
                                })()}
                              </div>
                            </td>
                          )}
                          {/* Prix Capacite HCE */}
                          {hasPrixCapaciteHce() && (
                            <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                              <div className="text-sm font-medium text-black">
                                {(() => {
                                  const supplierData = getSupplierDataForSite(site);
                                  const capaciteHce = supplierData?.taxes_contribution?.prix_capacite_hce;
                                  return capaciteHce && capaciteHce > 0 
                                    ? `${capaciteHce.toFixed(2)} €/MWh`
                                    : '-';
                                })()}
                              </div>
                            </td>
                          )}
                          {/* Prix Capacite Pointe */}
                          {hasPrixCapacitePointe() && (
                            <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                              <div className="text-sm font-medium text-black">
                                {(() => {
                                  const supplierData = getSupplierDataForSite(site);
                                  const capacitePointe = supplierData?.taxes_contribution?.prix_capacite_pointe;
                                  return capacitePointe && capacitePointe > 0 
                                    ? `${capacitePointe.toFixed(2)} €/MWh`
                                    : '-';
                                })()}
                              </div>
                            </td>
                          )}
                          {/* Budget Capacité */}
                          <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                            <div className="text-sm font-medium text-black">
                              {(() => {
                                const supplierData = getSupplierDataForSite(site);
                                const budgetCapacite = supplierData?.taxes_contribution?.budget_capacite;
                                return budgetCapacite && budgetCapacite > 0 
                                  ? new Intl.NumberFormat('fr-FR', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2
                                    }).format(budgetCapacite) + ' €'
                                  : '-';
                              })()}
                            </div>
                          </td>
                          {/* Prix CEE et Budget CEE */}
                          {hasPrixCee() && (
                            <>
                              <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '80px', minWidth: '80px', maxWidth: '80px', backgroundColor: '#FFFFFF', color: 'black' }}>
                                <div className="text-sm font-medium text-black">
                                  {(() => {
                                    const supplierData = getSupplierDataForSite(site);
                                    const prixCee = supplierData?.taxes_contribution?.prix_cee;
                                    return prixCee && prixCee > 0 
                                      ? `${prixCee.toFixed(2)} €/MWh`
                                      : '-';
                                  })()}
                                </div>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '90px', minWidth: '90px', maxWidth: '90px', backgroundColor: '#FFFFFF', color: 'black' }}>
                                <div className="text-sm font-medium text-black">
                                  {(() => {
                                    const supplierData = getSupplierDataForSite(site);
                                    const budgetCee = supplierData?.taxes_contribution?.budget_cee;
                                    return budgetCee && budgetCee > 0 
                                      ? `${budgetCee.toFixed(2).replace('.', ',')} €`
                                      : '-';
                                  })()}
                                </div>
                              </td>
                            </>
                          )}
                          {/* CTA */}
                          <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                            <div className="text-sm font-medium text-black">
                              {(() => {
                                const supplierData = getSupplierDataForSite(site);
                                const cta = supplierData?.taxes_contribution?.cta;
                                return cta && cta > 0 
                                  ? new Intl.NumberFormat('fr-FR', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2
                                    }).format(cta) + ' €'
                                  : '-';
                              })()}
                            </div>
                          </td>
                          {/* CSPE */}
                          <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                            <div className="text-sm font-medium text-black">
                              {(() => {
                                const supplierData = getSupplierDataForSite(site);
                                const cspe = supplierData?.taxes_contribution?.cspe;
                                return cspe && cspe > 0 
                                  ? new Intl.NumberFormat('fr-FR', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2
                                    }).format(cspe) + ' €'
                                  : '-';
                              })()}
                            </div>
                          </td>
                        </>
                      )}
                      
                      {/* BUDGET ANNUEL HTVA - toujours visible - 119px - identique à Détail de l'offre */}
                      <td className="px-6 py-3 whitespace-nowrap text-center" style={{ width: '119px', minWidth: '119px', maxWidth: '119px', backgroundColor: '#FFFFFF', color: 'black' }}>
                        <div className="text-sm font-medium text-black">
                          {(() => {
                            const supplierData = getSupplierDataForSite(site);
                            const budgetHTVA = supplierData?.budget_annuel_htva;
                            return budgetHTVA && budgetHTVA > 0 
                              ? new Intl.NumberFormat('fr-FR', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                }).format(budgetHTVA) + ' €'
                              : '-';
                          })()}
                        </div>
                      </td>
                      
                      {/* TVA et BUDGET ANNUEL TTC - visibles uniquement en mode TTC */}
                      {tableMode === 'TTC' && (
                        <>
                          {/* TVA */}
                          <td className="px-6 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#FFFFFF', color: 'black' }}>
                            <div className="text-sm font-medium text-black">
                              {(() => {
                                const supplierData = getSupplierDataForSite(site);
                                const tva = supplierData?.tva;
                                return tva && tva > 0 
                                  ? new Intl.NumberFormat('fr-FR', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2
                                    }).format(tva) + ' €'
                                  : '-';
                              })()}
                            </div>
                          </td>
                          {/* BUDGET ANNUEL TTC */}
                          <td className="px-6 py-3 whitespace-nowrap text-center" style={{ width: '140px', minWidth: '140px', maxWidth: '140px', backgroundColor: '#FFFFFF', color: 'black' }}>
                            <div className="text-sm font-medium text-black">
                              {(() => {
                                const supplierData = getSupplierDataForSite(site);
                                const budgetTTC = supplierData?.budget_annuel_ttc;
                                return budgetTTC && budgetTTC > 0 
                                  ? new Intl.NumberFormat('fr-FR', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2
                                    }).format(budgetTTC) + ' €'
                                  : '-';
                              })()}
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="20" className="px-6 py-8 text-center text-gray-500">
                      <div style={{
                        width: '48px',
                        height: '48px',
                        background: 'rgba(239, 68, 68, 0.1)',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 12px'
                      }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                      </div>
                      <p>Erreur de chargement des données</p>
                      <p className="text-sm">Veuillez réessayer ou contacter le support.</p>
                    </td>
                  </tr>
                )}
                
                {/* Ligne TOTAL avec calculs - STICKY BOTTOM pour rester visible */}
                {filteredAndSortedSites && filteredAndSortedSites.length > 0 && (
                  <tr style={{ backgroundColor: '#BFC6CC', position: 'sticky', bottom: 0, zIndex: 15 }}>
                    {/* TOTAL - Première colonne */}
                    <td className="sticky left-0 px-3 py-3 whitespace-nowrap text-center border-r-2 shadow-lg" style={{ width: '135px', minWidth: '135px', maxWidth: '135px', backgroundColor: '#BFC6CC', color: 'black', fontWeight: 'bold', textAlign: 'center', zIndex: 25 }}>
                      TOTAL
                    </td>
                    
                    {/* Colonnes vides pour PRM, ADRESSE, SEGMENT */}
                    <td className="px-3 py-3 whitespace-nowrap" style={{ width: '134px', minWidth: '134px', maxWidth: '134px', backgroundColor: '#BFC6CC', color: 'black', textAlign: 'center' }}></td>
                    <td className="px-3 py-3 whitespace-nowrap" style={{ width: '144px', minWidth: '144px', maxWidth: '144px', backgroundColor: '#BFC6CC', color: 'black', textAlign: 'center' }}></td>
                    <td className="px-3 py-3 whitespace-nowrap" style={{ width: '88px', minWidth: '88px', maxWidth: '88px', backgroundColor: '#BFC6CC', color: 'black', textAlign: 'center' }}></td>
                    
                    {/* TOTAL CONSOMMATIONS - 129px - identique à Détail de l'offre */}
                    <td className="px-8 py-3 whitespace-nowrap text-center" style={{ width: '129px', minWidth: '129px', maxWidth: '129px', backgroundColor: '#BFC6CC', color: 'black' }}>
                      <div className="text-sm font-bold">
                        {(() => {
                          const total = filteredAndSortedSites.reduce((sum, site) => {
                            const supplierData = getSupplierDataForSite(site);
                            const conso = supplierData?.total_consommations || 0;
                            return sum + conso;
                          }, 0);
                          return total > 0 ? `${total.toFixed(3)} MWh` : '';
                        })()}
                      </div>
                    </td>
                    
                    {/* Colonnes de détail TOTAL CONSOMMATIONS (conditionnelles) - vides */}
                    {showConsommationDetails && (
                      <>
                        {hasConsoPointe() && (
                          <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#BFC6CC', color: 'black' }}></td>
                        )}
                        <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#BFC6CC', color: 'black' }}></td>
                        <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#BFC6CC', color: 'black' }}></td>
                        <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#BFC6CC', color: 'black' }}></td>
                        <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#BFC6CC', color: 'black' }}></td>
                      </>
                    )}
                    
                    {/* NOUVELLE ÉCHÉANCE vide - 103px - identique à Détail de l'offre */}
                    <td className="px-6 py-3 whitespace-nowrap text-center" style={{ width: '103px', minWidth: '103px', maxWidth: '103px', backgroundColor: '#BFC6CC', color: 'black' }}></td>
                    
                    {/* BUDGET FOURNITURE - Calcul du total */}
                    <td className="px-6 py-3 whitespace-nowrap text-center" style={{ width: '139px', minWidth: '139px', maxWidth: '139px', backgroundColor: '#BFC6CC', color: 'black' }}>
                      <div className="text-sm font-bold">
                        {(() => {
                          const total = filteredAndSortedSites.reduce((sum, site) => {
                            const supplierData = getSupplierDataForSite(site);
                            const budgetFourniture = supplierData?.budget_fourniture;
                            if (budgetFourniture) {
                              const siteTotal = (budgetFourniture.budget_electron || 0) + 
                                              (budgetFourniture.abonnement_par_an || 0) + 
                                              (budgetFourniture.budget_option_verte || 0);
                              return sum + siteTotal;
                            }
                            return sum;
                          }, 0);
                          return total > 0 
                            ? new Intl.NumberFormat('fr-FR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              }).format(total) + ' €'
                            : '';
                        })()}
                      </div>
                    </td>
                    
                    {/* Colonnes de détail Budget Fourniture totaux (conditionnelles) */}
                    {showBudgetDetails && (
                      <>
                        {/* PRIX ELECTRON - vides */}
                        <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#BFC6CC', color: 'black' }}></td>
                        <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#BFC6CC', color: 'black' }}></td>
                        <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#BFC6CC', color: 'black' }}></td>
                        <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#BFC6CC', color: 'black' }}></td>
                        {hasPrixElectronPointe() && (
                          <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '105px', minWidth: '105px', maxWidth: '105px', backgroundColor: '#BFC6CC', color: 'black' }}></td>
                        )}
                        
                        {/* ABONNEMENT PAR AN - Total */}
                        {hasAbonnementParAn() && (
                          <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '110px', minWidth: '110px', maxWidth: '110px', backgroundColor: '#BFC6CC', color: 'black' }}>
                            <div className="text-sm font-bold">
                              {(() => {
                                const total = filteredAndSortedSites.reduce((sum, site) => {
                                  const supplierData = getSupplierDataForSite(site);
                                  const abonnement = supplierData?.budget_fourniture?.abonnement_par_an || 0;
                                  return sum + abonnement;
                                }, 0);
                                return total > 0 
                                  ? new Intl.NumberFormat('fr-FR', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2
                                    }).format(total) + ' €'
                                  : '';
                              })()}
                            </div>
                          </td>
                        )}
                        
                        {/* PRIX OPTION VERTE et BUDGET OPTION VERTE */}
                        {hasPrixOptionVerte() && (
                          <>
                            <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#BFC6CC', color: 'black' }}></td>
                            <td className="px-2 py-3 whitespace-nowrap text-center" style={{ width: '110px', minWidth: '110px', maxWidth: '110px', backgroundColor: '#BFC6CC', color: 'black' }}>
                              <div className="text-sm font-bold">
                                {(() => {
                                  const total = filteredAndSortedSites.reduce((sum, site) => {
                                    const supplierData = getSupplierDataForSite(site);
                                    const budgetVerte = supplierData?.budget_fourniture?.budget_option_verte || 0;
                                    return sum + budgetVerte;
                                  }, 0);
                                  return total > 0 
                                    ? new Intl.NumberFormat('fr-FR', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                      }).format(total) + ' €'
                                    : '';
                                })()}
                              </div>
                            </td>
                          </>
                        )}
                      </>
                    )}
                    
                    {/* ACHEMINEMENT - Total - 129px - identique au header et Détail de l'offre */}
                    <td className="px-6 py-4 whitespace-nowrap text-center" style={{ width: '129px', minWidth: '129px', maxWidth: '129px', backgroundColor: '#BFC6CC', color: 'black' }}>
                      <div className="text-sm font-bold">
                        {(() => {
                          const total = filteredAndSortedSites.reduce((sum, site) => {
                            const supplierData = getSupplierDataForSite(site);
                            const acheminement = supplierData?.acheminement || 0;
                            return sum + acheminement;
                          }, 0);
                          return total > 0 
                            ? new Intl.NumberFormat('fr-FR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              }).format(total) + ' €'
                            : '';
                        })()}
                      </div>
                    </td>
                    
                    {/* TAXES ET CONTRIBUTION - Total - 134px - identique à Détail de l'offre */}
                    <td className="px-4 py-4 whitespace-nowrap text-center" style={{ width: '134px', minWidth: '134px', maxWidth: '134px', backgroundColor: '#BFC6CC', color: 'black' }}>
                      <div className="text-sm font-bold">
                        {(() => {
                          const total = filteredAndSortedSites.reduce((sum, site) => {
                            const supplierData = getSupplierDataForSite(site);
                            const taxesContrib = supplierData?.taxes_contribution;
                            if (taxesContrib) {
                              const siteTotal = (taxesContrib.budget_capacite || 0) + 
                                              (taxesContrib.budget_cee || 0) + 
                                              (taxesContrib.cta || 0) + 
                                              (taxesContrib.cspe || 0);
                              return sum + siteTotal;
                            }
                            return sum;
                          }, 0);
                          return total > 0 
                            ? new Intl.NumberFormat('fr-FR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              }).format(total) + ' €'
                            : '';
                        })()}
                      </div>
                    </td>
                    
                    {/* Colonnes de détail Taxes totaux (conditionnelles) */}
                    {showTaxesDetails && (
                      <>
                        {/* PRIX CAPACITE - vides */}
                        {hasPrixCapaciteHph() && <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '80px', minWidth: '80px', maxWidth: '80px', backgroundColor: '#BFC6CC', color: 'black' }}></td>}
                        {hasPrixCapaciteHch() && <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '80px', minWidth: '80px', maxWidth: '80px', backgroundColor: '#BFC6CC', color: 'black' }}></td>}
                        {hasPrixCapaciteHpe() && <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '80px', minWidth: '80px', maxWidth: '80px', backgroundColor: '#BFC6CC', color: 'black' }}></td>}
                        {hasPrixCapaciteHce() && <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '80px', minWidth: '80px', maxWidth: '80px', backgroundColor: '#BFC6CC', color: 'black' }}></td>}
                        {hasPrixCapacitePointe() && <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '80px', minWidth: '80px', maxWidth: '80px', backgroundColor: '#BFC6CC', color: 'black' }}></td>}
                        
                        {/* BUDGET CAPACITE - Total */}
                        <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#BFC6CC', color: 'black' }}>
                          <div className="text-sm font-bold">
                            {(() => {
                              const total = filteredAndSortedSites.reduce((sum, site) => {
                                const supplierData = getSupplierDataForSite(site);
                                const budgetCapacite = supplierData?.taxes_contribution?.budget_capacite || 0;
                                return sum + budgetCapacite;
                              }, 0);
                              return total > 0 
                                ? new Intl.NumberFormat('fr-FR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  }).format(total) + ' €'
                                : '';
                            })()}
                          </div>
                        </td>
                        
                        {/* PRIX CEE et BUDGET CEE */}
                        {hasPrixCee() && (
                          <>
                            <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '80px', minWidth: '80px', maxWidth: '80px', backgroundColor: '#BFC6CC', color: 'black' }}></td>
                            <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#BFC6CC', color: 'black' }}>
                              <div className="text-sm font-bold">
                                {(() => {
                                  const total = filteredAndSortedSites.reduce((sum, site) => {
                                    const supplierData = getSupplierDataForSite(site);
                                    const budgetCee = supplierData?.taxes_contribution?.budget_cee || 0;
                                    return sum + budgetCee;
                                  }, 0);
                                  return total > 0 
                                    ? new Intl.NumberFormat('fr-FR', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                      }).format(total) + ' €'
                                    : '';
                                })()}
                              </div>
                            </td>
                          </>
                        )}
                        
                        {/* CTA - Total */}
                        <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#BFC6CC', color: 'black' }}>
                          <div className="text-sm font-bold">
                            {(() => {
                              const total = filteredAndSortedSites.reduce((sum, site) => {
                                const supplierData = getSupplierDataForSite(site);
                                const cta = supplierData?.taxes_contribution?.cta || 0;
                                return sum + cta;
                              }, 0);
                              return total > 0 
                                ? new Intl.NumberFormat('fr-FR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  }).format(total) + ' €'
                                : '';
                            })()}
                          </div>
                        </td>
                        
                        {/* CSPE - Total */}
                        <td className="px-3 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#BFC6CC', color: 'black' }}>
                          <div className="text-sm font-bold">
                            {(() => {
                              const total = filteredAndSortedSites.reduce((sum, site) => {
                                const supplierData = getSupplierDataForSite(site);
                                const cspe = supplierData?.taxes_contribution?.cspe || 0;
                                return sum + cspe;
                              }, 0);
                              return total > 0 
                                ? new Intl.NumberFormat('fr-FR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  }).format(total) + ' €'
                                : '';
                            })()}
                          </div>
                        </td>
                      </>
                    )}
                    
                    {/* BUDGET ANNUEL HTVA - Total - 119px - identique à Détail de l'offre */}
                    <td className="px-6 py-3 whitespace-nowrap text-center" style={{ width: '119px', minWidth: '119px', maxWidth: '119px', backgroundColor: '#BFC6CC', color: 'black' }}>
                      <div className="text-sm font-bold">
                        {(() => {
                          const total = filteredAndSortedSites.reduce((sum, site) => {
                            const supplierData = getSupplierDataForSite(site);
                            const budgetHTVA = supplierData?.budget_annuel_htva || 0;
                            return sum + budgetHTVA;
                          }, 0);
                          return total > 0 
                            ? new Intl.NumberFormat('fr-FR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              }).format(total) + ' €'
                            : '';
                        })()}
                      </div>
                    </td>
                    
                    {/* TVA et BUDGET ANNUEL TTC totaux conditionnels */}
                    {tableMode === 'TTC' && (
                      <>
                        {/* TVA - Total */}
                        <td className="px-6 py-3 whitespace-nowrap text-center" style={{ width: '100px', minWidth: '100px', maxWidth: '100px', backgroundColor: '#BFC6CC', color: 'black' }}>
                          <div className="text-sm font-bold">
                            {(() => {
                              const total = filteredAndSortedSites.reduce((sum, site) => {
                                const supplierData = getSupplierDataForSite(site);
                                const tva = supplierData?.tva || 0;
                                return sum + tva;
                              }, 0);
                              return total > 0 
                                ? new Intl.NumberFormat('fr-FR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  }).format(total) + ' €'
                                : '';
                            })()}
                          </div>
                        </td>
                        
                        {/* BUDGET ANNUEL TTC - Total */}
                        <td className="px-6 py-3 whitespace-nowrap text-center" style={{ width: '140px', minWidth: '140px', maxWidth: '140px', backgroundColor: '#BFC6CC', color: 'black' }}>
                          <div className="text-sm font-bold">
                            {(() => {
                              const total = filteredAndSortedSites.reduce((sum, site) => {
                                const supplierData = getSupplierDataForSite(site);
                                const budgetTTC = supplierData?.budget_annuel_ttc || 0;
                                return sum + budgetTTC;
                              }, 0);
                              return total > 0 
                                ? new Intl.NumberFormat('fr-FR', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  }).format(total) + ' €'
                                : '';
                            })()}
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
        </div>
      </div>
    </div>
  );
};

// Main App Component
const AppContent = ({ isPublicView = false, publicData = null, publicShareInfo = null }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [latestAnalysis, setLatestAnalysis] = useState(publicData);
  const [analyses, setAnalyses] = useState([]);
  const [selectedOfferDetail, setSelectedOfferDetail] = useState(null); // Pour gérer les détails d'offres
  const [suppliers, setSuppliers] = useState([]); // État pour les fournisseurs
  // États pour "Notre favori" - persistés entre les changements d'onglets
  const [recommendedSupplierIndex, setRecommendedSupplierIndex] = useState(null);
  const [recommendationText, setRecommendationText] = useState(
    "Ce fournisseur offre le meilleur rapport qualité-prix pour votre profil de consommation avec des économies significatives."
  );
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    if (path === '/') {
      setActiveTab('dashboard');
    } else if (path === '/comparateur') {
      setActiveTab('comparateur');
    } else if (path === '/sites') {
      setActiveTab('sites');
    } else if (path === '/my-studies') {
      setActiveTab('my-studies');
    } else if (path === '/import') {
      setActiveTab('import');
    } else if (path === '/admin') {
      setActiveTab('admin');
    }
  }, [location]);

  useEffect(() => {
    // Ne pas fetch si on est en mode public (données déjà fournies)
    if (!isPublicView) {
      fetchAnalyses();
      fetchSuppliers();
    } else if (publicData) {
      setLatestAnalysis(publicData);
      // Charger la recommandation si elle existe
      if (publicData.recommendation) {
        const rec = publicData.recommendation;
        if (typeof rec === 'object') {
          setRecommendedSupplierIndex(rec.supplier_index ?? null);
          setRecommendationText(rec.text || "Ce fournisseur offre le meilleur rapport qualité-prix.");
        }
      }
    }
  }, [isPublicView, publicData]);

  const fetchAnalyses = async () => {
    try {
      const response = await axios.get(`${API}/analyses`);
      setAnalyses(response.data);
      if (response.data.length > 0) {
        
        // PRIORITÉ 1 : Vérifier si un ID d'analyse est spécifié dans l'URL hash
        let selectedAnalysis = null;
        const urlHash = window.location.hash;
        if (urlHash && urlHash.includes('/analysis/')) {
          const analysisIdFromUrl = urlHash.split('/analysis/')[1];
          if (analysisIdFromUrl) {
            selectedAnalysis = response.data.find(analysis => 
              analysis.analysis_id === analysisIdFromUrl
            );
            
            if (selectedAnalysis) {
              console.log('Using analysis from URL hash:', analysisIdFromUrl);
              setLatestAnalysis(selectedAnalysis);
              console.log('Selected analysis ID:', selectedAnalysis.analysis_id);
              return; // Sortir tôt si on trouve l'analyse dans l'URL
            } else {
              console.warn('Analysis ID from URL not found:', analysisIdFromUrl);
            }
          }
        }
        
        // PRIORITÉ 1 : Analysis spécifique avec données complètes (recommandée par les tests backend)
        // Cette analyse a 100% de complétude sur tous les champs (ECHEANCE, Budget Fourniture, ACHEMINEMENT, CTA, CSPE)
        const completeDataAnalysis = response.data.find(analysis => 
          analysis.analysis_id === '4a951e35-557e-4743-ba92-d15f64b2937b'
        );
        
        // PRIORITÉ 2 : Analyses avec données CTA et CSPE individuelles (par site)
        // Chercher les analyses les plus récentes qui contiennent des données CTA/CSPE au niveau des sites
        const analysisWithCtaCspeData = response.data.find(analysis => {
          const analysisDate = analysis.client_info?.analysis_date || '';
          // Priorité aux analyses les plus récentes (septembre 2025)
          return analysisDate.startsWith('2025-09') && 
                 analysis.total_sites > 0 &&
                 analysis.has_budget_fourniture === true;  // Indicateur que l'analyse a des données détaillées
        });
        
        // PRIORITÉ 2 : Analyses avec données ACHEMINEMENT ET TAXES (nouvelles analyses les plus récentes)
        // Chercher d'abord les analyses récentes du 12 septembre 2025 après 14h45 (contiennent ACHEMINEMENT + TAXES)
        const analysisWithAcheminementAndTaxes = response.data.find(analysis => {
          const analysisDate = analysis.client_info?.analysis_date || '';
          // Priorité aux analyses du 12 septembre 2025 après 14h45 (contiennent ACHEMINEMENT)
          return analysisDate.startsWith('2025-09-12T14:4') && 
                 analysisDate >= '2025-09-12T14:45:00' &&
                 analysis.total_sites > 0 &&
                 analysis.client_info !== null;
        });

        // PRIORITÉ 3 : Analyses avec données TAXES ET CONTRIBUTIONS (analyses récentes)
        const analysisWithTaxesData = response.data.find(analysis => {
          const analysisDate = analysis.client_info?.analysis_date || '';
          // Analyses du 12 septembre 2025 après 14h (contiennent les données TAXES)
          return analysisDate.startsWith('2025-09-12T14:') && 
                 analysis.total_sites > 0 &&
                 analysis.client_info !== null;
        });

        // PRIORITÉ 4 : analyse 6557f52a avec données budget détail complètes (fallback)
        const enhancedBudgetDetailAnalysis = response.data.find(analysis => 
          analysis.analysis_id === '6557f52a-771d-4e97-baf7-0e4a5a0aec0e'
        );

        // Priorité secondaire : analyse b4eb0a45 avec données TECHNOAK spécifiques
        const specificTechnoakAnalysis = response.data.find(analysis => 
          analysis.analysis_id === 'b4eb0a45-e3a2-466a-9ae8-d8202aa9e092'
        );

        // Priorité secondaire : analyses avec données budget_fourniture générales
        const analysisWithBudgetFourniture = response.data.find(analysis => {
          // Utiliser le flag has_budget_fourniture de l'API si disponible
          if (analysis.has_budget_fourniture !== undefined) {
            return analysis.has_budget_fourniture === true;
          }
          
          // Fallback : vérifier si au moins un site a des données budget_fourniture
          if (!analysis.sites || analysis.sites.length === 0) return false;
          
          return analysis.sites.some(site => 
            site.budget_fourniture !== null && 
            site.budget_fourniture !== undefined &&
            site.budget_fourniture !== 0
          );
        });

        // 1. PRIORITÉ ABSOLUE : Analyses avec sous-composants complets (Fourniture + Taxes)
        const analysisWithAllSubComponents = response.data.find(analysis => 
          analysis.client_info?.fourniture_amount && 
          analysis.client_info?.taxes_contribution_amount && 
          analysis.client_info?.acheminement_amount &&
          (analysis.client_info?.abonnement_amount !== null ||
           analysis.client_info?.energie_amount !== null ||
           analysis.client_info?.energie_verte_amount !== null) &&
          (analysis.client_info?.mecanisme_capacite_amount !== null ||
           analysis.client_info?.cee_amount !== null ||
           analysis.client_info?.cta_amount !== null ||
           analysis.client_info?.cspe_amount !== null)
        );
        
        // 2. Analyses avec sous-composants Fourniture uniquement
        const analysisWithFournitureSubComponents = response.data.find(analysis => 
          analysis.client_info?.fourniture_amount && 
          analysis.client_info?.taxes_contribution_amount && 
          analysis.client_info?.acheminement_amount &&
          (analysis.client_info?.abonnement_amount !== null ||
           analysis.client_info?.energie_amount !== null ||
           analysis.client_info?.energie_verte_amount !== null)
        );
        
        // 2. Analyses avec données Budget Actuel complètes
        const analysisWithBudgetChart = response.data.find(analysis => 
          analysis.client_info?.fourniture_amount && 
          analysis.client_info?.taxes_contribution_amount && 
          analysis.client_info?.acheminement_amount
        );
        
        // 3. Analyses avec au moins une donnée Budget Actuel
        const analysisWithPartialBudget = response.data.find(analysis => 
          analysis.client_info?.fourniture_amount || 
          analysis.client_info?.taxes_contribution_amount || 
          analysis.client_info?.acheminement_amount
        );
        
        // 4. Analyses OAK NATION complètes (legacy)
        const completeOakAnalysis = response.data.find(analysis => 
          analysis.client_info?.client_name === 'OAK NATION' && 
          analysis.suppliers_count > 0 &&
          analysis.client_info?.sites_by_segment !== null
        );
        
        // 5. N'importe quelle analyse OAK NATION avec fournisseurs
        const oakAnalysis = response.data.find(analysis => 
          analysis.client_info?.client_name === 'OAK NATION' && analysis.suppliers_count > 0
        );
        
        // PRIORITÉ SPÉCIALE : Trouver l'analyse avec le plus de fournisseurs marqués "OUI" ET des budgets ligne 21
        let analysisWithMostSelectedSuppliers = null;
        let maxSelectedSuppliersCount = 0;
        let hasLine21Data = false;
        let hasLine23Data = false;
        
        // Parcourir toutes les analyses pour trouver celle avec le plus de fournisseurs "OUI" et données ligne 21
        const analysisPromises = response.data.map(async (analysis) => {
          try {
            const rankingResponse = await axios.get(`${API}/analysis/${analysis.analysis_id}/ranking`);
            if (rankingResponse.data && rankingResponse.data.ranking) {
              const selectedSuppliers = rankingResponse.data.ranking.filter(supplier => supplier.is_selected === true);
              const hasLine21Budget = selectedSuppliers.some(supplier => supplier.budget_ligne21 && supplier.budget_ligne21 > 0);
              const hasLine23Budget = selectedSuppliers.some(supplier => supplier.budget_ligne23 && supplier.budget_ligne23 > 0);
              
              return { 
                analysis, 
                selectedCount: selectedSuppliers.length,
                hasLine21Data: hasLine21Budget,
                hasLine23Data: hasLine23Budget,
                createdAt: new Date(analysis.created_at)
              };
            }
            return { analysis, selectedCount: 0, hasLine21Data: false, hasLine23Data: false, createdAt: new Date(analysis.created_at) };
          } catch (error) {
            return { analysis, selectedCount: 0, hasLine21Data: false, hasLine23Data: false, createdAt: new Date(analysis.created_at) };
          }
        });
        
        try {
          const analysisResults = await Promise.all(analysisPromises);
          
          // Prioriser : 1) ligne 23 TTC + plus de fournisseurs, 2) ligne 21 + plus de fournisseurs, 3) plus récente
          analysisResults.sort((a, b) => {
            // Vérifier si analyses ont des données ligne 23 (TTC)
            const aHasTTC = a.hasLine23Data;
            const bHasTTC = b.hasLine23Data;
            
            // Priorité 1: Analyses avec données ligne 23 TTC (priorité absolue)
            if (aHasTTC && !bHasTTC) return -1;
            if (!aHasTTC && bHasTTC) return 1;
            
            // Priorité 2: Si égalité TTC, alors ligne 21 HTVA
            if (a.hasLine21Data && !b.hasLine21Data) return -1;
            if (!a.hasLine21Data && b.hasLine21Data) return 1;
            
            // Priorité 3: Plus de fournisseurs sélectionnés
            if (a.selectedCount !== b.selectedCount) return b.selectedCount - a.selectedCount;
            
            // Priorité 4: Plus récent si égalité
            return b.createdAt - a.createdAt;
          });
          
          if (analysisResults.length > 0) {
            const best = analysisResults[0];
            maxSelectedSuppliersCount = best.selectedCount;
            hasLine21Data = best.hasLine21Data;
            hasLine23Data = best.hasLine23Data;
            analysisWithMostSelectedSuppliers = best.analysis;
            
            console.log(`🎯 Analysis selection: ${maxSelectedSuppliersCount} suppliers marked "OUI", Line 21 data: ${hasLine21Data}, Line 23 data: ${hasLine23Data}`);
            if (analysisWithMostSelectedSuppliers) {
              console.log(`Selected analysis ID: ${analysisWithMostSelectedSuppliers.analysis_id}`);
            }
          }
        } catch (error) {
          console.log('Error finding analysis with most selected suppliers:', error);
        }

        // 6. N'importe quelle analyse avec fournisseurs
        const analysisWithSuppliers = response.data.find(analysis => analysis.suppliers_count > 0);
        
        // 7. La première analyse disponible
        const fallbackAnalysis = response.data[0];
        
        // Sélection finale basée sur les priorités (seulement si pas d'analyse spécifiée dans l'URL)
        const defaultSelectedAnalysis = 
          analysisWithMostSelectedSuppliers || // NOUVELLE PRIORITÉ : Plus de fournisseurs "OUI"
          completeDataAnalysis ||
          analysisWithCtaCspeData ||
          analysisWithAcheminementAndTaxes ||
          analysisWithTaxesData ||
          enhancedBudgetDetailAnalysis ||
          specificTechnoakAnalysis ||
          analysisWithBudgetFourniture ||
          analysisWithAllSubComponents || 
          analysisWithFournitureSubComponents ||
          analysisWithBudgetChart || 
          analysisWithPartialBudget || 
          completeOakAnalysis || 
          oakAnalysis || 
          analysisWithSuppliers || 
          fallbackAnalysis;
        
        setLatestAnalysis(defaultSelectedAnalysis);
        
        console.log('Selected analysis:', analysisWithMostSelectedSuppliers ? `Most Selected Suppliers (${maxSelectedSuppliersCount} suppliers marked "OUI"${hasLine23Data ? ', with Line 23 TTC data' : (hasLine21Data ? ', with Line 21 HTVA data' : '')}) - TOP PRIORITY` :
                    completeDataAnalysis ? 'Complete Data Analysis (100% Completeness - High Priority)' :
                    analysisWithCtaCspeData ? 'Analysis with CTA/CSPE Data (High Priority)' :
                    analysisWithAcheminementAndTaxes ? 'Analysis with ACHEMINEMENT + TAXES ET CONTRIBUTIONS (Latest)' :
                    analysisWithTaxesData ? 'Analysis with TAXES ET CONTRIBUTIONS (Recent)' :
                    enhancedBudgetDetailAnalysis ? 'Enhanced Budget Detail Analysis (6557f52a)' :
                    specificTechnoakAnalysis ? 'Specific TECHNOAK Analysis (b4eb0a45)' :
                    analysisWithBudgetFourniture ? 'Budget Fourniture Priority' :
                    analysisWithAllSubComponents ? 'All Sub-Components' : 
                    analysisWithFournitureSubComponents ? 'Fourniture Sub-Components' :
                    analysisWithBudgetChart ? 'Budget Complete' : 
                    analysisWithPartialBudget ? 'Budget Partial' :
                    completeOakAnalysis ? 'OAK Complete' :
                    oakAnalysis ? 'OAK Basic' :
                    analysisWithSuppliers ? 'With Suppliers' : 'Fallback');
                    
        console.log('Selected analysis ID:', defaultSelectedAnalysis?.analysis_id);
      }
    } catch (error) {
      console.error('Error fetching analyses:', error);
    }
  };

  // Fonction pour récupérer les fournisseurs depuis l'API
  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data);
      console.log('Suppliers loaded:', response.data.length);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const handleUploadSuccess = (data) => {
    setLatestAnalysis(data);
    setActiveTab('dashboard'); // Redirect to dashboard after successful upload
    fetchAnalyses(); // Refresh analyses list
  };

  const handleDeleteAnalysis = async (analysisId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette analyse ?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/analysis/${analysisId}`);
      // Refresh analyses list after deletion
      fetchAnalyses();
      // If deleted analysis was selected, clear selection
      if (latestAnalysis?.analysis_id === analysisId) {
        setLatestAnalysis(null);
      }
      alert('Analyse supprimée avec succès');
    } catch (error) {
      console.error('Error deleting analysis:', error);
      alert('Erreur lors de la suppression de l\'analyse');
    }
  };

  const renderContent = () => {
    // Si un détail d'offre est sélectionné, afficher le composant de détail
    if (selectedOfferDetail) {
      return (
        <OfferDetailTab 
          latestAnalysis={latestAnalysis} 
          supplierName={selectedOfferDetail}
          onBack={() => setSelectedOfferDetail(null)}
        />
      );
    }
    
    // Sinon, afficher les onglets normaux
    switch (activeTab) {
      case 'dashboard':
        return <DashboardTab latestAnalysis={latestAnalysis} setActiveTab={setActiveTab} isPublicView={isPublicView} />;
      case 'comparateur':
        return <ComparateurTab 
          latestAnalysis={latestAnalysis} 
          setSelectedOfferDetail={setSelectedOfferDetail} 
          suppliers={suppliers}
          recommendedSupplierIndex={recommendedSupplierIndex}
          setRecommendedSupplierIndex={setRecommendedSupplierIndex}
          recommendationText={recommendationText}
          setRecommendationText={setRecommendationText}
          user={user}
          isPublicView={isPublicView}
        />;
      case 'sites':
        return <SitesTab latestAnalysis={latestAnalysis} />;
      case 'my-studies':
        return <MyStudiesPanel onSelectStudy={async (analysisId, shareId = null) => {
          try {
            // Fetch the full analysis data from the API
            // Include share_id as query parameter to get share-specific recommendation
            const token = localStorage.getItem('access_token');
            const url = shareId 
              ? `${API}/analysis/${analysisId}?share_id=${shareId}`
              : `${API}/analysis/${analysisId}`;
            
            const response = await axios.get(url, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data) {
              setLatestAnalysis(response.data);
              
              // Mark the study as viewed if it's a shared study (for ADV tracking)
              if (shareId) {
                try {
                  await axios.post(`${API}/study/${analysisId}/mark-viewed`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                } catch (markError) {
                  console.log('Could not mark study as viewed:', markError);
                }
              }
              
              // Load recommendation data if it exists
              // This will be the share-specific recommendation if shareId was provided
              if (response.data.recommendation) {
                const rec = response.data.recommendation;
                setRecommendedSupplierIndex(rec.supplier_index ?? null);
                setRecommendationText(rec.text || "Ce fournisseur offre le meilleur rapport qualité-prix pour votre profil de consommation avec des économies significatives.");
              } else {
                // Reset to defaults if no recommendation
                setRecommendedSupplierIndex(null);
                setRecommendationText("Ce fournisseur offre le meilleur rapport qualité-prix pour votre profil de consommation avec des économies significatives.");
              }
              
              setActiveTab('dashboard');
            }
          } catch (error) {
            console.error('Error loading analysis:', error);
            alert('Erreur lors du chargement de l\'étude');
          }
        }} />;
      case 'import':
        // Import masqué en mode public
        if (isPublicView) return <DashboardTab latestAnalysis={latestAnalysis} setActiveTab={setActiveTab} isPublicView={isPublicView} />;
        return <ImportTab onUploadSuccess={handleUploadSuccess} onDeleteAnalysis={handleDeleteAnalysis} analyses={analyses} />;
      case 'admin':
        // Admin masqué en mode public
        if (isPublicView) return <DashboardTab latestAnalysis={latestAnalysis} setActiveTab={setActiveTab} isPublicView={isPublicView} />;
        return <AdminPanel />;
      default:
        return <DashboardTab latestAnalysis={latestAnalysis} setActiveTab={setActiveTab} isPublicView={isPublicView} />;
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#F5F6F9' // BLANC LUNAIRE de la charte PDE
    }}>
      <Header 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        latestAnalysis={latestAnalysis}
        isPublicView={isPublicView}
        publicShareInfo={publicShareInfo}
      />
      <main className="max-w-7xl mx-auto py-8">
        {renderContent()}
      </main>
      
      {/* Footer selon charte PDE */}
      <footer style={{
        padding: '20px 0',
        textAlign: 'center',
        borderTop: '1px solid #E2E6EA',
        background: '#FFFFFF'
      }}>
        {isPublicView && publicShareInfo && (
          <p style={{ fontFamily: 'var(--font-corps)', color: '#939BA0', fontSize: '0.8125rem', margin: 0 }}>
            Document partagé par {publicShareInfo.shared_by_name} le {publicShareInfo.shared_at ? new Date(publicShareInfo.shared_at).toLocaleDateString('fr-FR') : 'N/A'}
          </p>
        )}
      </footer>
    </div>
  );
};

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#F5F6F9' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #E2E6EA',
            borderTopColor: '#397DFF',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ fontFamily: 'var(--font-corps)', fontSize: '0.875rem', color: '#687175' }}>Chargement...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public routes - no authentication required */}
            <Route path="/study/public/:token" element={<PublicStudyView />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            {/* Protected routes */}
            <Route path="/" element={<ProtectedRoute><AppContent /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><AppContent /></ProtectedRoute>} />
            <Route path="/comparateur" element={<ProtectedRoute><AppContent /></ProtectedRoute>} />
            <Route path="/sites" element={<ProtectedRoute><AppContent /></ProtectedRoute>} />
            <Route path="/my-studies" element={<ProtectedRoute><AppContent /></ProtectedRoute>} />
            <Route path="/import" element={<ProtectedRoute><AppContent /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AppContent /></ProtectedRoute>} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
export { AppContent };